import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import SettingsMenu from './components/SettingsMenu.js';
import { getApiBase, getConfiguredApiBase, isPlaceholderApiUrl, credentialsModeForApiUrl } from "./backendProbe.js";
import { startOpenAiRealtimeCall } from "./openaiRealtimeVoice.js";
import { createSiteSource, createDeckSource, filesFromSource, previewFromFiles, isWebsiteRequest, isDeckRequest, isSiteFollowUp, reviseSource, handoffSteps, nextSiteTurn, computerFromModel, SITE_SUBJECT_QUESTION, researchViaPreview, siteAfterResearch, isChatFollowUp, explainPrevious, renderedSourceFromArtifacts } from "./siteSystem.js";
import "./chatbot.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext.js";
import { canPerformAction, getUpgradeMessage, getPlanLimits } from "./utils/planFeatures.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "react-i18next";
import PageHeader from "./components/PageHeader.js";
import { 
  FaMicrophone, 
  FaMicrophoneAlt, 
  FaMicrophoneAltSlash, 
  FaTimes,
  FaLightbulb,
  FaImages,
  FaStore,
  
  FaComments,
  FaChartLine,
  FaPlus,
  FaCog,
  FaUser,
  FaPlug,
  FaChartBar,
  FaCog as FaSettings,
  FaSlidersH,
  FaShieldAlt,
  FaBell,
  FaQuestionCircle,
  FaInfoCircle,
  FaEllipsisH,
  FaEnvelope
} from 'react-icons/fa';
import ImageEditor from "./ImageEditor.js";
import VideoEditor from "./VideoEditor.js";
import WebsiteBuilder from "./WebsiteBuilder.js";
import SandboxBrowser from "./components/SandboxBrowser.js";
import AgentSandbox from "./components/AgentSandbox.js";
import AIBrowserAgent from "./components/AIBrowserAgent.js";
import UsageMeter from "./components/UsageMeter.js";
import CreditTopUp from "./components/CreditTopUp.js";
import StreamingMessage from "./components/StreamingMessage.js";
import ChatActivityStatus, { ComposerActivityDock, liveWritingFromBuild } from "./components/ChatActivityStatus.js";
import PromptPresets from "./components/PromptPresets.js";
import ZolaidComputer from "./components/ZolaidComputer.js";
import VideoComputer from "./components/VideoComputer.js";
import ImageComputer from "./components/ImageComputer.js";
import AutonomousAgent from "./components/AutonomousAgent.js";
import ProVideoEditor from "./components/ProVideoEditor.js";
import ProVideoEditorV2 from "./components/ProVideoEditorV2.js";
import ProVideoEditorV3 from "./components/ProVideoEditorV3.js";
import ProImageEditor from "./components/ProImageEditor.js";
import ProImageEditorV2 from "./components/ProImageEditorV2.js";
import {
  findMatchingPlugin,
  connectedAppsWithIntegrationKeys,
  mcpAppsFromConnected,
} from "./plugins/appPlugins.js";
import { resolveVscodeEmbedUrl, defaultZolaidComputerViewMode } from "./utils/vscodeEmbedUrl.js";
import { getUnifiedToolsFor, UnifiedIcons } from "./unifiedTools.js";
import { aiBusinessProblems, aiProblemRankings } from "./aiToolsRankingsData.js";
import Apps from "./Apps.js";
import "./Apps.css";

/** VS Code tab iframe URL â€” see `resolveVscodeEmbedUrl` (env + dev default to 127.0.0.1:8080). */
const ZOLAID_VSCODE_EMBED_URL = resolveVscodeEmbedUrl(null);

// Error boundary for the whole chatbot so runtime errors don't white-screen the app
class ChatbotErrorBoundary extends React.Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Chatbot error:', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="container chatbot-theme-light" style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: 24 }}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.25rem', color: '#991b1b' }}>Something went wrong in the chatbot</h2>
            <p style={{ margin: 0, color: '#b91c1c', fontSize: 14 }}>
              {this.state.error?.message || String(this.state.error)}
            </p>
            <button
              type="button"
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8 }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const CHAT_HISTORY_STORAGE_KEY = 'zolaid_chat_history';
const ACTIVE_CHAT_STORAGE_KEY = 'zolaid_active_chat_id';
const FRESH_CHAT_MARKER = 'new';
const GUEST_CHAT_SCOPE = 'guest';

function readStoredAuthToken() {
  try {
    return localStorage.getItem('authToken') || localStorage.getItem('token') || null;
  } catch (_) {
    return null;
  }
}

function decodeJwtPayload(token) {
  if (!token || String(token).split('.').length < 2) return null;
  try {
    const segment = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = segment + '='.repeat((4 - (segment.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

/** Stable localStorage scope: guest vs signed-in user (id preferred, else email). */
function resolveChatStorageScope(user = null, token = undefined) {
  // `token === undefined` → read from storage (boot). Explicit `null` means logged out.
  const effectiveToken = token !== undefined ? token : readStoredAuthToken();
  const payload = decodeJwtPayload(effectiveToken);
  const userId = user?.userId ?? payload?.userId ?? payload?.sub ?? null;
  const emailRaw = user?.email ?? payload?.email ?? null;
  const email = typeof emailRaw === 'string' ? emailRaw.trim().toLowerCase() : '';
  if (userId != null && String(userId).trim() !== '') {
    return `uid:${String(userId).trim()}`;
  }
  if (email) return `email:${email}`;
  // Signed in but missing id/email claims — still isolate from the guest store.
  if (effectiveToken) return 'authenticated';
  return GUEST_CHAT_SCOPE;
}

/** Backend memory key identity: guest vs account (never share one id across both). */
function resolveChatApiUserId(user = null, token = null) {
  if (!token) return 'guest';
  const payload = decodeJwtPayload(token);
  const userId = user?.userId ?? payload?.userId ?? payload?.sub ?? null;
  if (userId != null && String(userId).trim() !== '') return String(userId).trim();
  const emailRaw = user?.email ?? payload?.email ?? null;
  const email = typeof emailRaw === 'string' ? emailRaw.trim().toLowerCase() : '';
  if (email) return `email:${email}`;
  return 'authenticated';
}

function chatHistoryStorageKey(scope) {
  return `${CHAT_HISTORY_STORAGE_KEY}::${scope || GUEST_CHAT_SCOPE}`;
}

function activeChatStorageKey(scope) {
  return `${ACTIVE_CHAT_STORAGE_KEY}::${scope || GUEST_CHAT_SCOPE}`;
}

/**
 * One-time migrate of legacy unscoped keys into the *current* scope only.
 * Never copies between guest and signed-in on later login/logout.
 */
function migrateLegacyChatStoreIfNeeded(scope) {
  try {
    const legacyHistory = localStorage.getItem(CHAT_HISTORY_STORAGE_KEY);
    if (legacyHistory == null) return;
    const scopedKey = chatHistoryStorageKey(scope);
    if (localStorage.getItem(scopedKey) == null) {
      localStorage.setItem(scopedKey, legacyHistory);
      const legacyActive = localStorage.getItem(ACTIVE_CHAT_STORAGE_KEY);
      if (legacyActive != null) {
        localStorage.setItem(activeChatStorageKey(scope), legacyActive);
      }
    }
    localStorage.removeItem(CHAT_HISTORY_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_CHAT_STORAGE_KEY);
  } catch (_) {}
}

function isComputerActivity(text) {
  return text === 'Building it on the computer.' || text === 'Editing it on the computer.' || text === 'Opening it on the computer.';
}

function settleWork(item, now = Date.now()) {
  if (!item || item.activity !== 'live') return item;
  const startedAt = item.startedAt || now;
  return {
    ...item,
    activity: 'done',
    startedAt,
    workedMs: item.workedMs != null ? item.workedMs : Math.max(0, now - startedAt),
  };
}

function settleStoppedItem(item, now = Date.now()) {
  if (!item || item.sender !== 'bot') return item;
  if (item.activity === 'live') return settleWork(item, now);
  if (item.activity === 'done') return item;
  if (!String(item.text || '').trim()) {
    return settleWork({
      ...item,
      activity: 'live',
      isStreaming: false,
      startedAt: item.startedAt || now,
    }, now);
  }
  const startedAt = item.startedAt || now;
  return {
    ...item,
    isStreaming: false,
    showWorked: true,
    startedAt,
    workedMs: item.workedMs != null ? item.workedMs : Math.max(0, now - startedAt),
  };
}

const WELCOME_TEXT = "What should I work on?";
const CHAT_STARTERS = [
  { label: 'Build a website', hint: 'A usable page', text: 'Build me a coffee shop website for people nearby. They should see the menu and find the shop.' },
  { label: 'Research a company', hint: 'A short public brief', text: 'Research Stripe and write a short brief on what the company does.' },
  { label: 'Make slides', hint: 'A deck for a meeting', text: 'Make a short slide deck about launching a new product.' },
  { label: 'Open Gmail', hint: 'Inbox on this screen', text: 'Open the Gmail screen' },
];
const STOCK_GREETINGS = new Set([
  WELCOME_TEXT,
  "Zolaid is an AI that helps you go from idea to execution.",
  "Hello! I'm ZOLAID, your AI assistant. How can I help you today?",
]);

function chatVisibleText(text) {
  const raw = String(text || '');
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('âŒ') || trimmed.includes('**Fix (')) return raw;
  if (/^\s*\{/.test(trimmed) && /"(mode|artifacts|html)"/.test(trimmed)) {
    const match = trimmed.match(/"content"\s*:\s*"((?:\\.|[^"\\])*)"/);
    const sentence = match
      ? match[1].replace(/\\"/g, '"').replace(/\\n/g, ' ').replace(/\\t/g, ' ').trim()
      : '';
    if (sentence && !/[{<>]|```/.test(sentence)) return sentence;
    return 'Building it on the computer.';
  }
  let next = raw.replace(/```[\w]*\n?[\s\S]*?```/g, ' ');
  next = next.replace(/<\/?(?:html|head|body|div|span|style|script|section|header|footer|main|nav|svg|path|button|ul|ol|li|h1|h2|h3|p|a)\b[^>]*>/gi, ' ');
  next = next.replace(/\{"mode"[\s\S]*$/g, ' ');
  next = next.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  return next || 'Building it on the computer.';
}

function privacyHidesChatHistory() {
  try {
    return localStorage.getItem('zolaid-privacy-enabled') === 'true';
  } catch (_) {
    return false;
  }
}

function readStoredChatHistory(scope = GUEST_CHAT_SCOPE) {
  if (privacyHidesChatHistory()) return [];
  try {
    migrateLegacyChatStoreIfNeeded(scope);
    const raw = localStorage.getItem(chatHistoryStorageKey(scope));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function writeStoredChatHistory(scope, history) {
  try {
    localStorage.setItem(chatHistoryStorageKey(scope), JSON.stringify(history));
  } catch (_) {}
}

function writeActiveChatMarker(scope, marker) {
  try {
    localStorage.setItem(activeChatStorageKey(scope), String(marker));
  } catch (_) {}
}

function readActiveThread(scope = GUEST_CHAT_SCOPE) {
  const history = readStoredChatHistory(scope);
  let marker = null;
  try {
    marker = localStorage.getItem(activeChatStorageKey(scope));
  } catch (_) {
    marker = null;
  }
  if (marker === FRESH_CHAT_MARKER) return null;
  if (marker) {
    const match = history.find((chat) => String(chat?.id) === String(marker));
    if (match && Array.isArray(match.messages) && match.messages.length) return match;
  }
  if (marker == null && history[0] && Array.isArray(history[0].messages) && history[0].messages.length > 1) {
    return history[0];
  }
  return null;
}

function bootThreadForScope(scope) {
  const thread = readActiveThread(scope);
  return {
    messages: thread?.messages?.length
      ? thread.messages
      : [{ id: Date.now(), sender: 'bot', text: WELCOME_TEXT }],
    chatId: thread?.id ?? null,
    history: readStoredChatHistory(scope),
    siteSource: thread?.siteSource && typeof thread.siteSource === 'object' ? thread.siteSource : null,
  };
}

function coerceMessageMillis(value) {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 1e12 && value <= 1e14) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{12,16}$/.test(trimmed)) {
      const ms = Number(trimmed);
      if (ms >= 1e12 && ms <= 1e14) return ms;
    }
    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      const parsed = Date.parse(trimmed);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function messageClock(msg) {
  if (!msg || typeof msg !== 'object') return null;
  const ms = coerceMessageMillis(msg.sentAt)
    ?? coerceMessageMillis(msg.createdAt)
    ?? coerceMessageMillis(msg.timestamp)
    ?? coerceMessageMillis(msg.startedAt)
    ?? coerceMessageMillis(msg.id);
  if (ms == null) return null;
  const date = new Date(ms);
  let label = '';
  try {
    label = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch (_) {
    return null;
  }
  if (!label) return null;
  let iso = '';
  try {
    iso = date.toISOString();
  } catch (_) {
    iso = '';
  }
  return { label, iso };
}

function messageSentTime(msg) {
  const clock = messageClock(msg);
  if (!clock) return null;
  return (
    <time className="message-sent-time" dateTime={clock.iso || undefined}>
      {clock.label}
    </time>
  );
}

function answerCopyText(msg) {
  if (!msg || msg.sender !== 'bot') return '';
  if (msg.isStreaming) return '';
  if (STOCK_GREETINGS.has(msg.text)) return '';
  const visible = chatVisibleText(msg.text);
  if (!visible) return '';
  return visible;
}

function userChatMessage(text, extra) {
  const fields = extra && typeof extra === 'object' ? extra : {};
  const sentAt = coerceMessageMillis(fields.sentAt) ?? Date.now();
  const id = fields.id != null ? fields.id : sentAt;
  return {
    sender: 'user',
    text,
    ...fields,
    id,
    sentAt: coerceMessageMillis(fields.sentAt) ?? coerceMessageMillis(id) ?? sentAt,
  };
}

function messagesForStorage(msgs) {
  return (Array.isArray(msgs) ? msgs : []).map((message) => {
    if (!message || typeof message !== 'object') return message;
    const next = { ...message };
    if (Array.isArray(message.files)) {
      next.files = message.files.map((file) => {
        if (!file || typeof file !== 'object') return file;
        const url = String(file.url || '');
        if (url.startsWith('blob:') || url.startsWith('data:')) {
          const { url: _url, ...rest } = file;
          return rest;
        }
        return file;
      });
    }
    return next;
  });
}

function siteSourceForStorage(source) {
  if (!source || typeof source !== 'object') return null;
  const omitSecrets = (value) => {
    if (Array.isArray(value)) return value.map(omitSecrets);
    if (!value || typeof value !== 'object') return value;
    const out = {};
    for (const [key, child] of Object.entries(value)) {
      if (/token|api[_-]?key|secret|password|authorization|credential/i.test(key)) continue;
      out[key] = omitSecrets(child);
    }
    return out;
  };
  try {
    const clone = omitSecrets(JSON.parse(JSON.stringify(source)));
    if (!clone || typeof clone !== 'object') return null;
    if (JSON.stringify(clone).length > 80000) return null;
    return clone;
  } catch (_) {
    return null;
  }
}

function siteContextNote(source) {
  if (!source || typeof source !== 'object') return '';
  const kind = String(source.kind || '').trim();
  const brand = String(source.brand || '').trim();
  if (!kind && !brand) return '';
  const accent = String(source.accent || '').trim();
  const parts = ['Current page in this chat:'];
  if (kind) parts.push(kind);
  if (brand) parts.push(`named ${brand}`);
  if (accent) parts.push(`accent ${accent}`);
  return parts.join(' ').slice(0, 300);
}

function priorTurnsForApi(msgs, messageToSend, retryMessageId) {
  let list = Array.isArray(msgs) ? msgs.slice() : [];
  if (retryMessageId != null) {
    const idx = list.findIndex((message) => message && message.id === retryMessageId);
    if (idx >= 0) list = list.slice(0, idx);
  }
  const turns = [];
  for (const message of list) {
    if (!message || message.error) continue;
    if (message.sender !== 'user' && message.sender !== 'bot') continue;
    const text = String(message.text || '').replace(/\u0000/g, '').trim();
    if (!text || STOCK_GREETINGS.has(text)) continue;
    turns.push({
      role: message.sender === 'user' ? 'user' : 'assistant',
      content: text.slice(0, 3000),
    });
  }
  const clipped = turns.slice(-16);
  const outgoing = String(messageToSend || '').trim();
  const last = clipped[clipped.length - 1];
  if (last && last.role === 'user' && last.content === outgoing) clipped.pop();
  return clipped;
}

const VIDEO_NUMBER_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
};

function videoSeconds(token) {
  const text = String(token || '').trim().toLowerCase();
  if (!text) return null;
  const clock = text.match(/^(\d+):(\d{1,2})$/);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  if (Object.prototype.hasOwnProperty.call(VIDEO_NUMBER_WORDS, text)) return VIDEO_NUMBER_WORDS[text];
  const num = Number(text);
  return Number.isFinite(num) ? num : null;
}

function firstVideoSeconds(text) {
  const source = String(text || '');
  const clock = source.match(/(\d+:\d{1,2})/);
  if (clock) return videoSeconds(clock[1]);
  const num = source.match(/\b(\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)\s*(seconds?|secs?|minutes?|mins?)?/i);
  if (!num) return null;
  const value = videoSeconds(num[1]);
  if (value == null) return null;
  if (num[2] && /^min/i.test(num[2])) return value * 60;
  return value;
}

/** Video edits stay on the Computer. A website or deck request is left for that path. */
function readVideoEdit(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const t = raw.toLowerCase();
  if (/\b(website|web site|landing page|homepage|powerpoint|pptx|slide deck|presentation)\b/.test(t)
    && /\b(create|build|make|design|generate)\b/.test(t)) {
    return null;
  }
  const mentionsMedia = /\b(videos?|footage|clips?|film)\b/.test(t);
  const editVerb = /\b(edit|editing|trim|trimming|cut|cutting|crop|shorten|chop)\b/.test(t);
  const rangeTalk = /\b(cut|trim|remove|chop|shorten|delete)\b/.test(t)
    && /\b(first|last|from|between|seconds?|secs?|minutes?)\b/.test(t);
  const caption = /\b(add|put|write|insert)\b.*\b(captions?|subtitles?)\b/.test(t)
    || /\b(captions?|subtitles?)\b.*\b(videos?|clips?|footage)\b/.test(t);
  const playRequest = /\b(play|preview|watch)\b/.test(t) && mentionsMedia;
  if (!((mentionsMedia && editVerb) || rangeTalk || caption || playRequest)) return null;

  const fromTo = t.match(/\b(?:from|between)\s+(\d+:\d{1,2}|\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)\s+(?:to|and|-)\s+(\d+:\d{1,2}|\d+(?:\.\d+)?|one|two|three|four|five|six|seven|eight|nine|ten)/);
  if (fromTo) {
    return { action: 'trim', start: videoSeconds(fromTo[1]), end: videoSeconds(fromTo[2]), fromEnd: null, play: true };
  }
  if (/\b(first|beginning|start|opening)\b/.test(t) && /\b(cut|trim|remove|chop|delete|shorten)\b/.test(t)) {
    const n = firstVideoSeconds(t);
    if (n != null) return { action: 'trim', start: n, end: null, fromEnd: null, play: true };
  }
  if (/\blast\b/.test(t) && /\b(cut|trim|remove|chop|delete|shorten)\b/.test(t)) {
    const n = firstVideoSeconds(t);
    if (n != null) return { action: 'trim', start: 0, end: null, fromEnd: n, play: true };
  }
  if (/\bkeep\b/.test(t) && /\bfirst\b/.test(t)) {
    const n = firstVideoSeconds(t);
    if (n != null) return { action: 'trim', start: 0, end: n, fromEnd: null, play: true };
  }
  if (/\b(trim|cut)\s+to\b/.test(t)) {
    const n = firstVideoSeconds(t);
    if (n != null) return { action: 'trim', start: 0, end: n, fromEnd: null, play: true };
  }
  if (playRequest && !editVerb && !rangeTalk && !caption) {
    return { action: 'play', start: null, end: null, fromEnd: null, play: true };
  }
  return { action: 'open', start: null, end: null, fromEnd: null, play: false };
}

const SETTINGS_NAV_GROUPS = [
  {
    label: 'Account',
    items: [{ id: 'overview', label: 'Account' }],
  },
  {
    label: 'Usage and billing',
    items: [
      { id: 'usage', label: 'Usage' },
      { id: 'spending', label: 'Spending' },
      { id: 'billing', label: 'Billing & Invoices' },
    ],
  },
  {
    label: 'General',
    items: [
      { id: 'settings', label: 'Settings' },
      { id: 'cloud-agents', label: 'Cloud Agents' },
      { id: 'bugbot', label: 'Bugbot' },
      { id: 'docs', label: 'Docs' },
    ],
  },
];

function SettingsNavIcon({ id }) {
  const props = {
    width: 16,
    height: 16,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  if (id === 'overview') {
    return (
      <svg {...props}>
        <circle cx="12" cy="8" r="3.25" />
        <path d="M5.2 19.2c1.35-3.05 3.7-4.45 6.8-4.45s5.45 1.4 6.8 4.45" />
      </svg>
    );
  }
  if (id === 'usage' || id === 'spending') {
    return (
      <svg {...props}>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="M8 16v-4" />
        <path d="M12 16V8" />
        <path d="M16 16v-6" />
      </svg>
    );
  }
  if (id === 'billing') {
    return (
      <svg {...props}>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18" />
      </svg>
    );
  }
  if (id === 'settings') {
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="3" />
        <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18" />
      </svg>
    );
  }
  if (id === 'cloud-agents') {
    return (
      <svg {...props}>
        <path d="M7 17h10a4 4 0 0 0 .4-8 5.5 5.5 0 0 0-10.6 1.6A3.5 3.5 0 0 0 7 17z" />
      </svg>
    );
  }
  if (id === 'bugbot') {
    return (
      <svg {...props}>
        <path d="M12 3 5 6.2v5.3c0 4.2 2.9 7.2 7 8.5 4.1-1.3 7-4.3 7-8.5V6.2L12 3z" />
      </svg>
    );
  }
  return (
    <svg {...props}>
      <path d="M6 4.5h8.5L19 9v10.5H6z" />
      <path d="M14 4.5V9h5" />
    </svg>
  );
}

export default function ChatbotPage() {
  const { user, token, userPlan, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [mainView, setMainView] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("view") === "apps" ? "apps" : "chat";
    } catch (_) {
      return "chat";
    }
  });
  const chatScope = resolveChatStorageScope(user, token);
  const chatScopeRef = useRef(chatScope);
  chatScopeRef.current = chatScope;
  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem('zolaid-language') || 'en';
    } catch (_) {
      return 'en';
    }
  });
  const [settingsToast, setSettingsToast] = useState("");
  const [agentRequestCount, setAgentRequestCount] = useState(0);
  const [tabCompletionCount, setTabCompletionCount] = useState(0);
  const [bootThread] = useState(() => bootThreadForScope(resolveChatStorageScope()));
  const [messages, setMessages] = useState(bootThread.messages);
  const [input, setInput] = useState("");
  const [socraticMode, setSocraticMode] = useState(false);
  const [socraticStage, setSocraticStage] = useState(0);
  const [businessIdea, setBusinessIdea] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  const [showBrowserOperator, setShowBrowserOperator] = useState(false);
  const [browserOperatorEnabled, setBrowserOperatorEnabled] = useState(() => {
    try {
      return localStorage.getItem('zolaid-browser-operator-enabled') === 'true';
    } catch (_) {
      return false;
    }
  });
  const [browserPreviewUrl, setBrowserPreviewUrl] = useState("https://www.booking.com/flights");
  const [showBrowserPanel, setShowBrowserPanel] = useState(false);
  const [browserPanelMinimized, setBrowserPanelMinimized] = useState(false);
  const [browserPanelFullscreen, setBrowserPanelFullscreen] = useState(false);
  /** Last browser operator run result: { steps, summary, finalUrl }. Used for Code/Dashboard tabs. */
  const [browserOperatorLastRun, setBrowserOperatorLastRun] = useState(null);
  /** Active tab in browser operator panel (preview only). */
  const [browserOperatorTab, setBrowserOperatorTab] = useState('preview');
  /** Live sandbox view (Operator-style): { running, stepIndex, screenshotBase64, url, lastAction } from GET /api/browser-operator/screenshot */
  const [browserOperatorLiveState, setBrowserOperatorLiveState] = useState(null);
  const [showImageEditorPanel, setShowImageEditorPanel] = useState(false);
  const [imageEditorPanelMinimized, setImageEditorPanelMinimized] = useState(false);
  /** Dedicated Photoshop workspace (same engine as Image Editor, separate panel chrome). */
  const [showPhotoshopEditorPanel, setShowPhotoshopEditorPanel] = useState(false);
  const [photoshopEditorPanelMinimized, setPhotoshopEditorPanelMinimized] = useState(false);
  const [showVideoEditorPanel, setShowVideoEditorPanel] = useState(false);
  const [videoEditorPanelMinimized, setVideoEditorPanelMinimized] = useState(false);
  /** When true, video editor will open with Script-to-Video section focused (reset after consumed) */
  const [openVideoEditorForScriptToVideo, setOpenVideoEditorForScriptToVideo] = useState(false);
  /** 'default' | 'large' | 'full' â€” full moves panel to right of typing box at full height */
  const [taskPanelView, setTaskPanelView] = useState('default');
  const [stream, setStream] = useState(null);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [listening, setListening] = useState(false);
  const [dictationDraft, setDictationDraft] = useState('');
  const [dictationLevels, setDictationLevels] = useState(() => Array.from({ length: 48 }, () => 0.08));
  const [voiceHint, setVoiceHint] = useState('');
  const voiceHintTimerRef = useRef(null);
  const dictationRecognitionRef = useRef(null); // unused legacy (Web Speech)
  const dictationAudioRef = useRef({ stream: null, ctx: null, raf: 0, recorder: null, chunks: [], timer: 0, transcribing: false });
  const dictationDraftRef = useRef(''); // finalized transcript for this session
  const dictationActiveRef = useRef(false);
  const dictationBaseInputRef = useRef(''); // typing-box text before this dictation started
  const [dictationBusy, setDictationBusy] = useState(false);
  const [voiceCallActive, setVoiceCallActive] = useState(false);
  const [voiceCallStatus, setVoiceCallStatus] = useState('');
  const [voicePresetMenuOpen, setVoicePresetMenuOpen] = useState(false);
  const [voicePreset, setVoicePreset] = useState(() => {
    try {
      const saved = localStorage.getItem('zolaid-voice-preset');
      if (saved === 'woman' || saved === 'man') return saved;
    } catch (_) {}
    return 'woman';
  });
  const voiceCallActiveRef = useRef(false);
  const voicePresetRef = useRef(voicePreset);
  const voiceCallRecognitionRef = useRef(null);
  const voiceSpokenIdsRef = useRef(new Set());
  const voiceSpeakingRef = useRef(false);
  const voiceStreamSpokenRef = useRef({ id: null, end: 0 });
  const voiceCallWrapRef = useRef(null);
  const realtimeCallRef = useRef(null);
  const voiceCallModeRef = useRef('browser'); // 'realtime' | 'browser'
  const voiceBriefRef = useRef([]); // recent voice transcripts for website handoff
  const voiceBuildTriggeredRef = useRef(false);
  const stopSpeechPlaybackRef = useRef(() => {});
  const startVoiceCallListeningRef = useRef(() => {});
  const startVoiceCallRef = useRef(() => {});
  const speakZolaidReplyRef = useRef(async () => {});
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [showProVideoEditor, setShowProVideoEditor] = useState(false);
  const [showProImageEditor, setShowProImageEditor] = useState(false);
  const [showWebsiteBuilder, setShowWebsiteBuilder] = useState(false);
  const [websiteBuilderSeed, setWebsiteBuilderSeed] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [chatHistory, setChatHistory] = useState(bootThread.history);
  const [currentChatId, setCurrentChatId] = useState(bootThread.chatId);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => (
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 769px)').matches
  ));
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [settingsNavQuery, setSettingsNavQuery] = useState('');
  const [selectedSettingsItem, setSelectedSettingsItem] = useState(null);
  const [privacyEnabled, setPrivacyEnabled] = useState(() => {
    try {
      return localStorage.getItem('zolaid-privacy-enabled') === 'true';
    } catch (_) {
      return false;
    }
  });
  const [showSearchGlass, setShowSearchGlass] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [messageFeedback, setMessageFeedback] = useState({}); // { messageId: 'thumbsUp' | 'thumbsDown' }
  const [showCreditTopUp, setShowCreditTopUp] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const chatAbortControllerRef = useRef(null);
  const stoppedReplyIdsRef = useRef(new Set());
  const pendingBotMessageIdRef = useRef(null);
  const [showPromptPresets, setShowPromptPresets] = useState(false);
  const [showZolaidComputer, setShowZolaidComputer] = useState(false);
  const [zolaidComputerInitialViewMode, setZolaidComputerInitialViewMode] = useState(defaultZolaidComputerViewMode);
  const [zolaidActions, setZolaidActions] = useState([]);
  const [zolaidArtifacts, setZolaidArtifacts] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  /** New Preview panel (AgentSandbox) docked beside chat. Opens from the small screen. */
  const [showSandbox, setShowSandbox] = useState(false);
  const [sandboxSettingsRequest, setSandboxSettingsRequest] = useState(null);
  const [sandboxPreviewKind, setSandboxPreviewKind] = useState('idle');
  const [sandboxProcessLog, setSandboxProcessLog] = useState([]);
  const [sandboxMinimized, setSandboxMinimized] = useState(false);
  const [sandboxFullscreen, setSandboxFullscreen] = useState(false);
  const [videoEdit, setVideoEdit] = useState(null);
  const [computerSlides, setComputerSlides] = useState([]);
  const slideTimersRef = useRef([]);
  const [siteBuild, setSiteBuild] = useState(null);
  const siteSourceRef = useRef(bootThread.siteSource);
  const awaitingSiteSubjectRef = useRef(false);
  const siteBriefRef = useRef(null);
  const siteStreamTokenRef = useRef(0);
  const siteStreamTimerRef = useRef(null);
  const chatQueueRef = useRef([]);
  const chatHttpDoneRef = useRef(true);
  const typewriterDoneRef = useRef(true);
  const streamingMessageIdRef = useRef(null);
  const sendMessageRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const currentChatIdRef = useRef(currentChatId);
  currentChatIdRef.current = currentChatId;
  const chatScopePrevRef = useRef(chatScope);

  // Switch guest vs signed-in chat stores on login/logout. Never merge across identities.
  useEffect(() => {
    if (chatScopePrevRef.current === chatScope) return;
    setIsLoadingChat(true);
    try { chatAbortControllerRef.current?.abort(); } catch (_) {}
    const next = bootThreadForScope(chatScope);
    siteSourceRef.current = next.siteSource;
    awaitingSiteSubjectRef.current = false;
    siteBriefRef.current = null;
    currentChatIdRef.current = next.chatId;
    setChatHistory(privacyEnabled ? [] : next.history);
    setCurrentChatId(next.chatId);
    setMessages(next.messages);
    setIsStreaming(false);
    setStreamingMessageId(null);
    // Mark scope applied only after UI state is pointed at the new store (blocks save race).
    chatScopePrevRef.current = chatScope;
    setTimeout(() => setIsLoadingChat(false), 100);
  }, [chatScope, privacyEnabled]);

  const pumpChatQueue = () => {
    if (!chatHttpDoneRef.current || !typewriterDoneRef.current) return;
    const next = chatQueueRef.current.shift();
    if (!next || !sendMessageRef.current) return;
    sendMessageRef.current(next.text, next.retryMessageId, next.files);
  };

  const markHttpDone = () => {
    chatHttpDoneRef.current = true;
    pendingBotMessageIdRef.current = null;
    chatAbortControllerRef.current = null;
  };

  const finishReplyNow = (botMessageId) => {
    chatHttpDoneRef.current = true;
    typewriterDoneRef.current = true;
    pendingBotMessageIdRef.current = null;
    chatAbortControllerRef.current = null;
    if (botMessageId == null || streamingMessageIdRef.current === botMessageId) {
      setIsStreaming(false);
      setStreamingMessageId(null);
      streamingMessageIdRef.current = null;
    }
    pumpChatQueue();
  };

  const stopActiveReply = () => {
    if (siteStreamTimerRef.current) {
      window.clearInterval(siteStreamTimerRef.current);
      siteStreamTimerRef.current = null;
    }
    siteStreamTokenRef.current += 1;
    const botId = streamingMessageIdRef.current || pendingBotMessageIdRef.current;
    if (botId != null) stoppedReplyIdsRef.current.add(botId);
    const controller = chatAbortControllerRef.current;
    if (controller) {
      try { controller.abort(); } catch (_) {}
    }
    stopSpeechPlaybackRef.current();
    if (botId != null) voiceSpokenIdsRef.current.add(botId);
    const now = Date.now();
    setMessages((prev) => prev.map((item) => {
      if (item.activity === 'live') return settleWork(item, now);
      if (botId != null && item.id === botId) return settleStoppedItem(item, now);
      return item;
    }));
    setSiteBuild((prev) => (prev && prev.streaming ? { ...prev, streaming: false } : prev));
    chatQueueRef.current = [];
    finishReplyNow(botId);
    if (voiceCallActiveRef.current) {
      setVoiceCallStatus('Listening');
      setTimeout(() => {
        if (voiceCallActiveRef.current && !voiceSpeakingRef.current) {
          startVoiceCallListeningRef.current();
        }
      }, 300);
    }
  };

  // Gmail organizer (OAuth handled by backend /connect/gmail)
  const [showGmailPanel, setShowGmailPanel] = useState(false);
  const [gmailState, setGmailState] = useState(() => {
    try {
      return localStorage.getItem('zolaid-gmail-state') || '';
    } catch (_) {
      return '';
    }
  });
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailMessages, setGmailMessages] = useState([]);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [gmailLastRefresh, setGmailLastRefresh] = useState(null);
  const [gmailError, setGmailError] = useState('');
  const [gmailOrganizing, setGmailOrganizing] = useState(false);
  const [gmailProgress, setGmailProgress] = useState({ done: 0, total: 0 });

  // Autonomous Agent state
  const [autonomousAgentEnabled, setAutonomousAgentEnabled] = useState(() => {
    try {
      return localStorage.getItem('zolaid-autonomous-agent-enabled') === 'true';
    } catch (_) {
      return false;
    }
  });
  const [showAutonomousAgent, setShowAutonomousAgent] = useState(false);
  const [autonomousAgentTask, setAutonomousAgentTask] = useState(null);
  const [autonomousAgentActive, setAutonomousAgentActive] = useState(false);
  const [operatorConfirmPending, setOperatorConfirmPending] = useState(null);
  const [operatorTakeoverPending, setOperatorTakeoverPending] = useState(null);
  const [operatorTakeoverInputValue, setOperatorTakeoverInputValue] = useState('');
  const [showAIBrowserAgent, setShowAIBrowserAgent] = useState(false);
  const [browserAgentTask, setBrowserAgentTask] = useState('');
  const pendingBrowserResultMessageIdRef = useRef(null);
  const lastBrowserUrlRef = useRef('');

  // Connected apps from Apps page (e.g. Spotify, Photoshop) â€“ used so the AI can suggest/use them (edit photo with Photoshop, etc.)
  const [connectedApps, setConnectedApps] = useState(() => {
    try {
      const raw = localStorage.getItem('zolaid-connected-apps');
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch (_) {
      return [];
    }
  });
  const connectedAppsRef = useRef(connectedApps);
  useEffect(() => {
    connectedAppsRef.current = connectedApps;
  }, [connectedApps]);

  /** Backend APP_*_API_KEY flags (e.g. APP_PHOTOSHOP_API_KEY) â€” merge into plugin + chat payload without localStorage */
  const [integrationKeyApps, setIntegrationKeyApps] = useState({});
  /** True when server has Firefly credentials (same as GET /chat/capabilities photoshopImageEdit). */
  const [photoshopImageEditServer, setPhotoshopImageEditServer] = useState(false);
  useEffect(() => {
    try {
      const base = getApiBase();
      const path = "/api/apps/integration-keys-status";
      const url = base ? `${base.replace(/\/+$/, "")}${path}` : path;
      fetch(url, { credentials: credentialsModeForApiUrl(url) })
        .then((r) => r.json())
        .then((d) => {
          if (d && d.ok && d.apps && typeof d.apps === "object") setIntegrationKeyApps(d.apps);
          if (d && typeof d.photoshopImageEdit === "boolean") setPhotoshopImageEditServer(d.photoshopImageEdit);
        })
        .catch(() => {});
    } catch (_) {}
  }, []);

  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage or default to 'light'
    const savedTheme = localStorage.getItem('zolaid-theme');
    return savedTheme || 'light';
  });
  const [resolvedTheme, setResolvedTheme] = useState('light'); // 'light' | 'dark' for CSS
  const [isDragging, setIsDragging] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [openChatMenuId, setOpenChatMenuId] = useState(null);

  // Model selector state
  const DEFAULT_MODELS = [
    { label: 'Claude Sonnet 4.5', value: 'claude-sonnet-4-5-20250929' },
    { label: 'Claude Opus 4.5', value: 'claude-opus-4-5' },
    { label: 'Gemini 3 Pro', value: 'gemini-3-pro' },
    { label: 'Grok Code', value: 'grok-code' }
  ];
  const [models, setModels] = useState(DEFAULT_MODELS);
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODELS[0].value);
  const [timezone, setTimezone] = useState(() => {
    try {
      return localStorage.getItem('zolaid-timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (_) {
      return 'UTC';
    }
  });

  // Helper functions
  const goToFromSettings = (path) => {
    setShowSettingsMenu(false);
    navigate(path);
  };

  const normalizeProblemIdFromQuery = (message) => {
    const text = String(message || '').toLowerCase();
    const problems = Array.isArray(aiBusinessProblems) ? aiBusinessProblems : [];
    if (!problems.length) return null;

    // Direct id match
    for (const p of problems) {
      const id = String(p.id || '').toLowerCase();
      if (id && text.includes(id)) return String(p.id);
    }

    // Heuristic keyword mapping
    const keywordToId = [
      { re: /\bmarketing\b/, id: 'marketing' },
      { re: /\bcontent\b|\bblog\b|\bseo content\b/, id: 'content-marketing' },
      { re: /\bseo\b|\bkeyword\b/, id: 'seo' },
      { re: /\bads\b|\badvert\b|\bfacebook ads\b|\bgoogle ads\b/, id: 'ads' },
      { re: /\bemail\b|\bnewsletter\b/, id: 'email-marketing' },
      { re: /\bsales\b|\bprospect\b|\boutbound\b/, id: 'sales' },
      { re: /\blead\b|\blead gen\b|\blead generation\b/, id: 'lead-generation' },
      { re: /\bsupport\b|\bcustomer support\b|\bticket\b/, id: 'customer-support' },
      { re: /\bautomation\b|\bworkflow\b|\bzapier\b/, id: 'automation' },
      { re: /\bbookkeeping\b|\baccounting\b/, id: 'bookkeeping' },
      { re: /\benablement\b/, id: 'sales-enablement' },
    ];
    for (const k of keywordToId) {
      if (k.re.test(text)) {
        const exists = problems.some((p) => String(p.id) === k.id);
        if (exists) return k.id;
      }
    }

    // Label / description match
    for (const p of problems) {
      const label = String(p.label || '').toLowerCase();
      const cleaned = label.replace(/best ai for\s+/g, '').trim();
      if (cleaned && cleaned.length > 2 && text.includes(cleaned)) return String(p.id);
      const labelTokens = cleaned.split(/[^a-z0-9]+/g).filter(Boolean);
      if (labelTokens.length) {
        const hitCount = labelTokens.reduce((acc, t) => acc + (t.length > 2 && text.includes(t) ? 1 : 0), 0);
        if (hitCount >= Math.min(2, labelTokens.length)) return String(p.id);
      }

      const desc = String(p.description || '').toLowerCase();
      if (desc) {
        const descTokens = desc.split(/[^a-z0-9]+/g).filter(Boolean);
        const hitCount = descTokens.reduce((acc, t) => acc + (t.length > 3 && text.includes(t) ? 1 : 0), 0);
        if (hitCount >= 3) return String(p.id);
      }
    }

    return null;
  };

  const buildAiToolsCategoryFallbackMarkdown = () => {
    const problems = Array.isArray(aiBusinessProblems) ? aiBusinessProblems : [];
    if (!problems.length) return "I don't have any AI tool ranking categories loaded right now.";
    const top = problems.slice(0, 12);
    const lines = [];
    lines.push("I can share ranked AI tools by category, but I couldn't detect which category you meant.");
    lines.push("");
    lines.push("Try one of these:");
    top.forEach((p) => {
      lines.push(`- **${p.label}** (say: \'best ai for ${String(p.id).replace(/-/g, ' ')}\')`);
    });
    lines.push("");
    lines.push("Example: **best ai for marketing**");
    return lines.join("\n");
  };

  const buildAiToolsRankingMarkdown = (problemId) => {
    const pid = String(problemId || '').trim();
    const problems = Array.isArray(aiBusinessProblems) ? aiBusinessProblems : [];
    const problem = problems.find((p) => String(p.id) === pid);
    const list = aiProblemRankings && typeof aiProblemRankings === 'object' ? aiProblemRankings[pid] : null;
    const items = Array.isArray(list) ? list : [];
    if (!problem || !items.length) return null;

    const top = items.slice(0, 5);
    const lines = [];
    const esc = (v) => String(v || '').replace(/\|/g, '\\|').replace(/\n/g, ' ').trim();

    lines.push(`### ${problem.label}`);
    if (problem.description) lines.push(`${problem.description}`);
    lines.push('');
    lines.push('| Rank | Tool | Fit | Rating | Pricing | Why | Website |');
    lines.push('|---:|---|---:|---:|---|---|---|');
    top.forEach((t, idx) => {
      const name = esc(t?.name);
      const pricing = esc(t?.pricing);
      const rating = t?.rating != null ? esc(t.rating) : '';
      const fit = t?.fitScore != null ? esc(t.fitScore) : '';
      const why = esc(t?.why);
      const website = esc(t?.website);
      lines.push(`| ${idx + 1} | ${name} | ${fit} | ${rating} | ${pricing} | ${why} | ${website} |`);
    });

    return lines.filter((x) => x !== null).join('\n');
  };

  const GmailLogo = ({ className = "gmail-logo" }) => (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
    >
      <path fill="#EA4335" d="M20 6.5V18c0 .55-.45 1-1 1h-2V9.7l-5 3.75-5-3.75V19H5c-.55 0-1-.45-1-1V6.5l8 6 8-6z" />
      <path fill="#FBBC04" d="M20 6.5l-8 6-8-6V6c0-.55.45-1 1-1h1l6 4.5L18 5h1c.55 0 1 .45 1 1v.5z" />
      <path fill="#34A853" d="M7 9.7V19H5c-.55 0-1-.45-1-1V6.5l3 3.2z" />
      <path fill="#4285F4" d="M20 6.5V18c0 .55-.45 1-1 1h-2V9.7l3-3.2z" />
    </svg>
  );

  const unifiedChatbotPlusTools = getUnifiedToolsFor('chatbotPlus');

  const openSandbox = (opts = {}) => {
    const kind = opts.kind || 'idle';
    if (opts.show !== false) {
      setShowSandbox(true);
      setSandboxMinimized(false);
    }
    setSandboxPreviewKind(kind);
    if (opts.url) setBrowserPreviewUrl(opts.url);
    if (opts.log) {
      setSandboxProcessLog([String(opts.log)]);
    }
  };

  const revealComputer = (message) => {
    setSandboxMinimized(false);
    setShowSandbox(true);
    if (message?.text === 'Editing it on the computer.') {
      setSandboxPreviewKind('video');
      return;
    }
    if (message?.text === 'Opening it on the computer.') {
      setSandboxPreviewKind('browser');
      return;
    }
    if (siteBuild) {
      setSandboxPreviewKind(siteBuild.kind === 'slides' ? 'slides' : 'build');
    }
  };

  const openLiveScreenUsage = () => {
    setShowSettingsMenu(false);
    setSelectedSettingsItem(null);
    setShowSandbox(true);
    setSandboxMinimized(false);
    setSandboxSettingsRequest({ section: 'usage', id: Date.now() });
  };

  const fetchGeneratedSiteFromBrief = async (brief, extras = {}) => {
    const base = getApiBase();
    if (!base) return null;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), 90000) : null;
    try {
      const res = await fetch(`${base}/website/generate-from-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: credentialsModeForApiUrl(base),
        signal: controller?.signal,
        body: JSON.stringify({
          brief: String(brief || '').slice(0, 4000),
          constraints: extras.constraints || undefined,
          previous: extras.previous || undefined,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data || (!data.html && !data.artifacts?.html)) return null;
      const html = String(data.html || data.artifacts?.html || '');
      const css = String(data.css || data.artifacts?.css || '');
      const js = String(data.js || data.artifacts?.js || '');
      if (html.length < 40 || !css) return null;
      const source = renderedSourceFromArtifacts({
        brand: data.brand || extras.fallbackBrand || 'Site',
        brief,
        html,
        css,
        js,
      });
      return { source, files: filesFromSource(source) };
    } catch (_) {
      return null;
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  };

  const fetchRevisedSiteFromBrief = async (source, instruction) => {
    const base = getApiBase();
    if (!base || !source) return null;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? window.setTimeout(() => controller.abort(), 90000) : null;
    try {
      let html = String(source.html || '');
      let css = String(source.css || '');
      let js = String(source.js || '');
      if (!html || !css) {
        try {
          const preview = previewFromFiles(filesFromSource(source));
          html = preview.html || html;
          css = preview.css || css;
          js = preview.js || js;
        } catch (_) { /* keep empty */ }
      }
      const res = await fetch(`${base}/website/revise-from-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: credentialsModeForApiUrl(base),
        signal: controller?.signal,
        body: JSON.stringify({
          brief: String(source.brief || '').slice(0, 2000),
          instruction: String(instruction || '').slice(0, 2000),
          brand: source.brand || '',
          html,
          css,
          js,
        }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data || (!data.html && !data.artifacts?.html)) return null;
      const next = renderedSourceFromArtifacts({
        brand: data.brand || source.brand || 'Site',
        brief: source.brief || instruction,
        html: String(data.html || data.artifacts?.html || ''),
        css: String(data.css || data.artifacts?.css || ''),
        js: String(data.js || data.artifacts?.js || ''),
      });
      if (next.html.length < 40 || !next.css) return null;
      return { source: next, files: filesFromSource(next) };
    } catch (_) {
      return null;
    } finally {
      if (timer) window.clearTimeout(timer);
    }
  };

  const beginLiveSite = (source, messageToSend, options = {}) => {
    let files;
    try {
      files = options.files || filesFromSource(source);
    } catch (_) {
      return;
    }
    const preview = previewFromFiles(files);
    const token = siteStreamTokenRef.current + 1;
    siteStreamTokenRef.current = token;
    siteSourceRef.current = source;
    if (siteStreamTimerRef.current) window.clearInterval(siteStreamTimerRef.current);
    const copies = files.map((file) => ({ ...file, shown: '' }));
    const brand = source.brand || 'the site';
    const thoughtText = source.kind === 'slides'
      ? [
          `This is a deck about ${source.topic}.`,
          'It opens on a full-color title, then an agenda, three points, a number, a quote, two columns, and a close.',
          'The lines are short enough to read from the back of the room.',
          'The titles and the sentences live in one source, so the files and the slides match.',
          'I will write system.json first, then slides.js.',
        ]
      : source.kind === 'stays'
      ? [
          `This is a place to find a stay, named ${brand}.`,
          'It is for someone choosing a home for a few nights.',
          'The page will be a search bar, a row of categories, and a grid of homes.',
          'The name, the colour, and the listings live in one source, so a change updates every file.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ]
      : source.kind === 'bank'
      ? [
          `This is a digital bank named ${brand}.`,
          'It is for someone opening an account, not browsing a generic product page.',
          'The page has a hero, accounts, cards, transfers, security, and fees.',
          'The name, the accounts, and the fees are written together, so the files and the page match.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ]
      : source.kind === 'search'
      ? [
          `This is a search engine named ${brand}.`,
          'The first thing on the page is a search box.',
          'Under it are example results, then where those results come from, and a privacy note.',
          'The name and the results are written together, so the files and the page match.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ]
      : source.kind === 'jobs'
      ? [
          `This is a job platform named ${brand}.`,
          'The page lists open roles, the companies hiring, and an application.',
          'The main action is to apply for a role.',
          'The roles and the companies are written together, so the files and the page match.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ]
      : source.kind === 'studied'
      ? [
          `This is a page named ${brand}, shaped from a public site.`,
          'I checked what that product does and how its public page is arranged.',
          'The page here uses its own name, its own sentences, and a colored panel instead of copied pictures.',
          'The name, the colors, and the sections are written together, so the files and the page match.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ]
      : source.kind === 'rendered'
      ? [
          'This is another website from the request.',
          'The page, the styles, and the script go on the computer.',
          'The chat stays one short line while those files are written.',
        ]
      : source.offer === 'agent'
      ? [
          `This is a website for ${brand}, an autonomous AI agent.`,
          'A customer should see what the agent does, which tasks it runs, how safety works, and how to start.',
          'The navigation is Agent, Tasks, Safety, and Plans.',
          'The name and the sections come from the request, so the files and the page match.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ]
      : [
          `This is a page for ${brand}, taken from the request.`,
          'It says what the product does, who uses it, and the action on the page.',
          'The name, the navigation, and the sections come from that request.',
          'I will write system.json first, then the readme, the package, the styles, the behaviour, and the page.',
        ];
    const thoughts = thoughtText.map((text) => ({ text, shown: '' }));
    const totalChars = copies.reduce((sum, file) => sum + file.content.length, 0);
    const codeChunk = Math.max(12, Math.ceil(totalChars / 200));
    setSandboxPreviewKind('build');
    setSandboxProcessLog(['Planning the page.']);
    setSiteBuild({
      phase: 'thinking',
      thoughts: thoughts.map((item) => ({ ...item })),
      files: copies.map((file) => ({ ...file })),
      active: copies[0].path,
      preview,
      streaming: true,
      fileNote: '',
      kind: source.kind === 'slides' ? 'slides' : 'site',
    });
    setInput('');
    setSelectedFiles([]);
    const replaceIds = new Set(options.replaceIds || []);
    const workId = Date.now() + 1;
    setMessages((prev) => [
      ...prev
        .filter((item) => !replaceIds.has(item.id))
        .map((item) => settleWork(item)),
      userChatMessage(messageToSend, { id: workId - 1 }),
      { id: workId, sender: 'bot', text: 'Building it on the computer.', activity: 'live', startedAt: Date.now(), steps: ['Planning the page.'] },
    ]);
    let mode = 'think';
    let thoughtIndex = 0;
    let fileIndex = 0;
    let lastLog = 'Planning the page.';
    const pushLog = (line) => {
      if (!line || line === lastLog) return;
      lastLog = line;
      setSandboxProcessLog((prev) => [...prev, line].slice(-12));
      setMessages((prev) => prev.map((item) => {
        if (item.id !== workId) return item;
        const next = Array.isArray(item.steps) ? item.steps : [];
        if (next[next.length - 1] === line) return item;
        return { ...item, steps: [...next, line] };
      }));
    };
    siteStreamTimerRef.current = window.setInterval(() => {
      if (siteStreamTokenRef.current !== token) {
        window.clearInterval(siteStreamTimerRef.current);
        return;
      }
      if (mode === 'think') {
        const thought = thoughts[thoughtIndex];
        if (thought && thought.shown.length < thought.text.length) {
          thought.shown = thought.text.slice(0, thought.shown.length + 3);
          setSiteBuild((prev) => (prev ? {
            ...prev,
            phase: 'thinking',
            thoughts: thoughts.map((item) => ({ ...item })),
          } : prev));
          return;
        }
        if (thoughtIndex < thoughts.length - 1) {
          thoughtIndex += 1;
          return;
        }
        mode = 'code';
        return;
      }
      const file = copies[fileIndex];
      if (!file) {
        window.clearInterval(siteStreamTimerRef.current);
        setSiteBuild((prev) => (prev ? {
          ...prev,
          phase: 'done',
          streaming: false,
          fileNote: source.kind === 'slides' ? 'The deck is ready.' : 'The page is ready.',
          files: copies.map((item) => ({ ...item })),
        } : prev));
        const ready = source.kind === 'slides' ? 'The deck is ready.' : 'The page is ready.';
        pushLog(ready);
        setMessages((prev) => prev.map((item) => settleWork(item)));
        return;
      }
      const nextLen = Math.min(file.content.length, file.shown.length + codeChunk);
      file.shown = file.content.slice(0, nextLen);
      setSiteBuild((prev) => (prev ? {
        ...prev,
        phase: 'code',
        active: file.path,
        fileNote: `Writing ${file.path}.`,
        files: copies.map((item) => ({ ...item })),
      } : prev));
      pushLog(`Writing ${file.path}.`);
      if (nextLen >= file.content.length) fileIndex += 1;
    }, 48);
  };

  const closeSandbox = () => {
    if (sandboxPreviewKind === 'video') {
      setMessages((prev) => prev.map((item) => (
        item.activity === 'live' && isComputerActivity(item.text)
          ? settleWork(item)
          : item
      )));
      setVideoEdit(null);
    }
    setShowSandbox(false);
    setSandboxFullscreen(false);
    setSandboxMinimized(false);
    setSandboxPreviewKind('idle');
  };

  const openChatSettingsUsage = () => {
    setSandboxSettingsRequest(null);
    closeSandbox();
    setShowSettingsMenu(true);
    setSelectedSettingsItem('usage');
  };

  const getUnifiedEmoji = (iconKey) => {
    if (iconKey === 'chart_emoji') return '\u{1F4CA}';
    if (iconKey === 'image_emoji') return '\u{1F5BC}\u{FE0F}';
    if (iconKey === 'video_emoji') return '\u{1F3AC}';
    if (iconKey === 'web_emoji') return '\u{1F310}';
    if (iconKey === 'idea_emoji') return '\u{1F4A1}';
    if (iconKey === 'camera_emoji') return '\u{1F4F7}';
    if (iconKey === 'computer_emoji') return '\u{1F4BB}';
    if (iconKey === 'prompt_emoji') return '\u{1F4A1}';
    return '';
  };

  const handleUnifiedChatbotToolClick = async (tool) => {
    if (!tool) return;
    if (tool.kind === 'route' && tool.route) {
      navigate(tool.route);
      return;
    }

    if (tool.key === 'google-drive') {
      loadGoogleDriveFiles();
      return;
    }

    if (tool.key === 'onedrive') {
      loadOneDriveFiles();
      return;
    }

    if (tool.key === 'gmail') {
      openSandbox({ kind: 'gmail', log: 'Opening Gmail in Preview' });
      setShowGmailPanel(true);
      try {
        const connected = await fetchGmailStatus();
        if (connected) await fetchGmailMessages();
      } catch (e) {
        setGmailError(e?.message || String(e));
      }
      return;
    }

    if (tool.key === 'camera' || tool.key === 'image-editor' || tool.key === 'video-editor' || tool.key === 'website-builder' || tool.key === 'idea-generation' || tool.key === 'prompt-presets' || tool.key === 'zolaid-computer') {
      return;
    }

    if (tool.key === 'camera') {
      openSandbox({ kind: 'camera', log: 'Opening camera in Preview' });
      openCamera();
      return;
    }

    if (tool.key === 'image-editor') {
      openSandbox({ kind: 'image', log: 'Opening Image Editor' });
      setShowPhotoshopEditorPanel(false);
      setShowImageEditorPanel(true);
      setImageEditorPanelMinimized(false);
      setTaskPanelView('default');
      return;
    }

    if (tool.key === 'video-editor') {
      openSandbox({ kind: 'video', log: 'Opening Video Editor' });
      setShowImageEditorPanel(false);
      setShowPhotoshopEditorPanel(false);
      setShowVideoEditorPanel(true);
      setVideoEditorPanelMinimized(false);
      setTaskPanelView('default');
      return;
    }

    if (tool.key === 'website-builder') {
      openSandbox({ kind: 'site', log: 'Opening website builder' });
      setShowWebsiteBuilder(true);
      return;
    }

    if (tool.key === 'idea-generation') {
      openSandbox({ kind: 'ideas', log: 'Starting Idea Generation' });
      startSocraticMethod();
      return;
    }

    if (tool.key === 'zolaid-computer') {
      openSandbox({ kind: 'site', log: 'Opening Zolaid Computer' });
      setShowZolaidComputer(true);
      return;
    }

    if (tool.key === 'prompt-presets') {
      openSandbox({ kind: 'presets', log: 'Opening prompt presets' });
      setShowPromptPresets(true);
    }
  };

  const showSettingsToastMsg = (message) => {
    setSettingsToast(message);
    setTimeout(() => setSettingsToast(""), 3000);
  };

  // Sync language with i18n
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  // No proactive backend probe on load â€” avoids 500 in console when backend is down. Models use defaults until first successful backend use.

  const CodeBlock = ({ inline, children, ...props }) => {
    if (!inline) return null;
      return (
        <code className="markdown-code-inline" {...props}>
          {children}
        </code>
    );
  };

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const chatBoxRef = useRef(null);
  /** Scroll target when Image Editor panel opens (above composer). */
  const imageEditorPanelRef = useRef(null);
  const photoshopPanelRef = useRef(null);
  const composerBoxRef = useRef(null);
  const plusButtonRef = useRef(null);
  const plusMenuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchDropdownInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const accountMenuRef = useRef(null);
  const initialMessageSentRef = useRef(false);

  // Auto-scroll chat
  // Apply theme on mount and when theme changes
  useEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement;
      const body = document.body;
      // Clear first (more reliable across browsers/extensions)
      root.classList.remove('dark-theme', 'light-theme');
      body.classList.remove('dark-theme', 'light-theme');

      const setDark = () => {
        root.classList.add('dark-theme');
        body.classList.add('dark-theme');
      };
      const setLight = () => {
        root.classList.add('light-theme');
        body.classList.add('light-theme');
      };

      if (theme === 'dark') return setDark();
      if (theme === 'light') return setLight();

      // System - use system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? setDark() : setLight();
    };

    applyTheme();

    // Listen for system theme changes if using system theme
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme();
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  useEffect(() => {
    const imageOpen = showImageEditorPanel && !imageEditorPanelMinimized;
    const psOpen = showPhotoshopEditorPanel && !photoshopEditorPanelMinimized;
    if (!imageOpen && !psOpen) return;
    const id = requestAnimationFrame(() => {
      if (psOpen) photoshopPanelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      else imageEditorPanelRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(id);
  }, [
    showImageEditorPanel,
    imageEditorPanelMinimized,
    showPhotoshopEditorPanel,
    photoshopEditorPanelMinimized,
    taskPanelView,
  ]);

  // Handle initial message and actions from homepage typing box
  useEffect(() => {
    const action = location.state?.action;
    const initialMsg = location.state?.initialMessage;
    const files = location.state?.files || [];
    const openVoiceCall = Boolean(location.state?.voiceCall);

    if (openVoiceCall && !action && !initialMsg && !(Array.isArray(files) && files.length > 0)) {
      window.history.replaceState({}, document.title);
      setTimeout(() => {
        try { startVoiceCallRef.current(); } catch (_) {}
      }, 400);
      return;
    }

    // Handle actions (camera, image-editor, etc.)
    if (action) {
      // Clear the state immediately
      window.history.replaceState({}, document.title);
      
      // Set files if any
      if (files.length > 0) {
        setSelectedFiles(files);
      }
      
      // Handle different actions
      setTimeout(() => {
        switch (action) {
          case 'camera':
          case 'image-editor':
          case 'video-editor':
          case 'website-builder':
          case 'idea-generation':
          case 'prompt-presets':
          case 'zolaid-computer':
            break;
          case 'google-drive':
            loadGoogleDriveFiles();
            break;
          case 'onedrive':
            loadOneDriveFiles();
            break;
          case 'gmail':
            openSandbox({ kind: 'gmail', log: 'Opening Gmail in Preview' });
            setShowGmailPanel(true);
            fetchGmailStatus()
              .then((connected) => {
                if (connected) return fetchGmailMessages();
                return null;
              })
              .catch(() => {});
            break;
          default:
            break;
        }
      }, 300);
      return;
    }
    
    // Handle initial message and/or files from homepage (files-only still needs a short prompt for sendMessage)
    const trimmedInitial =
      typeof initialMsg === "string" ? initialMsg.trim() : "";
    const hasIncomingFiles = Array.isArray(files) && files.length > 0;
    const autoSendText =
      trimmedInitial ||
      (hasIncomingFiles ? "I attached file(s) for you to review." : "");

    if (!initialMessageSentRef.current && autoSendText) {
      initialMessageSentRef.current = true; // Mark as sent immediately to prevent duplicates

      // Clear the state immediately to prevent re-sending
      window.history.replaceState({}, document.title);

      if (hasIncomingFiles) {
        setSelectedFiles(files);
      }

      const timeoutId = setTimeout(() => {
        sendMessage(autoSendText);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, []);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  // Keep Apps view in sync with ?view=apps (ChatGPT-style shell)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setMainView(params.get("view") === "apps" ? "apps" : "chat");
  }, [location.search]);

  const openAppsView = () => {
    setMainView("apps");
    const params = new URLSearchParams(location.search);
    params.set("view", "apps");
    navigate({ pathname: "/chatbot", search: `?${params.toString()}` }, { replace: false });
  };

  const openChatView = (extraParams = null) => {
    setMainView("chat");
    const params = new URLSearchParams(location.search);
    params.delete("view");
    if (extraParams && typeof extraParams === "object") {
      Object.entries(extraParams).forEach(([key, value]) => {
        if (value == null || value === "") params.delete(key);
        else params.set(key, String(value));
      });
    }
    const search = params.toString();
    navigate({ pathname: "/chatbot", search: search ? `?${search}` : "" }, { replace: true });
  };

  // On load: read ?connected= from URL (e.g. after Spotify OAuth or "Use in Chatbot" from Apps page), merge into connectedApps, persist, clear URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (!connected) return;
    const ids = connected.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const keepView = params.get("view");
    if (ids.length === 0) {
      const next = keepView === "apps" ? "?view=apps" : "";
      window.history.replaceState({}, '', `${window.location.pathname}${next}`);
      return;
    }
    setConnectedApps((prev) => {
      const next = [...new Set([...prev, ...ids])];
      try {
        localStorage.setItem('zolaid-connected-apps', JSON.stringify(next));
      } catch (_) {}
      return next;
    });
    params.delete("connected");
    params.delete("mcp");
    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ""}`);
    setMainView("chat");
  }, []);

  // On load: read Gmail callback flags (?gmail_connected=true&state=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailOk = params.get('gmail_connected');
    const state = params.get('state');
    if (gmailOk !== 'true') return;

    const s = String(state || '').trim();
    if (s) {
      setGmailState(s);
      try {
        localStorage.setItem('zolaid-gmail-state', s);
      } catch (_) {}
    }
    setGmailConnected(true);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const sanitizeGmailState = (v) => {
    const s = String(v || '').trim();
    const cleaned = s.replace(/[^a-zA-Z0-9_-]/g, '');
    if (cleaned.length >= 6 && cleaned.length <= 64) return cleaned;
    return '';
  };

  const getGmailState = () => {
    const existing = sanitizeGmailState(gmailState);
    if (existing) return existing;
    const base = user?.id != null ? `zolaid_u_${String(user.id)}` : `zolaid_default_${Date.now()}`;
    const next = sanitizeGmailState(base) || sanitizeGmailState(`zolaid_${String(user?.email || 'default').split('@')[0]}`) || 'zolaid_default';
    // Ensure min length 6
    const ensured = next.length >= 6 ? next : `${next}_00000`.slice(0, 6);
    setGmailState(ensured);
    try {
      localStorage.setItem('zolaid-gmail-state', ensured);
    } catch (_) {}
    return ensured;
  };

  const connectGmail = () => {
    const state = getGmailState();
    const base = getApiBase();
    const url = base ? `${base.replace(/\/+$/, '')}/connect/gmail?state=${encodeURIComponent(state)}` : `/connect/gmail?state=${encodeURIComponent(state)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fetchGmailStatus = async () => {
    const state = getGmailState();
    const base = getApiBase();
    const url = base ? `${base.replace(/\/+$/, '')}/connect/gmail/status?state=${encodeURIComponent(state)}` : `/connect/gmail/status?state=${encodeURIComponent(state)}`;
    const res = await fetch(url, { credentials: credentialsModeForApiUrl(url) });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || data?.message || `Gmail status failed (${res.status})`);
    setGmailConnected(Boolean(data?.connected));
    return Boolean(data?.connected);
  };

  const fetchGmailMessages = async () => {
    setGmailError('');
    setGmailLoading(true);
    try {
      const state = getGmailState();
      const base = getApiBase();
      const url = base
        ? `${base.replace(/\/+$/, '')}/connect/gmail/messages?state=${encodeURIComponent(state)}&maxResults=15`
        : `/connect/gmail/messages?state=${encodeURIComponent(state)}&maxResults=15`;
      const res = await fetch(url, { credentials: credentialsModeForApiUrl(url) });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || `Failed to load Gmail messages (${res.status})`);
      setGmailMessages(Array.isArray(data?.messages) ? data.messages : []);
      setGmailLastRefresh(Date.now());
      return Array.isArray(data?.messages) ? data.messages : [];
    } catch (e) {
      setGmailError(e?.message || String(e));
      return [];
    } finally {
      setGmailLoading(false);
    }
  };

  const defaultGmailLabels = [
    'Work',
    'Finance',
    'Sales',
    'Support',
    'Newsletter',
    'Personal',
    'Other',
  ];

  const getGmailLabelSummary = (msgs) => {
    const out = {};
    for (const l of defaultGmailLabels) out[l] = 0;
    const arr = Array.isArray(msgs) ? msgs : [];
    for (const m of arr) {
      const label = classifyGmailMessage(m);
      if (out[label] != null) out[label] += 1;
    }
    return out;
  };

  const classifyGmailMessage = (m) => {
    const subject = String(m?.subject || '').toLowerCase();
    const from = String(m?.from || '').toLowerCase();
    const snippet = String(m?.snippet || '').toLowerCase();
    const text = `${subject} ${from} ${snippet}`;

    if (/(invoice|receipt|payment|paid|billing|bill|refund|transaction|statement|order\s+#)/.test(text)) return 'Finance';
    if (/(support|ticket|case\s+#|help\s*desk|incident)/.test(text)) return 'Support';
    if (/(demo|quote|pricing|proposal|lead|meeting|calendar|call\s+booked)/.test(text)) return 'Sales';
    if (/(unsubscribe|newsletter|digest|no-?reply|noreply|notification)/.test(text)) return 'Newsletter';
    if (/(family|friend|party|wedding|birthday)/.test(text)) return 'Personal';
    if (/(github|jira|asana|notion|slack|trello|confluence|google|microsoft)/.test(text)) return 'Work';
    return 'Other';
  };

  const applyGmailLabel = async ({ messageId, label }) => {
    const state = getGmailState();
    const base = getApiBase();
    const url = base ? `${base.replace(/\/+$/, '')}/connect/gmail/apply-label` : '/connect/gmail/apply-label';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: credentialsModeForApiUrl(url),
      body: JSON.stringify({ state, messageId, label }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.error || `Failed to apply label (${res.status})`);
    return data;
  };

  const organizeGmailMessages = async () => {
    if (gmailOrganizing) return;
    setGmailError('');

    const connected = await fetchGmailStatus().catch(() => false);
    if (!connected) {
      setGmailError('Gmail is not connected yet. Click â€œConnect Gmailâ€ first.');
      return;
    }
    const msgs = gmailMessages.length ? gmailMessages : await fetchGmailMessages();
    if (!msgs.length) {
      setGmailError('No Gmail messages found to organize.');
      return;
    }

    const total = Math.min(15, msgs.length);
    const ok = window.confirm(`This will create up to 7 labels and apply them to ${total} recent emails. Continue?`);
    if (!ok) return;

    setGmailOrganizing(true);
    setGmailProgress({ done: 0, total });
    try {
      for (let i = 0; i < total; i += 1) {
        const m = msgs[i];
        const label = classifyGmailMessage(m);
        if (!defaultGmailLabels.includes(label)) continue;
        await applyGmailLabel({ messageId: m.id, label });
        setGmailProgress({ done: i + 1, total });
      }
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          sender: 'bot',
          text: `âœ… Gmail organizer: labeled ${total} message(s) into up to 7 labels (Work, Finance, Sales, Support, Newsletter, Personal, Other).`,
        },
      ]);
    } catch (e) {
      setGmailError(e?.message || String(e));
    } finally {
      setGmailOrganizing(false);
    }
  };

  // Load chat history when privacy toggles (auth-scope switches are handled above — no merge)
  useEffect(() => {
    if (!privacyEnabled) {
      setChatHistory(readStoredChatHistory(chatScopeRef.current));
    } else {
      setChatHistory([]);
    }
  }, [privacyEnabled]);

  // Save current chat to history when new messages are added (not when loading, and only if privacy is not enabled)
  useEffect(() => {
    if (privacyEnabled) return;
    // While auth scope is changing, messages still belong to the previous store — do not write across.
    if (chatScopePrevRef.current !== chatScope) return;
    const scope = chatScopeRef.current;
    if (messages.length > 1 && !isLoadingChat && currentChatId) {
      const chatTitle = messages.find(m => m.sender === 'user')?.text?.substring(0, 50) || 'New Chat';
      const storedSite = siteSourceForStorage(siteSourceRef.current);
      setChatHistory(prevHistory => {
        const chatEntry = {
          id: currentChatId,
          title: chatTitle,
          messages: messagesForStorage(messages),
          timestamp: new Date().toISOString(),
          ...(storedSite ? { siteSource: storedSite } : {}),
        };
        const updatedHistory = [
          chatEntry,
          ...prevHistory.filter(h => h.id !== currentChatId)
        ].slice(0, 20); // Keep only last 20 chats
        writeStoredChatHistory(scope, updatedHistory);
        return updatedHistory;
      });
      writeActiveChatMarker(scope, currentChatId);
    } else if (messages.length > 1 && !isLoadingChat && !currentChatId) {
      // Initialize chat ID on first message
      const chatId = currentChatIdRef.current || Date.now();
      currentChatIdRef.current = chatId;
      setCurrentChatId(chatId);
    }
  }, [messages, isLoadingChat, currentChatId, privacyEnabled, chatScope]);

  useEffect(() => {
    if (privacyEnabled) return;
    if (chatScopePrevRef.current !== chatScope) return;
    const scope = chatScopeRef.current;
    if (currentChatId) writeActiveChatMarker(scope, currentChatId);
    else writeActiveChatMarker(scope, FRESH_CHAT_MARKER);
  }, [currentChatId, privacyEnabled, chatScope]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchDropdownRef.current &&
        searchInputRef.current &&
        searchDropdownInputRef.current &&
        !searchDropdownRef.current.contains(event.target) &&
        !searchInputRef.current.contains(event.target) &&
        !searchDropdownInputRef.current.contains(event.target)
      ) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (showSearchDropdown && searchDropdownInputRef.current) {
    setTimeout(() => {
        searchDropdownInputRef.current?.focus();
      }, 100);
    }
  }, [showSearchDropdown]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showPlusMenu &&
        plusButtonRef.current &&
        plusMenuRef.current &&
        !plusButtonRef.current.contains(event.target) &&
        !plusMenuRef.current.contains(event.target)
      ) {
        setShowPlusMenu(false);
      }
      if (
        showSearchGlass &&
        plusButtonRef.current &&
        !plusButtonRef.current.contains(event.target) &&
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(event.target)
      ) {
        setShowSearchGlass(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPlusMenu, showSearchGlass]);

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showSettingsMenu &&
        settingsButtonRef.current &&
        settingsMenuRef.current &&
        !settingsButtonRef.current.contains(event.target) &&
        !settingsMenuRef.current.contains(event.target)
      ) {
        setShowSettingsMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSettingsMenu]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('account') === '1') {
      setSelectedSettingsItem('overview');
      setShowSettingsMenu(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (!showAccountMenu) return undefined;
    const handlePointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [showAccountMenu]);

  // Poll browser operator live screenshot when panel is open (Operator-style live view)
  // Use relative URL so dev server proxy can forward to backend; back off when backend is unreachable
  useEffect(() => {
    if (!showBrowserPanel) return;
    const apiBase = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
    const base = apiBase ? `${apiBase}/api/browser-operator` : '/api/browser-operator';
    let failCount = 0;
    const maxFails = 5;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${base}/screenshot`);
        if (res.ok) {
          failCount = 0;
          const data = await res.json();
          setBrowserOperatorLiveState(data);
        }
      } catch (_) {
        failCount += 1;
        if (failCount >= maxFails) clearInterval(interval);
      }
    }, 600);
    return () => clearInterval(interval);
  }, [showBrowserPanel]);

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Set video srcObject when stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  // Socratic questions
  const socraticQuestions = [
    {
      stage: "Initial Hypothesis Formulation",
      questions: [
        "What is the core problem your business aims to solve?",
        "Who are your primary target customers, and what do you know about their current struggles?",
        "How is your proposed solution different from what's already available?",
      ],
    },
    {
      stage: "Deeper Exploration",
      questions: [
        "What specific challenges do your target customers face that your product addresses?",
        "What evidence do you have that customers are currently dissatisfied with existing solutions?",
        "If your product succeeded beyond your wildest dreams, what would that look like, and what steps would lead there?",
      ],
    },
    {
      stage: "Validation and Refinement",
      questions: [
        "How would you test if your target customers actually have this problem?",
        "What's the smallest version of your product you could build to get initial user feedback?",
        "Considering your competitors, what unique value proposition truly sets you apart?",
      ],
    },
    {
      stage: "Identifying Potential Obstacles and Solutions",
      questions: [
        "What are the biggest risks you foresee in bringing this idea to market?",
        "If your product fails, what would be the most likely reason? How can you mitigate that now?",
        "What resources (financial, human, technical) do you anticipate needing, and how will you acquire them?",
      ],
    },
  ];

  // Start Socratic Method
  const startSocraticMethod = () => {
    setSocraticMode(true);
    setSocraticStage(0);
    setBusinessIdea({});

    const welcomeMsg = {
      id: Date.now(),
      sender: "bot",
      text:
        "ðŸš€ Welcome to ZOLAID's **Idea Generation** (Socratic-style coaching).\n\n" +
        "I'll adapt follow-up questions to what you already shared, suggest **small real-world experiments** to validate assumptions, and each reply ends with a **Validation progress** snapshot (âœ… / ðŸ”„ / â¬œ).\n\n" +
        "Share your rough idea or hypothesis â€” then answer the questions I ask.\n\n" +
        socraticQuestions[0].questions[0],
    };
    setMessages((prev) => [...prev, welcomeMsg]);
  };

  const processSocraticResponse = (userResponse) => {
    const currentStageData = socraticQuestions[socraticStage];
    const questionIndex = Object.keys(businessIdea).filter((k) =>
      k.startsWith(`stage${socraticStage}`)
    ).length;

    setBusinessIdea((prev) => ({
      ...prev,
      [`stage${socraticStage}_q${questionIndex}`]: userResponse,
    }));

    const nextQuestionIndex = questionIndex + 1;

    if (nextQuestionIndex < currentStageData.questions.length) {
      setTimeout(() => {
        const botMsg = {
          id: Date.now(),
          sender: "bot",
          text: "Great insight! " + currentStageData.questions[nextQuestionIndex],
        };
        setMessages((prev) => [...prev, botMsg]);
      }, 600);
    } else if (socraticStage < socraticQuestions.length - 1) {
      const nextStage = socraticStage + 1;
      setSocraticStage(nextStage);

      setTimeout(() => {
        const botMsg = {
          id: Date.now(),
          sender: "bot",
          text: `Excellent! Now let's move to the ${socraticQuestions[nextStage].stage} phase.\n\n${socraticQuestions[nextStage].questions[0]}`,
        };
        setMessages((prev) => [...prev, botMsg]);
      }, 600);
    } else {
      setSocraticMode(false);
      setTimeout(() => {
        const summaryMsg = {
          id: Date.now(),
          sender: "bot",
          text:
            "ðŸŽ‰ Congratulations! You've completed the Socratic Method process.\n\nYou've thoroughly explored your idea. Would you like me to generate a summary document of your responses?",
        };
        setMessages((prev) => [...prev, summaryMsg]);
      }, 600);
    }
  };

  // Detect if the user's request needs the browser (open site, book flight, search web, etc.)
  const detectBrowserTaskIntent = (text) => {
    const raw = String(text || '').trim();
    const t = raw.toLowerCase();
    const extractUrl = (...candidates) => {
      for (const s of candidates) {
        if (!s || typeof s !== 'string') continue;
        const u = s.trim();
        if (u.startsWith('http://') || u.startsWith('https://')) return u;
        // Any domain (with optional path)
        if (/^[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(\/[^\s]*)?$/i.test(u)) return 'https://' + u;
      }
      return '';
    };
    const patterns = [
      // Any website: full URL or domain (optional path)
      { re: /\b(open|go to|navigate to|visit|load|show me|browse to)\s+(?:the\s+)?(?:website\s+)?(https?:\/\/[^\s]+|[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(\/[^\s]*)?)/i, urlGroup: 2 },
      // Book flight/hotel/ticket â†’ open Booking.com so the agent can work there (not Google)
      { re: /\b(book|find|search for|get me|look for)\s+(?:a\s+)?(flight|flights|hotel|hotels|rental|ticket|tickets)\b/i, urlGroup: 2, taskUrl: (match) => {
        const w = (match[2] || '').toLowerCase();
        if (/\b(flight|flights|ticket|tickets)\b/.test(w)) return 'https://www.booking.com/flights';
        if (/\b(hotel|hotels)\b/.test(w)) return 'https://www.booking.com';
        if (/\b(rental)\b/.test(w)) return 'https://www.booking.com/cars';
        return 'https://www.booking.com';
      }},
      { re: /\b(search the web|search online|look up|google|search for)\s+(.+)?/i, urlGroup: null },
      { re: /\b(open|go to|visit|browse to)\s+(google|booking\.com|expedia|airbnb|amazon|youtube|wikipedia|github|twitter|x\.com)/i, urlGroup: 2 },
      { re: /\b(fill out|submit|complete)\s+(?:the\s+)?(form|application)/i, urlGroup: null },
      { re: /(https?:\/\/[^\s]+)/, urlGroup: 1 },
      { re: /\b(open|go to|visit|browse to)\s+([a-z0-9][-a-z0-9.]*\.[a-z]{2,}(\/[^\s]*)?)/i, urlGroup: 2 },
    ];
    for (const pattern of patterns) {
      const { re, urlGroup, taskUrl } = pattern;
      const m = raw.match(re);
      if (m) {
        let url = '';
        if (typeof taskUrl === 'function') {
          url = taskUrl(m) || '';
        } else if (urlGroup != null && m[urlGroup]) {
          url = extractUrl(m[urlGroup]);
        }
        if (urlGroup === 2 && !url && m[2]) {
          const name = (m[2] || '').toLowerCase();
          if (name === 'google') url = 'https://www.google.com';
          else if (name === 'wikipedia') url = 'https://en.wikipedia.org';
          else if (name === 'youtube') url = 'https://www.youtube.com';
          else if (name === 'booking.com') url = 'https://www.booking.com';
          else if (name === 'github') url = 'https://github.com';
          else if (name === 'twitter' || name === 'x.com') url = 'https://x.com';
          else url = extractUrl(m[2]);
        }
        return { needsBrowser: true, url: url || '' };
      }
    }
    if (/\b(open|go to|visit|book|search|find flights?|browse|look up|look for)\b.*\b(website|web|browser|flight|hotel|online|the web)\b/i.test(t)) {
      return { needsBrowser: true, url: '' };
    }
    if (/\b(in the browser|using the browser|on the web|on google)\b/i.test(t)) {
      return { needsBrowser: true, url: '' };
    }
    // Amazon KDP (Kindle Direct Publishing) upload automation
    if (/\b(kdp|kindle direct|amazon kdp|upload to kdp|publish on kdp|kdp upload|kdp.amazon)\b/i.test(t)) {
      return { needsBrowser: true, url: 'https://kdp.amazon.com', needsKdp: true };
    }
    return { needsBrowser: false, url: '' };
  };

  // Send user message to AI backend with streaming
  const sendMessage = async (messageText = null, retryMessageId = null, queuedFiles = undefined) => {
    sendMessageRef.current = sendMessage;
    const messageToSend = messageText || input.trim();
    if (!messageToSend) return;

    const gmailOpenRequest = !retryMessageId && !isWebsiteRequest(messageToSend) && /\bgmail\b/i.test(messageToSend) && /\b(open|show|see|organiser|organizer|screen|inbox|panel)\b/i.test(messageToSend);
    if (gmailOpenRequest) {
      openSandbox({ kind: 'gmail', log: 'Opening Gmail' });
      setShowGmailPanel(true);
      setInput('');
      setSelectedFiles([]);
      awaitingSiteSubjectRef.current = false;
      siteBriefRef.current = null;
      setMessages((prev) => [
        ...prev.map((item) => settleWork(item)),
        userChatMessage(messageToSend),
        { id: Date.now() + 1, sender: 'bot', text: 'Opening Gmail.' },
      ]);
      fetchGmailStatus()
        .then((connected) => {
          if (connected) return fetchGmailMessages();
          return null;
        })
        .catch((e) => setGmailError(e?.message || String(e)));
      return;
    }

    const videoEditRequest = readVideoEdit(messageToSend);
    if (videoEditRequest && !retryMessageId) {
      const id = Date.now();
      const needsClip = videoEditRequest.action === 'open'
        || videoEditRequest.action === 'play'
        || videoEditRequest.action === 'trim';
      openSandbox({
        kind: 'video',
        log: needsClip ? 'Video editor ready — open or drop a clip.' : 'Video editor open.',
        show: true,
      });
      setSandboxMinimized(false);
      setVideoEdit({ id, ...videoEditRequest, needMedia: needsClip });
      setInput('');
      setSelectedFiles([]);
      if (siteStreamTimerRef.current) {
        window.clearInterval(siteStreamTimerRef.current);
        siteStreamTimerRef.current = null;
      }
      const reply = videoEditRequest.action === 'trim'
        ? 'Video editor is open. Open or drop a clip and I will apply that trim on the timeline.'
        : videoEditRequest.action === 'play'
          ? 'Video editor is open. Open or drop a clip to preview it.'
          : 'Video editor is open. Open or drop a clip to start editing.';
      setMessages((prev) => [
        ...prev.map((item) => settleWork(item)),
        userChatMessage(messageToSend, { id }),
        { id: id + 1, sender: 'bot', text: reply },
      ]);
      return;
    }

    // AI Tools Rankings: answer directly inside chat when user asks for best AI for a business problem.
    const looksLikeRankingQuestion =
      /\b(best|top|rank|ranking|which)\b.*\b(ai|tool|assistant)\b/i.test(messageToSend) ||
      /\bbest ai for\b/i.test(messageToSend) ||
      /\bwhich ai\b.*\bfor\b/i.test(messageToSend) ||
      /\bai\b.*\bfor\b/i.test(messageToSend);
    if (looksLikeRankingQuestion) {
      const problemId = normalizeProblemIdFromQuery(messageToSend);
      const md = problemId ? buildAiToolsRankingMarkdown(problemId) : null;
      if (md) {
        const userMsgId = Date.now();
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend, { id: userMsgId }),
          { id: userMsgId + 1, sender: 'bot', text: md },
        ]);
        setInput('');
        return;
      }

      const userMsgId = Date.now();
      setMessages((prev) => [
        ...prev,
        userChatMessage(messageToSend, { id: userMsgId }),
        { id: userMsgId + 1, sender: 'bot', text: buildAiToolsCategoryFallbackMarkdown() },
      ]);
      setInput('');
      return;
    }

    if (!retryMessageId && isDeckRequest(messageToSend)) {
      beginLiveSite(createDeckSource(messageToSend), messageToSend);
      return;
    }

    const settleSiteTurn = async (turn) => {
      if (!turn || turn.action === 'pass') return false;
      if (turn.action === 'build' && turn.source) {
        awaitingSiteSubjectRef.current = false;
        siteBriefRef.current = null;
        // Prefer foundation-model codegen; fall back to structured source only if the model fails.
        const seed = turn.source;
        const waitId = Date.now() + 1;
        setInput('');
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend, { id: waitId - 1 }),
          {
            id: waitId,
            sender: 'bot',
            text: 'Writing a new page from your brief on the computer.',
            isStreaming: true,
          },
        ]);
        const constraints = {
          subject: seed.brand || undefined,
          kind: seed.kind || undefined,
          offer: seed.offer || undefined,
          audience: seed.hero?.lead || undefined,
        };
        const generated = await fetchGeneratedSiteFromBrief(messageToSend, {
          constraints,
          previous: { brand: seed.brand },
          fallbackBrand: seed.brand,
        });
        if (generated) {
          beginLiveSite(generated.source, messageToSend, {
            files: generated.files,
            replaceIds: [waitId - 1, waitId],
          });
        } else {
          beginLiveSite(seed, messageToSend, { replaceIds: [waitId - 1, waitId] });
        }
        return true;
      }
      if (turn.action === 'ask') {
        awaitingSiteSubjectRef.current = true;
        siteBriefRef.current = turn.brief || null;
        setInput('');
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend),
          { id: Date.now() + 1, sender: 'bot', text: turn.chat || SITE_SUBJECT_QUESTION },
        ]);
        return true;
      }
      if (turn.action === 'explain') {
        setInput('');
        setMessages((prev) => {
          const previous = [...prev].reverse().find((item) => item.sender === 'bot' && String(item.text || '').trim());
          return [
            ...prev,
            userChatMessage(messageToSend),
            { id: Date.now() + 1, sender: 'bot', text: explainPrevious(previous && previous.text) },
          ];
        });
        return true;
      }
      if (turn.action !== 'research') return false;
      let research = null;
      try {
        const base = getApiBase();
        const res = await fetch(`${base}/chat/site-research`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: turn.query }),
        });
        const data = res.ok ? await res.json() : null;
        if (data && typeof data === 'object') research = data;
      } catch (_) {
        research = null;
      }
      if (!research?.ok) {
        try {
          const preview = await researchViaPreview(turn.query);
          if (preview && (preview.ok || preview.about || (preview.headings || []).length || (preview.nav || []).length)) {
            research = preview;
          }
        } catch (_) {
          // The first lookup result stays. An empty result builds nothing.
        }
      }
      const researchBrief = [
        messageToSend,
        turn.query ? `Reference name: ${turn.query}` : '',
        research?.about ? `Public page notes: ${String(research.about).slice(0, 1200)}` : '',
        Array.isArray(research?.headings) && research.headings.length
          ? `Headings seen: ${research.headings.slice(0, 8).join(', ')}`
          : '',
      ].filter(Boolean).join('\n');
      const generated = await fetchGeneratedSiteFromBrief(researchBrief, {
        constraints: { referenceQuery: turn.query || undefined },
        fallbackBrand: turn.query || 'Site',
      });
      if (generated) {
        awaitingSiteSubjectRef.current = false;
        siteBriefRef.current = null;
        beginLiveSite(generated.source, messageToSend, { files: generated.files });
        return true;
      }
      const source = siteAfterResearch(messageToSend, turn.query, research);
      if (source) {
        awaitingSiteSubjectRef.current = false;
        siteBriefRef.current = null;
        beginLiveSite(source, messageToSend);
        return true;
      }
      awaitingSiteSubjectRef.current = true;
      setInput('');
      setMessages((prev) => [
        ...prev,
        userChatMessage(messageToSend),
        { id: Date.now() + 1, sender: 'bot', text: SITE_SUBJECT_QUESTION },
      ]);
      return true;
    };

    if (!retryMessageId && isChatFollowUp(messageToSend)) {
      if (await settleSiteTurn(nextSiteTurn(messageToSend, false))) return;
    }

    if (!retryMessageId && awaitingSiteSubjectRef.current && !isDeckRequest(messageToSend) && !isWebsiteRequest(messageToSend) && !isChatFollowUp(messageToSend)) {
      if (await settleSiteTurn(nextSiteTurn(messageToSend, siteBriefRef.current || { asking: 'subject' }))) return;
    }

    if (!retryMessageId && (isWebsiteRequest(messageToSend) || isSiteFollowUp(messageToSend, siteSourceRef.current))) {
      if (isWebsiteRequest(messageToSend)) {
        if (await settleSiteTurn(nextSiteTurn(messageToSend, null))) return;
      }
      // Prefer model revise/patch for LIVE sites; keep local reviseSource as fallback.
      const current = siteSourceRef.current;
      if (current) {
        const waitId = Date.now() + 1;
        setInput('');
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend, { id: waitId - 1 }),
          {
            id: waitId,
            sender: 'bot',
            text: 'Updating the page from your change.',
            isStreaming: true,
          },
        ]);
        const modelRevised = await fetchRevisedSiteFromBrief(current, messageToSend);
        if (modelRevised) {
          beginLiveSite(modelRevised.source, messageToSend, {
            files: modelRevised.files,
            replaceIds: [waitId - 1, waitId],
          });
          return;
        }
        const revised = reviseSource(siteSourceRef.current, messageToSend);
        if (revised) {
          beginLiveSite(revised, messageToSend, { replaceIds: [waitId - 1, waitId] });
          return;
        }
        setMessages((prev) => prev.map((msg) => (
          msg.id === waitId
            ? { ...msg, text: 'I could not apply that change to the page. Try a clearer rename or colour request.', isStreaming: false }
            : msg
        )));
        return;
      }
      const revised = reviseSource(siteSourceRef.current, messageToSend);
      if (revised) {
        beginLiveSite(revised, messageToSend);
        return;
      }
    }

    // App plugins: run connected-app plugins first (e.g. Photoshop â†’ open image editor, Spotify â†’ reply only)
    const plugin = findMatchingPlugin(messageToSend, connectedApps, integrationKeyApps);
    if (plugin && !retryMessageId) {
      const hadAttachment = selectedFiles.length > 0;
      // Open panel for every plan â€” manual tools work on Hobby; Pro gates AI generation inside billing, not here.
      if (plugin.action === 'open_image_editor' && false) {
        setTaskPanelView('default');
        const usePhotoshopPanel = plugin.editorTarget === 'photoshop';
        if (usePhotoshopPanel) {
          setShowPhotoshopEditorPanel(true);
          setPhotoshopEditorPanelMinimized(false);
          setShowImageEditorPanel(false);
        } else {
          setShowImageEditorPanel(true);
          setImageEditorPanelMinimized(false);
          setShowPhotoshopEditorPanel(false);
        }
        setInput("");
        setSelectedFiles([]);
        const botMessage = typeof plugin.getBotMessage === 'function'
          ? plugin.getBotMessage({ hadAttachment })
          : `**${plugin.name}** â€” opening editor.`;
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend),
          { id: Date.now() + 1, sender: "bot", text: botMessage },
        ]);
        return;
      }
      if (plugin.action === 'reply_only') {
        const botMessage = typeof plugin.getBotMessage === 'function'
          ? plugin.getBotMessage({ hadAttachment })
          : `**${plugin.name}** â€” You have this app connected. Ask me anything and I'll use it when relevant.`;
        setInput("");
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend),
          { id: Date.now() + 1, sender: "bot", text: botMessage },
        ]);
        return;
      }
    }

    // Image editor intent (generic, no app plugin): open Image Editor panel above typing box
    const mLower = String(messageToSend).toLowerCase();
    const imageEditIntent = /\b(edit|open|use|launch|start|show)\s+(?:my\s+)?(?:an?\s+)?(image|photo|picture|pic|photo editor|image editor)\b/i.test(mLower)
      || /\b(image|photo|picture)\s+(?:editor|editing)\b/i.test(mLower)
      || /\b(crop|resize|retouch|enhance)\s+(?:my\s+)?(?:an?\s+)?(image|photo|picture)\b/i.test(mLower);
    if (imageEditIntent && !retryMessageId && false) {
      setTaskPanelView('default');
      setShowPhotoshopEditorPanel(false);
      setShowImageEditorPanel(true);
      setImageEditorPanelMinimized(false);
      setInput("");
      setSelectedFiles([]);
      setMessages((prev) => [
        ...prev,
        userChatMessage(messageToSend),
        {
          id: Date.now() + 1,
          sender: "bot",
          text: "**Image Editor** is open above the typing box. Upload an image and use the tools to edit, or ask the AI assistant inside the panel.",
        },
      ]);
      return;
    }

    // Video editor intent: open Video Editor panel above typing box
    const videoEditIntent = /\b(edit|open|use|launch|start|show)\s+(?:my\s+)?(?:a\s+)?(video|footage|clip)\b/i.test(mLower)
      || /\b(video|film)\s+(?:editor|editing)\b/i.test(mLower)
      || /\b(cut|trim|merge)\s+(?:my\s+)?(?:a\s+)?(video|footage)\b/i.test(mLower);
    if (videoEditIntent && !retryMessageId && false) {
      setShowPhotoshopEditorPanel(false);
      setShowImageEditorPanel(false);
      setShowVideoEditorPanel(true);
      setVideoEditorPanelMinimized(false);
      setInput("");
      setSelectedFiles([]);
      setMessages((prev) => [
        ...prev,
        userChatMessage(messageToSend),
        {
          id: Date.now() + 1,
          sender: "bot",
          text: "**Video Editor** is open above the typing box. Import footage and use the timeline, or ask the AI assistant inside the panel.",
        },
      ]);
      return;
    }

    // Browser task: the live computer opens the site and can click, type, and scroll.
    let browserIntent = detectBrowserTaskIntent(messageToSend);
    if (!browserIntent.needsBrowser && lastBrowserUrlRef.current && /^\s*(scroll(?:\s+(?:up|down))?|click\b|type\b|search\s+for\b)/i.test(messageToSend)) {
      browserIntent = { needsBrowser: true, url: lastBrowserUrlRef.current };
    }
    if (browserIntent.needsBrowser && !retryMessageId) {
      try {
        setBrowserOperatorEnabled(true);
        localStorage.setItem('zolaid-browser-operator-enabled', 'true');
      } catch (_) {}
      openSandbox({ kind: 'browser', log: 'Opening the browser', url: browserIntent.url || undefined, show: false });
      setShowAIBrowserAgent(false); // No AI inside browser operator â€” main chat AI only
      const initialUrl = browserIntent.url || '';
      if (initialUrl) setBrowserPreviewUrl(initialUrl);
      setInput("");
      setSelectedFiles([]);
      const runningMsgId = Date.now() + 1;
      pendingBrowserResultMessageIdRef.current = runningMsgId;
      setMessages((prev) => [
        ...prev,
        userChatMessage(messageToSend),
        { id: runningMsgId, sender: "bot", text: browserIntent.needsKdp ? "ðŸ”„ Opening KDP and starting automationâ€¦" : "Opening it on the computer.", activity: browserIntent.needsKdp ? undefined : "live", startedAt: Date.now(), steps: browserIntent.needsKdp ? undefined : ["Opening the page."] },
      ]);

      if (browserIntent.needsKdp) {
        (async () => {
          const apiBase = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
          const kdpBase = apiBase ? `${apiBase}/api/kdp` : '/api/kdp';
          try {
            await fetch(`${kdpBase}/start`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
            await fetch(`${kdpBase}/action`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ action: 'goto', url: 'https://kdp.amazon.com' }),
            });
            setMessages((prev) => prev.map((m) => (m.id === runningMsgId ? { ...m, text: "âœ… KDP session started. Sign in to KDP in the browser panel if needed. You can say: \"fill KDP metadata with title X\", \"upload interior [path]\", or \"KDP screenshot\" (backend needs KDP_AUTOMATION_ENABLED=true)." } : m)));
          } catch (e) {
            setMessages((prev) => prev.map((m) => (m.id === runningMsgId ? { ...m, text: `Opened KDP in the browser panel. Sign in there to continue. (Backend KDP automation: ${e.message || 'not available'})` } : m)));
          }
          pendingBrowserResultMessageIdRef.current = null;
        })();
        return;
      }

      (async () => {
        const apiBase = (() => { try { const { getApiBase } = require("./backendProbe.js"); return getApiBase(); } catch (_) { return (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, ''); } })();
        const operatorBase = apiBase ? `${apiBase}/api/browser-operator` : '/api/browser-operator';
        const startUrl = initialUrl && initialUrl.startsWith('http') ? initialUrl : (lastBrowserUrlRef.current || 'https://www.google.com');
        setBrowserPreviewUrl(startUrl);

        try {
          const localRes = await fetch('/api/live-browser/run', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userGoal: messageToSend, initialUrl: startUrl }),
          });
          const localData = await localRes.json().catch(() => ({}));
          if (localRes.ok && localData.ok && localData.summary) {
            const finalUrl = localData.finalUrl || startUrl;
            if (finalUrl) {
              setBrowserPreviewUrl(finalUrl);
              lastBrowserUrlRef.current = finalUrl;
            }
            setBrowserOperatorLastRun({ steps: localData.steps || [], summary: localData.summary, finalUrl });
            const finishedAt = Date.now();
            setMessages((prev) => [
              ...prev.map((m) => (m.id === runningMsgId ? settleWork(m, finishedAt) : m)),
              { id: finishedAt + 2, sender: 'bot', text: localData.summary },
            ]);
            pendingBrowserResultMessageIdRef.current = null;
            return;
          }
        } catch (_) {}

        try {
          const res = await fetch(`${operatorBase}/run`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userGoal: messageToSend,
              initialUrl: startUrl,
              maxSteps: 12,
            }),
          });
          const data = await res.json().catch(() => ({}));

          if (res.ok && data.ok && data.summary != null) {
            if (data.finalUrl) setBrowserPreviewUrl(data.finalUrl);
            setBrowserOperatorLastRun({ steps: data.steps || [], summary: data.summary || '', finalUrl: data.finalUrl || '' });
            setMessages((prev) => prev.map((m) => (m.id === runningMsgId ? { ...m, text: `âœ… ${data.summary}` } : m)));
            pendingBrowserResultMessageIdRef.current = null;
            return;
          }

          if (res.status === 403 || res.status === 503) {
            setMessages((prev) => prev.map((m) => (m.id === runningMsgId ? { ...m, text: `âš ï¸ Browser operator is disabled: ${data.error || res.statusText}. For AI that opens sites and acts from screenshots (like a human), set BROWSER_OPERATOR_ENABLED=true and OPENAI_API_KEY on the backend. Falling back to one-shot navigateâ€¦` } : m)));
          }
        } catch (_) {}

        const chatEndpoint = apiBase ? `${apiBase}/chat` : '/chat';
        const currentUrl = initialUrl || 'about:blank';
        const browserPrompt =
          `[BROWSER_CONTROL] You are controlling a browser. Current page URL: ${currentUrl}. User goal: ${messageToSend}. ` +
          `Reply with ONLY a JSON object, no markdown: {"action":"navigate"|"done","value":"..."}. ` +
          `For "navigate", value is the full URL to open. For "done", value is a short summary for the user. One response only.`;
        try {
          const res = await fetch(chatEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({
              userId: resolveChatApiUserId(user, token),
              message: browserPrompt,
              model: selectedModel,
            }),
          });
          const data = await res.json().catch(() => ({}));
          const rawReply = (data.reply || data.contract?.content || '').trim();
          let summary = rawReply;
          let nextUrl = null;
          const jsonMatch = rawReply.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            try {
              const obj = JSON.parse(jsonMatch[0]);
              const act = (obj.action || '').toLowerCase();
              const val = obj.value || '';
              if (act === 'navigate' && val) {
                nextUrl = val.startsWith('http') ? val : `https://${val}`;
                summary = `Opened ${nextUrl}.`;
              } else if (act === 'done' && val) {
                summary = val;
              }
            } catch (_) {}
          }
          if (nextUrl) setBrowserPreviewUrl(nextUrl);
          setMessages((prev) => prev.map((m) => (m.id === runningMsgId ? { ...m, text: summary } : m)));
        } catch (err) {
          setMessages((prev) => prev.map((m) => (m.id === runningMsgId ? { ...m, text: `Browser error: ${err.message || 'Request failed'}.` } : m)));
        }
        pendingBrowserResultMessageIdRef.current = null;
      })();
      return;
    }

    // Autonomous Agent Detection: detect commands like "autonomous:", "/autonomous", "agent:", "/agent"
    const wantsAutonomous =
      autonomousAgentEnabled &&
      (mLower.startsWith("autonomous:") ||
       mLower.startsWith("/autonomous ") ||
       mLower.startsWith("agent:") ||
       mLower.startsWith("/agent "));

    if (wantsAutonomous && !retryMessageId) {
      // Extract the actual task from the command
      const taskText = messageToSend
        .replace(/^(autonomous:|\/autonomous|agent:|\/agent)\s*/i, '')
        .trim();

      if (taskText) {
        setAutonomousAgentTask(taskText);
        setShowAutonomousAgent(true);
        setAutonomousAgentActive(true);
        setInput("");
        setSelectedFiles([]);
        setMessages((prev) => [
          ...prev,
          userChatMessage(messageToSend),
          {
            id: Date.now() + 1,
            sender: "bot",
            text: `ðŸ¤– Activating Autonomous Agent for task:\n\n"${taskText}"\n\nThe agent is working behind the scenes. You'll get a message here when it finishes.`
          },
        ]);
        return;
      }
    }

    // Check plan limits for agent requests
    const canRequest = canPerformAction(userPlan, "agent_request", {
      agentRequests: agentRequestCount,
    });

    if (!canRequest) {
      const limits = getPlanLimits(userPlan);
      const upgradeMsg = getUpgradeMessage("agent_request");
      const botMessage = {
        id: Date.now(),
        sender: "bot",
        text: `âš ï¸ You've reached your daily limit of ${limits.maxAgentRequests} agent requests. ${upgradeMsg}`,
      };
      setMessages((prev) => [...prev, botMessage]);
      return;
    }

    // Remove retry message if retrying
    if (retryMessageId) {
      setMessages((prev) => prev.filter(msg => msg.id !== retryMessageId));
    }

    if (!retryMessageId && (!chatHttpDoneRef.current || !typewriterDoneRef.current)) {
      chatQueueRef.current.push({
        text: messageToSend,
        files: selectedFiles.slice(),
        retryMessageId: null,
      });
      setInput('');
      setSelectedFiles([]);
      return;
    }

    chatHttpDoneRef.current = false;
    typewriterDoneRef.current = false;

    // Attach selected files to message if any
    const filesToAttach = queuedFiles !== undefined
      ? (queuedFiles && queuedFiles.length ? queuedFiles : null)
      : (selectedFiles.length > 0 ? selectedFiles : null);
    
    const sentAt = Date.now();
    const userMessage = { 
      id: sentAt,
      sentAt,
      sender: "user", 
      text: messageToSend,
      files: filesToAttach ? filesToAttach.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        url: URL.createObjectURL(file) // Create preview URL
      })) : null
    };
    setMessages((prev) => [...prev, userMessage]);
    // Clear input and files after message is added
    setInput("");
    setSelectedFiles([]);
    setAgentRequestCount((prev) => prev + 1);
    
    // One id for this thread so the next message stays on the same conversation.
    let chatId = currentChatIdRef.current || currentChatId;
    if (!chatId) {
      chatId = Date.now();
      currentChatIdRef.current = chatId;
      setCurrentChatId(chatId);
    }

    // Create placeholder bot message for streaming
    const botMessageId = Date.now() + 1; // Ensure unique ID
    const botMessage = {
      id: botMessageId,
      sentAt: botMessageId,
      sender: "bot",
      text: "",
      isStreaming: false, // Don't show streaming until we have text
      error: null,
      startedAt: Date.now(),
    };
    setMessages((prev) => [...prev, botMessage]);
    setIsStreaming(true);
    setStreamingMessageId(botMessageId);
    streamingMessageIdRef.current = botMessageId;
    pendingBotMessageIdRef.current = botMessageId;

    const controller = new AbortController();
    chatAbortControllerRef.current = controller;

    try {
      // In dev (localhost), use relative /chat so CRA proxy forwards to backend; fallback to 8081 when proxy fails
      const onLocalhost = typeof window !== 'undefined' && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location.origin);
      const apiBase = getApiBase();
      const chatEndpoint = apiBase ? `${apiBase}/chat` : '/chat';

      const history = priorTurnsForApi(messagesRef.current, messageToSend, retryMessageId);
      const pageNote = siteContextNote(siteSourceRef.current);
      if (pageNote) history.push({ role: 'assistant', content: pageNote });
      const mergedConnected = connectedAppsWithIntegrationKeys(connectedApps, integrationKeyApps);
      const mcpEnabled = mcpAppsFromConnected(mergedConnected);
      const payload = {
        userId: resolveChatApiUserId(user, token),
        message: messageToSend,
        conversationId: String(chatId),
        history,
        model: selectedModel,
        connectedApps: mergedConnected,
        /** Enables backend MCP tool packs for these app ids (gmail, google-calendar, …). */
        mcpToolPacks: mcpEnabled,
        gmailState: typeof getGmailState === "function" ? getGmailState() : (gmailState || ""),
        photoshopImageEdit: photoshopImageEditServer,
        chatMode: socraticMode ? "ideaGeneration" : "normal",
      };
      const fetchOpts = {
        method: "POST",
        mode: "cors",
        credentials: credentialsModeForApiUrl(chatEndpoint),
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      };

      let res = await fetch(chatEndpoint, fetchOpts).catch((fetchError) => {
        console.error('Fetch error details:', { error: fetchError, endpoint: chatEndpoint });
        throw new Error(`Network error: Unable to reach the backend server. Endpoint: ${chatEndpoint}. Error: ${fetchError.message}`);
      });

      // 503: app { code: "starting" }, or Cloud Run / LB cold start (non-JSON body, often no CORS) â€” retry with backoff
      for (let attempt = 0; attempt < 5 && res.status === 503; attempt++) {
        let peek = null;
        try {
          peek = await res.clone().json();
        } catch (_) {
          peek = null;
        }
        const shouldRetry = peek?.code === "starting" || peek === null;
        if (!shouldRetry) break;
        const waitMs =
          peek?.code === "starting"
            ? Math.min(10_000, (Number(peek.retryAfterSec) || 2) * 1000)
            : Math.min(8_000, 1500 * (attempt + 1));
        await new Promise((r) => setTimeout(r, waitMs));
        res = await fetch(chatEndpoint, fetchOpts).catch((fetchError) => {
          console.error('Fetch error details (503 retry):', { error: fetchError, endpoint: chatEndpoint });
          throw new Error(`Network error: Unable to reach the backend server. Endpoint: ${chatEndpoint}. Error: ${fetchError.message}`);
        });
      }

      // If proxy returned 500 (backend down / ECONNREFUSED), retry direct to localhost (default 8080, then 8081 if port busy)
      let consumed500Body = null;
      if (!res.ok && res.status === 500 && onLocalhost && chatEndpoint === "/chat") {
        consumed500Body = await res.text().catch(() => "");
        if (/econnrefused|proxy error|could not proxy/i.test(consumed500Body)) {
          let lastNetworkError = null;
          for (const port of [8080, 8081]) {
            try {
              res = await fetch(`http://localhost:${port}/chat`, {
                ...fetchOpts,
                credentials: credentialsModeForApiUrl(`http://localhost:${port}/chat`),
              });
              lastNetworkError = null;
              break;
            } catch (fetchError) {
              lastNetworkError = fetchError;
              console.error(`Fallback localhost:${port}/chat failed:`, fetchError);
            }
          }
          if (lastNetworkError) {
            throw new Error(
              "Cannot reach the backend. Tried the dev proxy and direct http://localhost:8080/chat then :8081. " +
                "Start the API: `cd backend && npm run dev` (or `npm start` in backend â€” listens on 8080 by default). " +
                "Or run `npm run dev` from the repo root to start frontend + backend together."
            );
          }
        }
      }

      // HTML body: wrong host (SPA index.html), Cloud Run / LB error page (502/503), or redirect to HTML.
      const contentType = res.headers.get("content-type");
      if (contentType && contentType.includes("text/html")) {
        const status = res.status;
        const looksLikeBackendUrl = /zolaid-backend|\.run\.app/i.test(chatEndpoint);
        const healthUrl = (() => {
          try {
            if (/^https?:\/\//i.test(chatEndpoint)) {
              const u = new URL(chatEndpoint);
              u.pathname = "/health";
              u.search = "";
              u.hash = "";
              return u.toString();
            }
          } catch (_) {}
          return "/health";
        })();
        const whenBackendUrl =
          `The API host returned an HTML page (HTTP ${status}), not JSON â€” usually a Cloud Run / load balancer error (502/503/504), a timeout, or the container not ready. ` +
          `Open ${healthUrl} in a browser (expect JSON with "status":"ok") and check Cloud Run logs for the backend service.`;
        const whenStatic =
          `The request may have hit your static site (index.html) instead of the API. Rebuild with REACT_APP_API_URL set to your zolaid-backend URL ` +
          `or set window.__ZOLAID_API_BASE__ in public/runtime-config.js, then redeploy.`;
        throw new Error(
          `Received HTML instead of JSON. ${looksLikeBackendUrl ? whenBackendUrl : whenStatic} Endpoint used: ${chatEndpoint}.`
        );
      }
      
      if (!res.ok) {
        // Try to parse structured JSON error first (preferred); use already-read body if we consumed it for proxy check
        let errJson = null;
        let errText = consumed500Body != null ? consumed500Body : "";
        try {
          if (consumed500Body == null) errText = await res.text();
          try {
            errJson = errText ? JSON.parse(errText) : null;
          } catch (_) {}
        } catch (_) {}

        const code = errJson?.code || null;
        const hint = errJson?.hint || null;
        const msg = errJson?.error || errText || "Unknown error";
        if (code === "missing_openai_key" && hint) {
          throw new Error(msg + "\n\n" + hint);
        }
        if (code === "empty_response" && hint) {
          throw new Error(msg + "\n\n" + hint);
        }
        const detailsText =
          typeof errJson?.details === 'string'
            ? errJson.details
            : errJson?.details
              ? JSON.stringify(errJson.details)
              : '';
        const combined = `${msg}\n${detailsText}\n${errText}`.toLowerCase();

        console.error('API error response:', res.status, errJson || errText);

        // Special-case: OpenAI billing/quota problems
        // - New backend returns 429 + code=insufficient_quota
        // - Older backend sometimes wraps upstream 429 into a 502 with error text "OpenAI request failed (429)."
        const looksLikeQuota =
          (res.status === 429 && String(code || '').toLowerCase().includes('insufficient_quota')) ||
          (res.status === 502 && combined.includes('openai request failed (429)') && combined.includes('insufficient_quota')) ||
          combined.includes('you exceeded your current quota') ||
          combined.includes('insufficient_quota');

        if (looksLikeQuota) {
          throw new Error(
            "OpenAI quota exceeded. Add billing/credits in your OpenAI project or use a new API key, then restart the backend.\n" +
              (hint ? `\nHint: ${hint}\n` : "") +
              "\nOpenAI billing page: `https://platform.openai.com/account/billing`"
          );
        }

        // Special-case: rate limit (not billing), so users don't confuse it with quota.
        if (res.status === 429 && !looksLikeQuota) {
          throw new Error(
            "OpenAI rate limit hit (429). Wait ~10â€“60 seconds and try again, or lower request frequency.\n" +
              (hint ? `\nHint: ${hint}\n` : "")
          );
        }

        // Proxy/backend unreachable (e.g. ECONNREFUSED when backend on 8080 is not running)
        const isProxyOrUnreachable =
          res.status === 500 &&
          (combined.includes('econnrefused') || combined.includes('proxy error') || combined.includes('could not proxy'));
        if (isProxyOrUnreachable) {
          throw new Error(
            "Cannot reach the backend. 1) Start the backend: cd backend && npm run dev (runs on port 8080). " +
            "2) If you use REACT_APP_API_URL in frontend/.env, restart the frontend. " +
            "3) Click **Test connection** below to verify the backend (or open /chat/diag in a new tab)."
          );
        }

        throw new Error(`Backend error (${res.status}): ${msg}` + (hint ? `\n\n${hint}` : ""));
      }
      
      const data = await res.json();
      
      // Check if response has an error
      if (!res.ok || data.error) {
        const errorMessage = data.error || `HTTP error! status: ${res.status}`;
        throw new Error(errorMessage);
      }
      
      // Check if we got a reply
      if (!data.reply) {
        throw new Error("No reply received from the server");
      }
      // Surface the common failure mode clearly (backend replied but no usable content)
      if (String(data.reply || "").trim().toLowerCase() === "no response") {
        throw new Error("Backend returned 'No response'. This usually means OPENAI_API_KEY is missing/invalid on the server.");
      }

      const contract = data.contract && typeof data.contract === 'object' ? data.contract : null;
      const rendered = computerFromModel(contract) || computerFromModel(data.reply);
      if (rendered) {
        const byPath = Object.fromEntries((rendered.files || []).map((f) => [f.path, f.content]));
        const source = renderedSourceFromArtifacts({
          brand: 'Site',
          brief: messageToSend,
          html: byPath['index.html'] || '',
          css: byPath['styles.css'] || '',
          js: byPath['app.js'] || '',
        });
        beginLiveSite(
          source,
          messageToSend,
          { files: rendered.files, replaceIds: [userMessage.id, botMessageId] },
        );
        finishReplyNow(botMessageId);
        return;
      }

      const formatContractForDisplay = (c) => {
        if (!c) return data.reply;
        const mode = c.mode ? String(c.mode) : 'explain';
        const intent = c.intent ? String(c.intent) : 'chat';
        const content = c.content ? String(c.content) : '';
        const artifacts = c.artifacts && typeof c.artifacts === 'object' ? c.artifacts : {};
        const parts = [];
        parts.push(`**Mode:** ${mode}`);
        parts.push(`**Intent:** ${intent}`);
        if (content) parts.push(`\n${content}`);

        if (artifacts.plan) parts.push(`\n### Plan\n${artifacts.plan}`);
        if (artifacts.simulation) parts.push(`\n### Simulation\n${artifacts.simulation}`);
        if (artifacts.decisions) parts.push(`\n### Decisions\n${artifacts.decisions}`);
        if (artifacts.outcomes) parts.push(`\n### Outcomes\n${artifacts.outcomes}`);
        if (Array.isArray(artifacts.steps) && artifacts.steps.length) parts.push(`\n### Steps\n${artifacts.steps.slice(0, 12).map((s, i) => `${i + 1}. ${String(s || '').trim()}`).join('\n')}`);
        if (Array.isArray(artifacts.commands) && artifacts.commands.length) parts.push(`\n### Commands (run in terminal)\n${artifacts.commands.slice(0, 10).map((s) => `- ${String(s || '').trim()}`).join('\n')}`);
        if (Array.isArray(artifacts.successCriteria) && artifacts.successCriteria.length) parts.push(`\n### Success criteria\n${artifacts.successCriteria.slice(0, 8).map((s) => `- ${String(s || '').trim()}`).join('\n')}`);

        return parts.join('\n');
      };
      
      // Track AI actions for Zolaid
      const addAction = (type, description, details = {}) => {
        setZolaidActions(prev => [...prev, {
          type,
          description,
          details,
          timestamp: new Date().toLocaleTimeString(),
          ...details
        }]);
      };

      // Update message with full response only if this request is still the one we're waiting for (avoid race with newer message)
      if (!data.reply) {
        // No reply, mark as not streaming
        setMessages((prev) => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: "No response received.", isStreaming: false }
            : msg
        ));
        finishReplyNow(botMessageId);
        setCurrentTask(null);
      } else if (pendingBotMessageIdRef.current != null && pendingBotMessageIdRef.current !== botMessageId) {
        finishReplyNow(botMessageId);
        return;
      } else {
        // ------------------------------
        // Level 2+ Output Contract support
        // ------------------------------
        if (contract) {
          const artifacts = contract.artifacts && typeof contract.artifacts === 'object' ? contract.artifacts : {};

          // If render artifacts exist, reflect them into ZolaidComputer preview actions
          if (artifacts && Object.keys(artifacts).length > 0) {
            setZolaidArtifacts(artifacts);
          }

          if (contract.mode === 'render' && (artifacts.html || artifacts.css || artifacts.js)) {
            const source = renderedSourceFromArtifacts({
              brand: 'Site',
              brief: messageToSend,
              html: String(artifacts.html || ''),
              css: String(artifacts.css || ''),
              js: String(artifacts.js || ''),
            });
            beginLiveSite(
              source,
              messageToSend,
              {
                files: filesFromSource(source),
                replaceIds: [userMessage.id, botMessageId],
              },
            );
            finishReplyNow(botMessageId);
            return;
          }

          // If execute actions exist, require human approval before running
          if (contract.mode === 'execute' && Array.isArray(contract.actions) && contract.actions.length > 0) {
            const proposedActions = contract.actions
              .filter(a => a && typeof a === 'object' && typeof a.type === 'string')
              .map((a) => ({
                ...a,
                delay: typeof a.delay === 'number' && a.delay >= 0 ? a.delay : 0,
              }));

            setCurrentTask(contract.intent || 'execute');

            setMessages((prev) => prev.map(msg =>
              msg.id === botMessageId
                ? {
                    ...msg,
                    text: `${formatContractForDisplay(contract)}\n\n---\n\n**Proposed actions:** ${proposedActions.length}\n\nClick **Approve** to run them in Zolaid Computer.`,
                    isStreaming: true,
                    contract,
                    pendingActions: proposedActions
                  }
                : msg
            ));

            markHttpDone();
            return;
          }

          // Plan/simulate/explain: just display artifacts clearly
          if (contract.mode === 'plan') {
            setCurrentTask(contract.intent || null);
            setMessages((prev) => prev.map(msg =>
              msg.id === botMessageId
                ? { ...msg, text: `${formatContractForDisplay(contract)}\n\n---\n\nClick **Execute plan** to proceed (approval step).`, isStreaming: true, contract, pendingPlan: true }
                : msg
            ));
            markHttpDone();
            return;
          }

          if (contract.mode === 'simulate' || contract.mode === 'explain' || contract.mode === 'think') {
            setCurrentTask(contract.intent || null);
            // Simple chat: show only the message text; otherwise show full contract (mode/intent/content)
            const displayText = chatVisibleText(
              contract.mode === 'explain' && (contract.intent === 'chat' || !contract.intent)
                ? (contract.content || data.reply || '').trim() || formatContractForDisplay(contract)
                : formatContractForDisplay(contract)
            );
            setMessages((prev) => prev.map(msg =>
              msg.id === botMessageId
                ? { ...msg, text: displayText, isStreaming: true, contract }
                : msg
            ));
            markHttpDone();
            return;
          }
        }

        // Detect code in response and create structured actions
        const codeMatches = data.reply.match(/```[\s\S]*?```/g);
        if (codeMatches) {
          codeMatches.forEach(codeBlock => {
            const code = codeBlock.replace(/```[\w]*\n?/g, '').replace(/```/g, '').trim();
            
            // Check if it's HTML/CSS/JS
            const isHTML = code.includes('<html') || code.includes('<!DOCTYPE') || code.includes('<body') || code.includes('<div');
            
            if (isHTML) {
              // Extract HTML, CSS, JS
              const htmlMatch = code.match(/<body[^>]*>([\s\S]*?)<\/body>/i) || code.match(/([\s\S]*)/);
              const cssMatch = code.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
              const jsMatch = code.match(/<script[^>]*>([\s\S]*?)<\/script>/gi);
              
              const html = htmlMatch ? htmlMatch[1] || htmlMatch[0] : code;
              const css = cssMatch ? cssMatch.map(s => s.replace(/<\/?style[^>]*>/gi, '')).join('\n') : '';
              const js = jsMatch ? jsMatch.map(s => s.replace(/<\/?script[^>]*>/gi, '')).join('\n') : '';
              
              addAction('updatePreview', 'Update preview with generated code', {
                html,
                css,
                js,
                delay: 0,
              });
              setZolaidComputerInitialViewMode('preview');
            } else {
              addAction('code', 'Generated code', { code, delay: 0 });
            }
          });
        }

        // Detect URLs
        const urlMatches = data.reply.match(/https?:\/\/[^\s\)]+/g);
        if (urlMatches) {
          urlMatches.forEach(url => {
            addAction('moveCursor', 'Move cursor to URL', { x: 200, y: 100, delay: 0 });
            addAction('click', 'Click URL', { selector: 'a', position: { x: 200, y: 100 }, delay: 0 });
            addAction('navigate', `Opened URL: ${url.substring(0, 50)}...`, { url, delay: 0 });
          });
        }

        // Detect file operations
        if (data.reply.toLowerCase().includes('created') || data.reply.toLowerCase().includes('saved')) {
          addAction('createFile', 'File operation detected', {
            path: 'output.html',
            content: codeMatches?.[0] || '',
            operation: 'create/save',
            delay: 0,
          });
        }

        // Add general execution action
        const taskDescription = codeMatches ? 'Generating code' : urlMatches ? 'Navigating to URL' : 'Processing response';
        setCurrentTask(taskDescription);
        addAction('execute', taskDescription, {
          messageLength: data.reply.length,
          hasCode: !!codeMatches,
          hasUrls: !!urlMatches,
          delay: 0,
        });

        const visibleReply = chatVisibleText(data.reply);
        setMessages((prev) => prev.map(msg =>
          msg.id === botMessageId
            ? { ...msg, text: visibleReply, isStreaming: visibleReply === String(data.reply || '').trim() }
            : msg
        ));
        markHttpDone();
      }
    } catch (err) {
      // Stop aborts the in-flight fetch. The button handler already settled the row.
      if (err.name === 'AbortError' || stoppedReplyIdsRef.current.has(botMessageId)) {
        const alreadySettled = stoppedReplyIdsRef.current.has(botMessageId);
        stoppedReplyIdsRef.current.delete(botMessageId);
        if (!alreadySettled) {
          setMessages((prev) => prev.map((item) => (
            item.id === botMessageId ? settleStoppedItem(item, Date.now()) : item
          )));
          finishReplyNow(botMessageId);
        }
        return;
      }
      console.error("Chat error:", err);

      const onLocalhostUi =
        typeof window !== "undefined" &&
        /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(String(window.location?.origin || ""));
      const apiUrl = onLocalhostUi
        ? (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '')
        : getConfiguredApiBase();
      const chatEndpoint = apiUrl ? `${apiUrl}/chat` : '/chat';
      const originNow = typeof window !== "undefined" ? window.location.origin : "";
      const apiLooksPlaceholder =
        !apiUrl || isPlaceholderApiUrl((process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, ''));
      const endpointHint = "";
      const stepsHintLocal =
        "\n\n**Fix (local dev):** Start the backend in a **second terminal**:\n```\ncd backend && npm start\n```\n(Or from project root: `npm run dev` to run both.) Set `REACT_APP_API_URL=http://localhost:8080` in `frontend/.env` if you are not using the CRA proxy. Then click **Retry**.";
      const stepsHintProd = apiLooksPlaceholder
        ? `\n\n**Fix (hosted app):** The baked **REACT_APP_API_URL** is empty or still a placeholder.\n` +
          `1) Set **REACT_APP_API_URL** in \`frontend/.env.production\` or Cloud Build substitution **_REACT_APP_API_URL** to your real backend, then rebuild.\n` +
          `2) Or set **window.__ZOLAID_API_BASE__** in \`public/runtime-config.js\` (or \`<meta name="zolaid-api-base" content="â€¦">\` in index.html) and redeploy static files.\n` +
          `3) On the backend, set **CORS_ORIGINS** to include **${originNow}** (and \`https://zolaid.com\` if needed).`
        : `\n\n**Fix (hosted app):** Chat calls **${chatEndpoint}**.\n` +
          `1) Open **${apiUrl}/health** â€” expect JSON \`{"status":"ok",...}\`. If it fails, the backend is down or **REACT_APP_API_URL** is wrong.\n` +
          `2) If you changed API hosts, rebuild the frontend so **REACT_APP_API_URL** matches.\n` +
          `3) Backend must allow this siteâ€™s origin in CORS â€” include **${originNow}** (e.g. **CORS_ORIGINS** on Cloud Run). \`https://zolaid.com\` is already allowed in code if you deploy the latest backend.`;

      const stepsHint = onLocalhostUi ? stepsHintLocal : stepsHintProd;

      let errorMessage = err.message || "Network error";
      if (err.message && err.message.includes("Unexpected token")) {
        errorMessage =
          "Backend returned HTML instead of JSON. The API URL may be wrong (empty REACT_APP_API_URL on the frontend build?), or the request hit the static host instead of the API." +
          endpointHint +
          stepsHint;
      } else if (
        (err.message && err.message.includes("Failed to fetch")) ||
        (err.message && err.message.includes("Load failed"))
      ) {
        errorMessage = "Unable to connect to the backend." + endpointHint + stepsHint;
      } else if (err.message && err.message.includes("Not allowed by CORS")) {
        errorMessage = "Backend rejected the request (CORS). Allow your frontend origin in the backend CORS config (e.g. add " + window.location.origin + " to allowedOrigins)." + endpointHint;
      } else if (err.message && err.message.includes("Network error")) {
        errorMessage = err.message + endpointHint + stepsHint;
      } else {
        errorMessage = errorMessage + endpointHint;
      }
      
      // Update message with error (only if this response is still the one we're waiting for)
      if (pendingBotMessageIdRef.current === botMessageId) {
        setMessages((prev) => prev.map(msg => 
          msg.id === botMessageId 
            ? { 
                ...msg, 
                text: "âŒ **Failed to get a response from AI.**\n\n**Error:** " + errorMessage + "\n\nðŸ”„ **Retry** after starting the backend or fixing the connection.",
                isStreaming: false,
                error: true,
              }
            : msg
        ));
        finishReplyNow(botMessageId);
      } else {
        finishReplyNow(botMessageId);
      }
    }
  };

  // Retry/Regenerate message
  const retryMessage = (messageId) => {
    const failedMessage = messages.find(msg => msg.id === messageId);
    if (failedMessage && failedMessage.sender === "bot") {
      // Find the user message before this bot message
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      if (messageIndex > 0) {
        const userMessage = messages[messageIndex - 1];
        if (userMessage.sender === "user") {
          sendMessage(userMessage.text, messageId);
        }
      }
    }
  };

  // Load files from Google Drive
  const loadGoogleDriveFiles = async () => {
    try {
      const googleClientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || "").trim();
      const googleApiKey = (process.env.REACT_APP_GOOGLE_API_KEY || "").trim();
      if (!googleClientId || !googleApiKey) {
        const missing = [];
        if (!googleClientId) missing.push("REACT_APP_GOOGLE_CLIENT_ID");
        if (!googleApiKey) missing.push("REACT_APP_GOOGLE_API_KEY");
        alert(
          "Google Drive Picker is not configured.\n\n" +
          "Add to frontend/.env:\n  " + missing.join("\n  ") + "=your-value\n\n" +
          "Get the API key: Google Cloud Console â†’ APIs & Services â†’ Credentials â†’ Create credentials â†’ API key. " +
          "Then enable \"Google Picker API\" for your project (APIs & Services â†’ Library). Restart the dev server after changing .env."
        );
        return;
      }

      const loadScriptOnce = (id, src) =>
        new Promise((resolve, reject) => {
          const existing = document.getElementById(id);
          if (existing) return resolve(true);
          const s = document.createElement("script");
          s.id = id;
          s.src = src;
          s.async = true;
          s.onload = () => resolve(true);
          s.onerror = () => reject(new Error(`Failed to load ${src}`));
          document.head.appendChild(s);
        });

      // Load Google APIs (Picker) + Google Identity Services (OAuth token)
      await loadScriptOnce("google-api-js", "https://apis.google.com/js/api.js");
      await loadScriptOnce("google-gsi-js", "https://accounts.google.com/gsi/client");

      const requestAccessToken = () =>
        new Promise((resolve, reject) => {
          const gis = window.google?.accounts?.oauth2;
          if (!gis) return reject(new Error("Google Identity Services not available."));
          const tokenClient = gis.initTokenClient({
            client_id: googleClientId,
            scope: "https://www.googleapis.com/auth/drive.readonly",
            callback: (resp) => {
              if (resp?.access_token) resolve(resp.access_token);
              else reject(new Error(resp?.error || "Failed to get access token"));
            },
            error_callback: (err) => {
              reject(new Error(err?.message || err?.type || "Google Drive sign-in was cancelled or blocked"));
            },
          });
          // Prefer silent reuse; GIS still shows the account/consent UI when needed.
          tokenClient.requestAccessToken({ prompt: "" });
        });

      const accessToken = await requestAccessToken();

      const mimeToExt = (mime) => {
        const m = String(mime || "").toLowerCase();
        if (m.includes("pdf")) return ".pdf";
        if (m.includes("png")) return ".png";
        if (m.includes("jpeg") || m.includes("jpg")) return ".jpg";
        if (m.includes("webp")) return ".webp";
        if (m.includes("gif")) return ".gif";
        if (m.includes("csv")) return ".csv";
        if (m.includes("plain")) return ".txt";
        if (m.includes("mp4")) return ".mp4";
        if (m.includes("webm")) return ".webm";
        return "";
      };

      const exportSpecForGoogleMime = (mime) => {
        const m = String(mime || "");
        if (m === "application/vnd.google-apps.document") return { mime: "application/pdf", ext: ".pdf" };
        if (m === "application/vnd.google-apps.spreadsheet") return { mime: "text/csv", ext: ".csv" };
        if (m === "application/vnd.google-apps.presentation") return { mime: "application/pdf", ext: ".pdf" };
        if (m === "application/vnd.google-apps.drawing") return { mime: "image/png", ext: ".png" };
        return { mime: "application/pdf", ext: ".pdf" };
      };

      const downloadDriveFile = async ({ id, name, mimeType }) => {
        const isGoogleDoc = String(mimeType || "").startsWith("application/vnd.google-apps");
        let url = "";
        let outName = String(name || "drive-file");
        let outMime = String(mimeType || "application/octet-stream");
        if (isGoogleDoc) {
          const exp = exportSpecForGoogleMime(mimeType);
          outMime = exp.mime;
          if (!outName.toLowerCase().endsWith(exp.ext)) outName += exp.ext;
          url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}/export?mimeType=${encodeURIComponent(exp.mime)}`;
        } else {
          const ext = mimeToExt(mimeType);
          if (ext && !outName.toLowerCase().endsWith(ext)) outName += ext;
          url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?alt=media`;
        }

        const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
        if (!res.ok) throw new Error(`Failed to download file (${res.status})`);
        const blob = await res.blob();
        return new File([blob], outName, { type: blob.type || outMime });
      };

      await new Promise((resolve, reject) => {
        if (!window.gapi) return reject(new Error("Google API (gapi) not available."));
        window.gapi.load("picker", { callback: resolve, onerror: reject });
      });

      const showPicker = () =>
        new Promise((resolve) => {
          const picker = new window.google.picker.PickerBuilder()
            .setDeveloperKey(googleApiKey)
            .setOAuthToken(accessToken)
            .addView(window.google.picker.ViewId.DOCS)
            .addView(window.google.picker.ViewId.PDFS)
            .addView(window.google.picker.ViewId.IMAGES)
            .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
            .setCallback((data) => resolve(data))
            .build();
          picker.setVisible(true);
        });

      const data = await showPicker();
      if (data?.[window.google.picker.Response.ACTION] !== window.google.picker.Action.PICKED) return;

      const docs = data?.[window.google.picker.Response.DOCUMENTS] || [];
      if (!docs.length) return;

      // Download selected docs into real File objects and attach
      const picked = [];
      for (const doc of docs) {
        try {
          const f = await downloadDriveFile({ id: doc.id, name: doc.name, mimeType: doc.mimeType });
          picked.push(f);
        } catch (e) {
          console.error("Drive download failed:", e);
        }
      }

      if (picked.length) {
        setSelectedFiles((prev) => [...prev, ...picked]);
        setMessages((prev) => [...prev, { id: Date.now(), sender: "bot", text: `âœ… Added ${picked.length} file(s) from Google Drive.` }]);
      } else {
        alert("No files could be downloaded from Google Drive. (Some Google file types may require export permissions.)");
      }
    } catch (error) {
      console.error('Error loading Google Drive files:', error);
      alert(`Unable to access Google Drive Picker.\n\n${error?.message || String(error)}`);
    }
  };

  // Load files from OneDrive
  const loadOneDriveFiles = async () => {
    try {
      // Prefer dedicated OneDrive id; fall back to the shared Microsoft Entra app client id.
      const clientId = (
        process.env.REACT_APP_ONEDRIVE_CLIENT_ID ||
        process.env.REACT_APP_MICROSOFT_CLIENT_ID ||
        ""
      ).trim();
      if (!clientId) {
        alert(
          "OneDrive Picker is not configured.\n\n" +
          "Add REACT_APP_ONEDRIVE_CLIENT_ID (or REACT_APP_MICROSOFT_CLIENT_ID) to frontend/.env and restart the dev server.\n" +
          "In Azure Portal, register redirect URI: " + window.location.origin
        );
        return;
      }

      const loadScriptOnce = (id, src) =>
        new Promise((resolve, reject) => {
          const existing = document.getElementById(id);
          if (existing) return resolve(true);
          const s = document.createElement("script");
          s.id = id;
          s.src = src;
          s.async = true;
          s.onload = () => resolve(true);
          s.onerror = () => reject(new Error(`Failed to load ${src}`));
          document.head.appendChild(s);
        });

      // Official OneDrive picker script (opens a picker dialog, not the full OneDrive web UI)
      await loadScriptOnce("onedrive-js", "https://js.live.net/v7.2/OneDrive.js");
      if (!window.OneDrive?.open) throw new Error("OneDrive picker not available.");

      const pickResult = await new Promise((resolve, reject) => {
        window.OneDrive.open({
          clientId,
          action: "download",
          multiSelect: true,
          // In-page dialog avoids popup blockers / "popup window is disconnected" in embedded browsers.
          openInNewWindow: false,
          advanced: {
            // Must match an Azure app Redirect URI (SPA or Web) for this origin.
            redirectUri: window.location.origin,
          },
          success: resolve,
          cancel: () => resolve(null),
          error: (err) => {
            const msg = err?.message || err?.errorCode || String(err || "OneDrive picker error");
            reject(new Error(msg));
          },
        });
      });
      if (!pickResult?.value?.length) return;

      const files = pickResult.value;

      const fetchToFile = async (f) => {
        const name = String(f?.name || "onedrive-file");
        const mimeType = f?.file?.mimeType || f?.mimeType || "application/octet-stream";
        const downloadUrl =
          f?.["@microsoft.graph.downloadUrl"] ||
          f?.downloadUrl ||
          f?.content?.downloadUrl ||
          f?.["@content.downloadUrl"] ||
          null;
        if (!downloadUrl) return null;
        const res = await fetch(downloadUrl);
        if (!res.ok) return null;
        const blob = await res.blob();
        return new File([blob], name, { type: blob.type || mimeType });
      };

      const picked = [];
      for (const f of files) {
        try {
          const file = await fetchToFile(f);
          if (file) picked.push(file);
        } catch (e) {
          console.error("OneDrive download failed:", e);
        }
      }

      if (picked.length) {
        setSelectedFiles((prev) => [...prev, ...picked]);
        setMessages((prev) => [...prev, { id: Date.now(), sender: "bot", text: `âœ… Added ${picked.length} file(s) from OneDrive.` }]);
      } else {
        alert("No files could be downloaded from OneDrive. (Some files may require Graph downloadUrl; we'll add that next if needed.)");
      }
    } catch (error) {
      console.error('Error loading OneDrive files:', error);
      alert(`Unable to access OneDrive Picker.\n\n${error?.message || String(error)}`);
    }
  };

  // Open Camera
  const openCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setStream(mediaStream);
      setShowCamera(true);
    } catch (err) {
      alert("Camera access denied or not available.");
      console.error(err);
    }
  };

 







  




  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
    setStream(null);
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = canvas.toDataURL("image/jpeg");

    const imageSentAt = Date.now();
    const imageMsg = {
      id: imageSentAt,
      sentAt: imageSentAt,
      sender: "user",
      text: "ðŸ“· [Image captured]",
      image: imageData,
    };
    setMessages((prev) => [...prev, imageMsg]);

    closeCamera();
  };





  const showVoiceQuietHint = (message) => {
    setVoiceHint(message);
    if (voiceHintTimerRef.current) clearTimeout(voiceHintTimerRef.current);
    voiceHintTimerRef.current = setTimeout(() => setVoiceHint(''), 2500);
  };

  const waitForSpeechVoices = () => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      return Promise.resolve([]);
    }
    const listed = window.speechSynthesis.getVoices() || [];
    if (listed.length) return Promise.resolve(listed);
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        try { window.speechSynthesis.onvoiceschanged = null; } catch (_) {}
        resolve(window.speechSynthesis.getVoices() || []);
      };
      try {
        window.speechSynthesis.onvoiceschanged = finish;
      } catch (_) {}
      // Chrome often fills voices a moment after first getVoices()
      setTimeout(finish, 600);
    });
  };

  const pickSpeechVoice = (preset, voicesList) => {
    const voices = Array.isArray(voicesList) && voicesList.length
      ? voicesList
      : (typeof window !== 'undefined' && window.speechSynthesis
        ? (window.speechSynthesis.getVoices() || [])
        : []);
    if (!voices.length) return null;
    const en = voices.filter((v) => /^en(-|_)/i.test(v.lang) || /^en$/i.test(v.lang));
    const pool = en.length ? en : voices;
    const qualityBoost = (n) => {
      let q = 0;
      // Prefer neural / online / natural voices that sound human
      if (/neural|natural|online|premium|enhanced|wavenet|studio|journey|generative|aria|jenny|guy|ryan|sonia|natasha|libby|michelle|andrew|emma|brian|samantha|ava|allison|susan|zira|google us english|microsoft/.test(n)) q += 8;
      if (/microsoft .+ online|google/.test(n)) q += 4;
      if (/compact|espeak|robot|novelty|whisper|whispering|bad news|good news|zarvox|trinoids|boing|bubbles|cellos|organ|superstar/.test(n)) q -= 12;
      return q;
    };
    const genderScore = (n) => {
      if (preset === 'woman') {
        if (/female|woman|zira|samantha|karen|moira|tessa|victoria|susan|linda|hazel|fiona|ava|allison|serena|salli|joanna|ivy|kimberly|aria|jenny|sonia|natasha|libby|michelle|emma|catherine|heather/.test(n)) return 6;
        if (/male|david|mark|daniel|alex(?!a)|fred|bruce|ravi|thomas|guy|matthew|justin|joey|andrew|brian|ryan|george|james/.test(n)) return -8;
        return 0;
      }
      if (preset === 'man') {
        if (/male|david|mark|daniel|fred|bruce|ravi|thomas|guy|matthew|justin|joey|george|james|andrew|brian|ryan|christopher|eric|steffan/.test(n)) return 6;
        if (/female|woman|zira|samantha|karen|moira|tessa|victoria|susan|linda|hazel|fiona|ava|allison|serena|salli|joanna|aria|jenny|sonia|natasha|libby|michelle|emma/.test(n)) return -8;
        if (/alex(?!a)/.test(n)) return 4;
        return 0;
      }
      return /neural|natural|premium|aria|jenny|guy/.test(n) ? 2 : 0;
    };
    const score = (voice) => {
      const n = `${voice.name} ${voice.voiceURI}`.toLowerCase();
      return qualityBoost(n) + genderScore(n) + (voice.localService ? 0 : 1);
    };
    return [...pool].sort((a, b) => score(b) - score(a))[0] || pool[0];
  };

  const stopSpeechPlayback = () => {
    try {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch (_) {}
    voiceSpeakingRef.current = false;
  };
  stopSpeechPlaybackRef.current = stopSpeechPlayback;

  const cleanTextForSpeech = (rawText) => String(rawText || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#*_>`\[\]()]/g, ' ')
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/\p{Extended_Pictographic}/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const shouldSkipVoiceSpeech = (text) => {
    const t = String(text || '').trim();
    if (!t) return true;
    if (/^Building it on the computer\.?$/i.test(t)) return true;
    if (/^Editing it on the computer\.?$/i.test(t)) return true;
    if (/^Opening Gmail\.?$/i.test(t)) return true;
    if (t.length < 2) return true;
    return false;
  };

  const speakZolaidReply = async (rawText, { interrupt = true, allowWhenCallEnded = false } = {}) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      return;
    }
    const clean = cleanTextForSpeech(rawText);
    if (!clean || shouldSkipVoiceSpeech(clean)) return;
    if (interrupt) stopSpeechPlayback();
    const voices = await waitForSpeechVoices();
    const voice = pickSpeechVoice(voicePresetRef.current, voices);
    const chunks = clean
      .slice(0, 1800)
      .split(/(?<=[.!?])\s+/)
      .map((part) => part.trim())
      .filter(Boolean);
    const parts = chunks.length ? chunks : [clean.slice(0, 1800)];

    voiceSpeakingRef.current = true;
    if (voiceCallActiveRef.current) setVoiceCallStatus('Speaking');

    // Chrome can freeze speechSynthesis until resume() is poked
    try {
      if (window.speechSynthesis.paused) window.speechSynthesis.resume();
    } catch (_) {}

    for (let i = 0; i < parts.length; i += 1) {
      if (!allowWhenCallEnded && !voiceCallActiveRef.current && i > 0) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        const utterance = new SpeechSynthesisUtterance(parts[i]);
        if (voice) {
          utterance.voice = voice;
          utterance.lang = voice.lang || 'en-US';
        } else {
          utterance.lang = 'en-US';
        }
        utterance.rate = 0.95;
        utterance.pitch = 1;
        utterance.volume = 1;
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          resolve();
        };
        utterance.onend = finish;
        utterance.onerror = finish;
        try {
          window.speechSynthesis.speak(utterance);
          // Keep Chrome's speech engine awake during long utterances
          const kick = window.setInterval(() => {
            try {
              if (window.speechSynthesis.speaking) window.speechSynthesis.resume();
              else window.clearInterval(kick);
            } catch (_) {
              window.clearInterval(kick);
            }
          }, 8000);
          utterance.onend = () => { window.clearInterval(kick); finish(); };
          utterance.onerror = () => { window.clearInterval(kick); finish(); };
        } catch (_) {
          finish();
        }
      });
      if (i < parts.length - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 90));
      }
    }
    voiceSpeakingRef.current = false;
  };
  speakZolaidReplyRef.current = speakZolaidReply;

  const stopVoiceCallListening = () => {
    const recognition = voiceCallRecognitionRef.current;
    voiceCallRecognitionRef.current = null;
    if (recognition) {
      try { recognition.onresult = null; } catch (_) {}
      try { recognition.onend = null; } catch (_) {}
      try { recognition.onerror = null; } catch (_) {}
      try { recognition.stop(); } catch (_) {}
      try { recognition.abort(); } catch (_) {}
    }
  };

  const startVoiceCallListening = () => {
    if (!voiceCallActiveRef.current || voiceSpeakingRef.current) return;
    if (voiceCallModeRef.current === 'realtime') return;
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      showVoiceQuietHint('Speech recognition is not available in this browser.');
      return;
    }
    stopVoiceCallListening();
    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.lang = 'en-US';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      let handledFinal = false;
      recognition.onstart = () => {
        if (voiceCallActiveRef.current) setVoiceCallStatus('Listening');
      };
      recognition.onerror = (event) => {
        if (!voiceCallActiveRef.current) return;
        const err = event?.error || '';
        // no-speech / aborted are normal — just restart listening
        if (err === 'not-allowed' || err === 'service-not-allowed') {
          showVoiceQuietHint('Microphone permission is blocked for voice call.');
          return;
        }
        setTimeout(() => {
          if (voiceCallActiveRef.current && !voiceSpeakingRef.current) startVoiceCallListening();
        }, 350);
      };
      recognition.onend = () => {
        voiceCallRecognitionRef.current = null;
        if (!voiceCallActiveRef.current || voiceSpeakingRef.current || handledFinal) return;
        setTimeout(() => {
          if (voiceCallActiveRef.current && !voiceSpeakingRef.current && !voiceCallRecognitionRef.current) {
            startVoiceCallListening();
          }
        }, 200);
      };
      recognition.onresult = (event) => {
        if (!voiceCallActiveRef.current || voiceSpeakingRef.current) return;
        let finalText = '';
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const result = event.results[i];
          if (result?.isFinal) {
            finalText += String(result[0]?.transcript || '');
          }
        }
        const transcript = finalText.trim();
        if (!transcript) {
          if (voiceCallActiveRef.current) setVoiceCallStatus('Listening');
          return;
        }
        handledFinal = true;
        stopVoiceCallListening();
        setVoiceCallStatus('Working');
        // Quick human ack so the call does not feel silent while the model thinks
        speakZolaidReplyRef.current('Okay.', { interrupt: true }).finally(() => {
          if (!voiceCallActiveRef.current) return;
          if (sendMessageRef.current) sendMessageRef.current(transcript);
        });
      };
      voiceCallRecognitionRef.current = recognition;
      recognition.start();
    } catch (_) {
      showVoiceQuietHint('Voice call could not start listening.');
    }
  };
  startVoiceCallListeningRef.current = startVoiceCallListening;

  const endVoiceCall = () => {
    voiceCallActiveRef.current = false;
    setVoiceCallActive(false);
    setVoiceCallStatus('');
    stopVoiceCallListening();
    stopSpeechPlayback();
    voiceCallModeRef.current = 'browser';
    const realtime = realtimeCallRef.current;
    realtimeCallRef.current = null;
    if (realtime && typeof realtime.stop === 'function') {
      try { realtime.stop(); } catch (_) {}
    }
  };

  const triggerWebsiteBuildFromVoice = (briefRaw) => {
    const fromArgs = String(briefRaw || '').replace(/\s+/g, ' ').trim();
    const fromHistory = voiceBriefRef.current
      .filter((t) => t.role === 'user' || /\b(website|site|landing|airbnb|search|listing|booking|filter|payment|blue|build)\b/i.test(t.text))
      .map((t) => t.text)
      .slice(-12)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    const brief = fromArgs || fromHistory || 'Create a clean modern website landing page';
    if (voiceBuildTriggeredRef.current) {
      return { ok: true, status: 'already_building', message: 'Website build already started on the live computer.' };
    }
    const prompt = /\b(create|build|make)\b.*\b(website|site|landing page)\b/i.test(brief)
      || /\blike\s+(an?\s+)?airbnb\b/i.test(brief)
      || /\bstays?\b/i.test(brief)
      ? brief
      : `Create a website. ${brief}`;

    // Skip the typed-chat clarifying loop — voice already gathered the brief.
    // Build immediately so the small live dock appears above the typing box.
    // Prefer model-generated code; keep structured createSiteSource as thin fallback.
    voiceBuildTriggeredRef.current = true;
    awaitingSiteSubjectRef.current = false;
    siteBriefRef.current = null;

    setTimeout(async () => {
      let source = null;
      let files = null;
      try {
        const generated = await fetchGeneratedSiteFromBrief(prompt, { fallbackBrand: 'Site' });
        if (generated) {
          source = generated.source;
          files = generated.files;
        }
      } catch (err) {
        console.warn('Voice model site generation failed:', err?.message || err);
      }
      if (!source) {
        try {
          source = createSiteSource(prompt);
          if (!source) source = createSiteSource('Create a clean modern website landing page');
          const revised = reviseSource(source, prompt);
          if (revised) source = revised;
          files = filesFromSource(source);
        } catch (err) {
          console.warn('Voice site plan failed, using default source:', err?.message || err);
          try {
            source = createSiteSource('Create a clean modern website landing page');
            files = filesFromSource(source);
          } catch (_) {
            voiceBuildTriggeredRef.current = false;
            return;
          }
        }
      }
      beginLiveSite(source, prompt, files ? { files } : {});
      // Keep the full sandbox closed until they tap the small screen (product rule),
      // but scroll the composer dock into view so the live thumb is obvious.
      try {
        composerBoxRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      } catch (_) {}
    }, 0);

    return {
      ok: true,
      status: 'building',
      message: 'Live computer screen is open above the typing box. Zolaid is building the website there now.',
    };
  };

  const maybeBuildWebsiteFromVoiceTranscript = (role, text) => {
    const clean = String(text || '').trim();
    if (!clean) return;
    voiceBriefRef.current = [...voiceBriefRef.current, { role, text: clean }].slice(-40);
    if (voiceBuildTriggeredRef.current) return;

    // If the model talks around a failed tool call, start the build from the brief anyway.
    if (role !== 'user') {
      const stalled =
        /\b(issue starting the live build|cannot open the live|couldn't start the live|could not start the live|having (an )?trouble (starting|opening) the live)\b/i.test(clean)
        || (/\blive build\b/i.test(clean) && /\b(issue|trouble|problem|fix)\b/i.test(clean));
      if (!stalled) return;
      const history = voiceBriefRef.current.map((t) => t.text).join(' ');
      if (!/\b(website|web site|landing|airbnb|homepage|site)\b/i.test(history) && !/\b(go ahead|build it)\b/i.test(history)) {
        return;
      }
      triggerWebsiteBuildFromVoice(history);
      return;
    }

    const goAhead = /\b(go ahead|build it|create it|start building|open the (live )?screen|open the computer|build the (web)?site)\b/i.test(clean);
    const directSite = isWebsiteRequest(clean);
    if (!goAhead && !directSite) return;

    const history = voiceBriefRef.current.map((t) => `${t.role}: ${t.text}`).join('\n');
    const looksLikeSiteTalk = /\b(website|web site|airbnb|landing page|listings?|booking|search bar|homepage)\b/i.test(history);
    if (!directSite && !looksLikeSiteTalk) return;

    const briefParts = voiceBriefRef.current
      .filter((t) => t.role === 'user' || /\b(website|airbnb|search|listing|booking|filter|payment|blue)\b/i.test(t.text))
      .map((t) => t.text)
      .slice(-12);
    const brief = briefParts.join(' ').trim() || clean;
    triggerWebsiteBuildFromVoice(brief);
  };

  const appendVoiceTranscript = (role, text) => {
    const clean = String(text || '').trim();
    if (!clean) return;
    // Skip duplicate consecutive bot lines (e.g. repeated greetings)
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (
        last
        && last.sender === (role === 'user' ? 'user' : 'bot')
        && String(last.text || '').trim() === clean
      ) {
        return prev;
      }
      const id = Date.now() + Math.floor(Math.random() * 1000);
      voiceSpokenIdsRef.current.add(id);
      return [
        ...prev,
        {
          id,
          sender: role === 'user' ? 'user' : 'bot',
          text: clean,
        },
      ];
    });
    maybeBuildWebsiteFromVoiceTranscript(role, clean);
  };

  const failOpenAiVoiceCall = (err) => {
    const why = String(err?.message || err || '').trim();
    showVoiceQuietHint(
      why
        ? `OpenAI voice unavailable (${why.slice(0, 100)}). Try again.`
        : 'OpenAI voice unavailable. Try again.'
    );
    endVoiceCall();
  };

  const startVoiceCall = () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      showVoiceQuietHint('Microphone is not available in this browser.');
      return;
    }
    if (realtimeCallRef.current) {
      try { realtimeCallRef.current.stop(); } catch (_) {}
      realtimeCallRef.current = null;
    }
    voiceSpokenIdsRef.current = new Set(
      (messagesRef.current || []).map((m) => m.id).filter((id) => id != null)
    );
    voiceStreamSpokenRef.current = { id: null, end: 0 };
    voiceBriefRef.current = [];
    voiceBuildTriggeredRef.current = false;
    voiceCallModeRef.current = 'realtime';
    voiceCallActiveRef.current = true;
    setVoiceCallActive(true);
    setVoicePresetMenuOpen(false);
    setVoiceCallStatus('Connecting');
    stopVoiceCallListening();
    stopSpeechPlayback();

    startOpenAiRealtimeCall({
      preset: voicePresetRef.current,
      onStatus: (status) => {
        if (voiceCallActiveRef.current && voiceCallModeRef.current === 'realtime') {
          setVoiceCallStatus(status);
        }
      },
      onTranscript: (role, text) => {
        if (voiceCallActiveRef.current && voiceCallModeRef.current === 'realtime') {
          appendVoiceTranscript(role, text);
        }
      },
      onToolCall: async (name, args) => {
        if (name === 'build_website_on_computer') {
          const brief = String(args?.brief || args?.description || '').trim();
          return triggerWebsiteBuildFromVoice(brief);
        }
        return { ok: false, error: `Unknown tool: ${name}` };
      },
      onError: (err) => {
        console.warn('Realtime voice error:', err?.message || err);
        if (!voiceCallActiveRef.current || voiceCallModeRef.current !== 'realtime') return;
        const msg = String(err?.message || '');
        const hardFail = /auth|unauthorized|invalid_api_key|connection failed|quota|billing/i.test(msg);
        // Keep an already-live OpenAI call unless the peer connection hard-failed.
        if (realtimeCallRef.current && !hardFail) {
          showVoiceQuietHint('Voice glitch — still on OpenAI voice.');
          return;
        }
        failOpenAiVoiceCall(err);
      },
    }).then((session) => {
      if (!voiceCallActiveRef.current || voiceCallModeRef.current !== 'realtime') {
        try { session.stop(); } catch (_) {}
        return;
      }
      realtimeCallRef.current = session;
      setVoiceCallStatus('Listening');
    }).catch((err) => {
      console.warn('Realtime voice failed to start:', err?.message || err);
      if (!voiceCallActiveRef.current || voiceCallModeRef.current !== 'realtime') return;
      failOpenAiVoiceCall(err);
    });
  };
  startVoiceCallRef.current = startVoiceCall;

  const toggleVoiceCall = () => {
    setVoicePresetMenuOpen(false);
    if (voiceCallActiveRef.current) {
      endVoiceCall();
      return;
    }
    startVoiceCall();
  };

  const selectVoicePreset = (preset) => {
    setVoicePreset(preset);
    voicePresetRef.current = preset;
    try { localStorage.setItem('zolaid-voice-preset', preset); } catch (_) {}
    setVoicePresetMenuOpen(false);
    if (realtimeCallRef.current && typeof realtimeCallRef.current.updateVoice === 'function') {
      realtimeCallRef.current.updateVoice(preset);
    }
  };

  // Preload system voices so the first spoken line can use a natural neural voice
  useEffect(() => {
    waitForSpeechVoices();
  }, []);

  // Keep preset ref synced; speak finished bot replies during an active voice call
  useEffect(() => {
    voicePresetRef.current = voicePreset;
  }, [voicePreset]);

  useEffect(() => {
    voiceCallActiveRef.current = voiceCallActive;
  }, [voiceCallActive]);

  // Speak finished replies, and speak completed sentences while the reply is still streaming
  useEffect(() => {
    if (!voiceCallActive) return undefined;
    // OpenAI Realtime handles mic + speech itself — do not double with browser TTS/STT
    if (voiceCallModeRef.current === 'realtime') return undefined;

    const streamingBot = [...messages].reverse().find(
      (msg) => msg.sender === 'bot' && (msg.isStreaming || msg.id === streamingMessageId) && msg.text
    );
    if (streamingBot && streamingBot.text) {
      const text = String(streamingBot.text);
      if (voiceStreamSpokenRef.current.id !== streamingBot.id) {
        voiceStreamSpokenRef.current = { id: streamingBot.id, end: 0 };
      }
      const already = voiceStreamSpokenRef.current.end;
      const unread = text.slice(already);
      const match = unread.match(/^[\s\S]*?[.!?](?=\s|$)/);
      if (match && match[0].trim().length > 12 && !shouldSkipVoiceSpeech(match[0])) {
        const sentence = match[0];
        voiceStreamSpokenRef.current.end = already + sentence.length;
        stopVoiceCallListening();
        let cancelled = false;
        (async () => {
          await speakZolaidReply(sentence, { interrupt: false });
          if (!cancelled && voiceCallActiveRef.current && !isStreaming) {
            // fall through to finished-reply handler on next tick
          }
        })();
        return () => { cancelled = true; };
      }
    }

    if (isStreaming || messages.some((msg) => msg.activity === 'live')) return undefined;

    const lastBot = [...messages].reverse().find(
      (msg) => msg.sender === 'bot' && msg.text && !msg.error && !msg.isStreaming
    );
    if (!lastBot || voiceSpokenIdsRef.current.has(lastBot.id)) {
      if (!voiceSpeakingRef.current && !voiceCallRecognitionRef.current) {
        startVoiceCallListening();
      }
      return undefined;
    }
    if (shouldSkipVoiceSpeech(lastBot.text)) {
      voiceSpokenIdsRef.current.add(lastBot.id);
      if (!voiceSpeakingRef.current && !voiceCallRecognitionRef.current) {
        startVoiceCallListening();
      }
      return undefined;
    }
    voiceSpokenIdsRef.current.add(lastBot.id);
    stopVoiceCallListening();
    let cancelled = false;
    (async () => {
      // If we already spoke streaming sentences, only speak the remainder
      const streamed = voiceStreamSpokenRef.current;
      const full = String(lastBot.text || '');
      const remainder = streamed.id === lastBot.id && streamed.end > 0
        ? full.slice(streamed.end).trim()
        : full;
      voiceStreamSpokenRef.current = { id: null, end: 0 };
      if (remainder) {
        await speakZolaidReply(remainder, { interrupt: streamed.end === 0 });
      } else if (!voiceSpeakingRef.current) {
        // Fully spoken during streaming — just go back to listening
      }
      if (!cancelled && voiceCallActiveRef.current && !voiceSpeakingRef.current) {
        startVoiceCallListening();
      } else if (!cancelled && voiceCallActiveRef.current) {
        // Wait for the last queued utterance to finish, then listen
        const waitListen = setInterval(() => {
          if (!voiceCallActiveRef.current) {
            clearInterval(waitListen);
            return;
          }
          if (!voiceSpeakingRef.current && !window.speechSynthesis?.speaking) {
            clearInterval(waitListen);
            startVoiceCallListening();
          }
        }, 200);
      }
    })();
    return () => { cancelled = true; };
  }, [voiceCallActive, isStreaming, messages, streamingMessageId]);

  useEffect(() => () => {
    endVoiceCall();
    cancelDictation();
    if (voiceHintTimerRef.current) clearTimeout(voiceHintTimerRef.current);
  }, []);

  // Typing in the box: close voice menu and end call so the waveform becomes Send.
  // Do NOT end the call when sendMessage clears input programmatically.
  useEffect(() => {
    if (!String(input || '').trim()) return;
    setVoicePresetMenuOpen(false);
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const typingInComposer = Boolean(
      active && (
        active.classList?.contains('composer-input')
        || active.classList?.contains('composer-textarea')
        || active.getAttribute?.('data-composer') === 'true'
        || (active.tagName === 'TEXTAREA' && active.closest?.('.composer-box, .composer-form, .chat-composer'))
      )
    );
    if (typingInComposer && voiceCallActiveRef.current) endVoiceCall();
  }, [input]);

  useEffect(() => {
    if (!voicePresetMenuOpen) return undefined;
    const onDocDown = (event) => {
      if (voiceCallWrapRef.current && !voiceCallWrapRef.current.contains(event.target)) {
        setVoicePresetMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [voicePresetMenuOpen]);

  const composeDictationInput = (finalDraft) => {
    const base = String(dictationBaseInputRef.current || '').trim();
    const spoken = String(finalDraft || '').trim();
    if (base && spoken) return `${base} ${spoken}`;
    return spoken || base;
  };

  const applyDictationToTypingBox = (finalDraft) => {
    const spoken = String(finalDraft || '').trim();
    setDictationDraft(spoken);
    setInput(composeDictationInput(spoken));
  };

  const stopDictationCapture = () => {
    const audio = dictationAudioRef.current;
    if (audio.timer) {
      try { window.clearInterval(audio.timer); } catch (_) {}
      audio.timer = 0;
    }
    if (audio.raf) {
      try { cancelAnimationFrame(audio.raf); } catch (_) {}
      audio.raf = 0;
    }
    const recorder = audio.recorder;
    audio.recorder = null;
    if (recorder && recorder.state !== 'inactive') {
      try { recorder.ondataavailable = null; } catch (_) {}
      try { recorder.onstop = null; } catch (_) {}
      try { recorder.stop(); } catch (_) {}
    }
    if (audio.stream) {
      try { audio.stream.getTracks().forEach((t) => t.stop()); } catch (_) {}
      audio.stream = null;
    }
    if (audio.ctx) {
      try { audio.ctx.close(); } catch (_) {}
      audio.ctx = null;
    }
    audio.chunks = [];
    audio.transcribing = false;
    setDictationLevels(Array.from({ length: 48 }, () => 0.08));
    setDictationBusy(false);
  };

  const startDictationMeterFromStream = (stream) => {
    const audio = dictationAudioRef.current;
    if (audio.raf) {
      try { cancelAnimationFrame(audio.raf); } catch (_) {}
      audio.raf = 0;
    }
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.72;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      audio.ctx = ctx;
      const tick = () => {
        analyser.getByteFrequencyData(data);
        const next = [];
        for (let i = 0; i < 48; i += 1) {
          const idx = Math.floor((i / 48) * data.length);
          const v = (data[idx] || 0) / 255;
          next.push(Math.max(0.08, Math.min(1, v * 1.4)));
        }
        setDictationLevels(next);
        audio.raf = requestAnimationFrame(tick);
      };
      audio.raf = requestAnimationFrame(tick);
    } catch (_) {
      // Wave stays idle if analyser fails — transcription still works.
    }
  };

  const transcribeDictationBlob = async (blob) => {
    if (!blob || blob.size < 400) return '';
    const apiBase = getApiBase();
    const body = new FormData();
    const ext = (blob.type || '').includes('mp4') ? 'mp4' : 'webm';
    body.append('audio', blob, `speech.${ext}`);
    const res = await fetch(`${apiBase}/api/realtime/transcribe`, {
      method: 'POST',
      body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || `Transcription failed (${res.status})`);
    }
    return String(data.text || '').trim();
  };

  const flushDictationAudio = async ({ final = false } = {}) => {
    const audio = dictationAudioRef.current;
    if (audio.transcribing) return;
    const chunks = audio.chunks.slice();
    if (!chunks.length) return;
    const type = chunks[0]?.type || 'audio/webm';
    const blob = new Blob(chunks, { type });
    if (!final) {
      // Keep recording; only snapshot current audio for partial transcript
    } else {
      audio.chunks = [];
    }
    audio.transcribing = true;
    setDictationBusy(true);
    try {
      const text = await transcribeDictationBlob(blob);
      if (text) {
        dictationDraftRef.current = text;
        applyDictationToTypingBox(text);
      }
    } catch (err) {
      if (final) {
        showVoiceQuietHint(err?.message || 'Could not convert speech to text.');
      }
    } finally {
      audio.transcribing = false;
      setDictationBusy(false);
    }
  };

  const cancelDictation = () => {
    dictationActiveRef.current = false;
    stopDictationCapture();
    dictationDraftRef.current = '';
    setDictationDraft('');
    setListening(false);
    setInput(String(dictationBaseInputRef.current || ''));
  };

  const finalizeDictationTranscript = async () => {
    dictationActiveRef.current = false;
    const audio = dictationAudioRef.current;
    const recorder = audio.recorder;
    if (audio.timer) {
      try { window.clearInterval(audio.timer); } catch (_) {}
      audio.timer = 0;
    }
    // Keep conversion silent: never surface status under the composer.
    setVoiceHint('');
    setDictationBusy(true);
    try {
      if (recorder && recorder.state === 'recording') {
        await new Promise((resolve) => {
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            resolve();
          };
          recorder.addEventListener('stop', finish, { once: true });
          try { recorder.requestData(); } catch (_) {}
          try { recorder.stop(); } catch (_) { finish(); }
          window.setTimeout(finish, 1200);
        });
        audio.recorder = null;
      }
      await flushDictationAudio({ final: true });
    } finally {
      const finalText = String(dictationDraftRef.current || '').trim();
      const message = composeDictationInput(finalText);
      setInput(message);
      stopDictationCapture();
      setListening(false);
      setDictationBusy(false);
      setVoiceHint('');
      return message;
    }
  };

  const finishDictationKeepText = async () => {
    await finalizeDictationTranscript();
    dictationDraftRef.current = '';
    setDictationDraft('');
    dictationBaseInputRef.current = '';
  };

  const sendDictation = async () => {
    const message = await finalizeDictationTranscript();
    dictationDraftRef.current = '';
    setDictationDraft('');
    dictationBaseInputRef.current = '';
    if (!message || replyInProgress) return;
    setInput('');
    if (sendMessageRef.current) sendMessageRef.current(message);
  };

  // Mic: record → OpenAI Whisper on your backend → typing box (no Chrome Web Speech / Google network)
  const startVoiceInput = async () => {
    if (listening || dictationActiveRef.current) {
      finishDictationKeepText();
      return;
    }
    if (voiceCallActiveRef.current) {
      showVoiceQuietHint('End the voice call to use dictation.');
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      showVoiceQuietHint('Microphone recording is not available in this browser.');
      return;
    }

    dictationBaseInputRef.current = String(input || '');
    dictationDraftRef.current = '';
    setDictationDraft('');
    dictationActiveRef.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      const preferredTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
      ];
      const mimeType = preferredTypes.find((t) => MediaRecorder.isTypeSupported(t)) || '';
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      const audio = dictationAudioRef.current;
      audio.stream = stream;
      audio.recorder = recorder;
      audio.chunks = [];
      audio.transcribing = false;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audio.chunks.push(event.data);
        }
      };

      recorder.start(1000);
      setListening(true);
      startDictationMeterFromStream(stream);

      // Partial transcripts every few seconds so text appears while you speak
      audio.timer = window.setInterval(() => {
        if (!dictationActiveRef.current) return;
        try { recorder.requestData(); } catch (_) {}
        flushDictationAudio({ final: false });
      }, 3500);
    } catch (err) {
      dictationActiveRef.current = false;
      stopDictationCapture();
      setListening(false);
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        showVoiceQuietHint('Microphone permission is blocked.');
      } else {
        showVoiceQuietHint('Could not start microphone recording.');
      }
    }
  };

  // Reset chat
  const startNewChat = () => {
    currentChatIdRef.current = null;
    siteSourceRef.current = null;
    awaitingSiteSubjectRef.current = false;
    siteBriefRef.current = null;
    if (!privacyEnabled) writeActiveChatMarker(chatScopeRef.current, FRESH_CHAT_MARKER);
    setCurrentChatId(null);
    setMessages([
      {
        id: Date.now(),
        sender: "bot",
        text: WELCOME_TEXT,
      },
    ]);
    setSocraticMode(false);
    setSocraticStage(0);
    setBusinessIdea({});
    setIsStreaming(false);
    setStreamingMessageId(null);
    openChatView();
  };

  // Clear current chat (keep history)
  const clearChat = () => {
    if (window.confirm("Are you sure you want to clear this chat? This will remove all messages in the current conversation.")) {
      startNewChat();
    }
  };

  // Delete a chat from history
  const deleteChat = (chatId, e) => {
    e.stopPropagation(); // Prevent loading the chat when clicking delete
    if (window.confirm("Are you sure you want to delete this chat?")) {
      setChatHistory(prevHistory => {
        const updatedHistory = prevHistory.filter(h => h.id !== chatId);
        writeStoredChatHistory(chatScopeRef.current, updatedHistory);
        
        // If the deleted chat was the current one, start a new chat
        if (currentChatId === chatId) {
          startNewChat();
        }
        
        return updatedHistory;
      });
    }
  };

  // Edit a message
  const startEditMessage = (messageId, currentText) => {
    setEditingMessageId(messageId);
    setEditText(currentText);
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
  };

  // Cycle task panel size: default â†’ large â†’ full (right side) â†’ default
  const cycleTaskPanelView = () => {
    setTaskPanelView((v) => (v === 'default' ? 'large' : v === 'large' ? 'full' : 'default'));
  };

  useEffect(() => {
    if (taskPanelView !== 'full') {
      setBrowserPanelFullscreen(false);
    }
  }, [taskPanelView]);

  const saveEdit = (messageId) => {
    if (!editText.trim()) {
      cancelEdit();
      return;
    }

    setMessages(prevMessages => 
      prevMessages.map(msg => 
        msg.id === messageId 
          ? { ...msg, text: editText.trim() }
          : msg
      )
    );
    
    cancelEdit();
  };

  // Handle drag and drop files
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  // Load a chat from history
  const loadChat = (chat) => {
    setIsLoadingChat(true);
    currentChatIdRef.current = chat.id;
    setCurrentChatId(chat.id);
    siteSourceRef.current = chat.siteSource && typeof chat.siteSource === 'object' ? chat.siteSource : null;
    setMessages(chat.messages);
    setShowSearchDropdown(false);
    setSearchQuery("");
    openChatView();
    setTimeout(() => setIsLoadingChat(false), 100);
  };

  // Filter chat history based on search query
  const filteredChatHistory = chatHistory.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.messages.some(msg => msg.text?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Color emoji for a library row. Same meanings as before; drawn from a color emoji font.
  const renderChatEmoji = (symbol) => (
    <span className="chat-history-emoji" aria-hidden="true">{symbol}</span>
  );

  // Function to get icon based on chat content
  const getChatIcon = (chat) => {
    const title = chat.title.toLowerCase();
    const firstMessage = chat.messages.find(m => m.sender === 'user')?.text?.toLowerCase() || '';
    const content = (title + ' ' + firstMessage).toLowerCase();

    // Shopping related
    if (content.match(/\b(shopping|buy|purchase|store|shop|product|item|cart|checkout|amazon|ebay|retail)\b/)) {
      return renderChatEmoji('\u{1F6CD}\u{FE0F}');
    }
    // Business/Entrepreneurship
    if (content.match(/\b(business|startup|entrepreneur|company|venture|profit|revenue|market|sales|strategy)\b/)) {
      return renderChatEmoji('\u{1F4BC}');
    }
    // Technology/Tech
    if (content.match(/\b(tech|technology|software|app|website|digital|computer|laptop|programming|code|ai|artificial intelligence)\b/)) {
      return renderChatEmoji('\u{1F4BB}');
    }
    // Education/Learning
    if (content.match(/\b(learn|study|education|course|tutorial|lesson|school|university|student|teach)\b/)) {
      return renderChatEmoji('\u{1F393}');
    }
    // Coding/Programming
    if (content.match(/\b(code|coding|programming|developer|javascript|python|react|html|css|function|variable|debug)\b/)) {
      return renderChatEmoji('\u{1F468}\u{200D}\u{1F4BB}');
    }
    // Music
    if (content.match(/\b(music|song|artist|album|playlist|spotify|sound|audio|instrument|guitar|piano)\b/)) {
      return renderChatEmoji('\u{1F3B5}');
    }
    // Gaming
    if (content.match(/\b(game|gaming|gamer|playstation|xbox|nintendo|steam|console|player|level|quest)\b/)) {
      return renderChatEmoji('\u{1F3AE}');
    }
    // Health/Fitness
    if (content.match(/\b(health|fitness|exercise|workout|gym|diet|nutrition|wellness|medical|doctor|hospital)\b/)) {
      return renderChatEmoji('\u{2764}\u{FE0F}');
    }
    // Food/Cooking
    if (content.match(/\b(food|recipe|cooking|restaurant|meal|dinner|lunch|breakfast|chef|kitchen|ingredient)\b/)) {
      return renderChatEmoji('\u{1F37D}\u{FE0F}');
    }
    // Travel
    if (content.match(/\b(travel|trip|vacation|flight|hotel|destination|tourist|journey|adventure|explore)\b/)) {
      return renderChatEmoji('\u{2708}\u{FE0F}');
    }
    // Home/Real Estate
    if (content.match(/\b(home|house|apartment|real estate|property|furniture|decor|interior|design|room)\b/)) {
      return renderChatEmoji('\u{1F3E0}');
    }
    // Default icon
    return renderChatEmoji('\u{1F4D6}');
  };

  const authPayload = (() => {
    if (!token || String(token).split('.').length < 2) return null;
    try {
      const segment = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = segment + '='.repeat((4 - (segment.length % 4)) % 4);
      return JSON.parse(atob(padded));
    } catch (_) {
      return null;
    }
  })();
  const signedInEmail = (user?.email || authPayload?.email || '').trim();
  const isSignedIn = Boolean(token || user);
  const accountDisplayName = (() => {
    const named = user?.name || user?.username || authPayload?.name || authPayload?.username;
    if (typeof named === 'string' && named.trim()) return named.trim();
    if (signedInEmail) return signedInEmail.split('@')[0];
    return 'Guest';
  })();
  const accountInitial = (accountDisplayName.trim().charAt(0) || 'Z').toUpperCase();
  const accountKindLabel = 'Personal';
  const accountPlanLabel = (() => {
    const key = String(userPlan || '').toLowerCase();
    if (key === 'pro') return 'Pro';
    if (key === 'pro+') return 'Pro+';
    if (key === 'ultra') return 'Ultra';
    return 'Free';
  })();
  const settingsQuery = settingsNavQuery.trim().toLowerCase();
  const visibleSettingsGroups = SETTINGS_NAV_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (!settingsQuery) return true;
        return item.label.toLowerCase().includes(settingsQuery)
          || group.label.toLowerCase().includes(settingsQuery);
      }),
    }))
    .filter((group) => group.items.length > 0);

  const replyInProgress = isStreaming || messages.some((msg) => msg.activity === 'live');
  let dockedComputer = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const item = messages[i];
    if ((item?.activity === 'live' || item?.activity === 'done') && isComputerActivity(item.text)) {
      dockedComputer = item;
      break;
    }
  }

  // JSX
  return (
    <ChatbotErrorBoundary>
      <div className={`container chatbot-theme-${resolvedTheme}`}>
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}${showAccountMenu ? ' account-menu-open' : ''}`}>
        <div className="sidebar-header-top">
          <Link to="/" className="sidebar-brand" aria-label="Zolaid home">
            <img src="/logo.png" alt="Zolaid Logo" className="sidebar-logo" />
            <span className="sidebar-brand-text">Zolaid</span>
          </Link>
          <button
            className="sidebar-toggle-internal"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <img src="/sidebar-toggle-icon.svg" alt="" width={18} height={18} className="sidebar-toggle-icon-img" />
          </button>
        </div>
        <div
          className={`new-chat-btn${mainView === 'chat' && currentChatId == null ? ' is-active' : ''}`}
          onClick={startNewChat}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startNewChat();
            }
          }}
          role="button"
          tabIndex={0}
          title="New Chat"
        >
          <FaPlus className="new-chat-icon" />
          <span>New Chat</span>
        </div>
        <button
          type="button"
          className={`new-chat-btn sidebar-apps-btn${mainView === 'apps' ? ' is-active' : ''}`}
          title="Apps"
          onClick={openAppsView}
        >
          <FaStore className="new-chat-icon" aria-hidden="true" />
          <span>Apps</span>
        </button>
        <div className="sidebar-search p-2" ref={searchDropdownRef} title="Search chats">
          <div 
            className="relative flex items-center bg-gray-100 rounded-lg px-3 py-2 hover:bg-gray-200"
            onClick={() => setShowSearchDropdown(true)}
            style={{ cursor: 'pointer' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              className="absolute left-3 text-gray-500"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
            </svg>
        <input
              ref={searchInputRef}
          type="text"
          placeholder="Search chats..."
              className="pl-8 pr-2 w-full bg-transparent outline-none text-sm text-gray-700"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchDropdown(true);
              }}
              onFocus={() => setShowSearchDropdown(true)}
              onClick={(e) => {
                e.stopPropagation();
                setShowSearchDropdown(true);
              }}
            />
          </div>
          
        </div>


        <div className="sidebar-content">
          <div className="section chat-history-section">
          <h3>Library</h3>
            <div className="chat-history-list">
              {chatHistory.length > 0 ? (
                chatHistory.slice(0, 20).map((chat) => (
                  <div
                    key={chat.id}
                    className={`section-item chat-history-item-wrapper${String(chat.id) === String(currentChatId) ? ' is-active' : ''}`}
                    title={chat.title}
                  >
                    <div className="chat-history-item-content" onClick={() => loadChat(chat)}>
                      {getChatIcon(chat)}
                      <span className="chat-history-item-title">{chat.title}</span>
                    </div>
                    <div className="chat-menu-container">
                      <button
                        className="chat-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenChatMenuId(openChatMenuId === chat.id ? null : chat.id);
                        }}
                        title="More options"
                        aria-label="More options"
                      >
                        <FaEllipsisH size={14} />
                      </button>
                      {openChatMenuId === chat.id && (
                        <>
                          <div className="chat-menu-overlay" onClick={() => setOpenChatMenuId(null)}></div>
                          <div className="chat-menu-dropdown">
                            <div className="chat-menu-item" onClick={() => {
                              // Rename chat (placeholder)
                              const newTitle = prompt('Rename chat:', chat.title);
                              if (newTitle && newTitle.trim()) {
                                setChatHistory(prevHistory => {
                                  const updated = prevHistory.map(h => 
                                    h.id === chat.id ? { ...h, title: newTitle.trim() } : h
                                  );
                                  writeStoredChatHistory(chatScopeRef.current, updated);
                                  return updated;
                                });
                              }
                              setOpenChatMenuId(null);
                            }}>
                              âœï¸ Rename
                            </div>
                            <div className="chat-menu-item" onClick={() => {
                              // Export chat (placeholder)
                              const chatData = JSON.stringify(chat.messages, null, 2);
                              const blob = new Blob([chatData], { type: 'application/json' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${chat.title.replace(/[^a-z0-9]/gi, '_')}.json`;
                              a.click();
                              URL.revokeObjectURL(url);
                              setOpenChatMenuId(null);
                            }}>
                              ðŸ“¥ Export
                            </div>
                            <div className="chat-menu-divider"></div>
                            <div className="chat-menu-item chat-menu-item-danger" onClick={(e) => {
                              e.stopPropagation();
                              deleteChat(chat.id, e);
                              setOpenChatMenuId(null);
                            }}>
                              ðŸ—‘ï¸ Delete Chat
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="section-item empty-state">Your chats show up here.</div>
              )}
            </div>
          </div>
        </div>

        <div className="sidebar-fixed-bottom">
          <div className="sidebar-footer">
            <div className="sidebar-account" ref={accountMenuRef}>
              {showAccountMenu && (
                <div className="sidebar-account-menu" role="menu" aria-label="Account">
                  <div className="sidebar-account-head">
                    <span className="sidebar-account-avatar" aria-hidden="true">{accountInitial}</span>
                    <div className="sidebar-account-head-copy">
                      <div className="sidebar-account-name">{accountDisplayName}</div>
                    <div className="sidebar-account-kind">{accountKindLabel}</div>
                    </div>
                  </div>
                  <div className="sidebar-account-plan">
                    <span className="sidebar-account-plan-name">{accountPlanLabel}</span>
                    <button
                      type="button"
                      className="sidebar-account-upgrade"
                      onClick={() => {
                        setShowAccountMenu(false);
                        navigate('/pricing');
                      }}
                    >
                      Upgrade
                    </button>
                  </div>
                  <button
                    type="button"
                    className="sidebar-account-item"
                    role="menuitem"
                    onClick={() => {
                      setShowAccountMenu(false);
                      setSelectedSettingsItem('overview');
                      setShowSettingsMenu(true);
                    }}
                  >
                    Account
                  </button>
                  <button
                    type="button"
                    className="sidebar-account-item"
                    role="menuitem"
                    onClick={() => {
                      setShowAccountMenu(false);
                      setSelectedSettingsItem('settings');
                      setShowSettingsMenu(true);
                    }}
                  >
                    Settings
                  </button>
                  <button
                    type="button"
                    className="sidebar-account-item"
                    role="menuitem"
                    onClick={() => {
                      setShowAccountMenu(false);
                      navigate('/');
                    }}
                  >
                    <span>Homepage</span>
                    <svg className="sidebar-account-external" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 17 17 7"/><path d="M9 7h8v8"/></svg>
                  </button>
                  <a
                    className="sidebar-account-item"
                    role="menuitem"
                    href="mailto:hello@zolaid.com"
                    onClick={() => setShowAccountMenu(false)}
                  >
                    <span>Get help</span>
                    <svg className="sidebar-account-external" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 17 17 7"/><path d="M9 7h8v8"/></svg>
                  </a>
                  <button
                    type="button"
                    className="sidebar-account-item"
                    role="menuitem"
                    onClick={() => {
                      setShowAccountMenu(false);
                      navigate('/docs');
                    }}
                  >
                    <span>Docs</span>
                    <svg className="sidebar-account-external" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 17 17 7"/><path d="M9 7h8v8"/></svg>
                  </button>
                  {isSignedIn ? (
                    <>
                      <div className="sidebar-account-divider" role="separator" />
                      <button
                        type="button"
                        className="sidebar-account-item sidebar-account-item-signout"
                        role="menuitem"
                        onClick={() => {
                          setShowAccountMenu(false);
                          try { logout(); } catch (_) {}
                          navigate('/');
                        }}
                      >
                        Sign out
                      </button>
                    </>
                  ) : null}
                </div>
              )}
              <div className="sidebar-account-bar">
                <button
                  type="button"
                  className="sidebar-account-trigger"
                  title="Account"
                  aria-label="Account"
                  aria-haspopup="menu"
                  aria-expanded={showAccountMenu}
                  onClick={() => {
                    setShowSettingsMenu(false);
                    setShowAccountMenu((open) => !open);
                  }}
                >
                  {isSignedIn ? (
                    <span className="sidebar-account-trigger-avatar" aria-hidden="true">{accountInitial}</span>
                  ) : (
                  <FaUser className="section-item-icon sidebar-account-person" aria-hidden="true" />
                  )}
                  <span className="sidebar-account-trigger-label">Account</span>
                </button>
                <button
                  type="button"
                  className="section-item settings-item"
                  aria-label="Settings"
                  ref={settingsButtonRef}
                  onClick={() => {
                    setShowAccountMenu(false);
                    setSelectedSettingsItem((item) => item || 'overview');
                    setShowSettingsMenu((open) => !open);
                  }}
                >
                  <FaCog className="section-item-icon" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`chat-panel ${showSandbox ? 'chat-panel-split chat-panel-split--preview-right' : ''} ${(showBrowserPanel || showImageEditorPanel || showPhotoshopEditorPanel || showVideoEditorPanel) && taskPanelView === 'full' ? 'chat-panel-split' : ''} ${((showBrowserPanel && taskPanelView === 'full' && browserPanelFullscreen) || sandboxFullscreen) ? 'browser-panel-fullscreen' : ''}`}
      >
        <div className={`chat-panel-main${mainView === 'apps' ? ' chat-panel-main--apps' : ''}`}>
        {mainView === 'apps' ? (
          <div className="chat-apps-host">
            <Apps
              embedded
              onAppAdded={(app, list) => {
                if (Array.isArray(list)) setConnectedApps(list);
                else if (app?.id) {
                  setConnectedApps((prev) => {
                    const id = String(app.id).toLowerCase();
                    return prev.includes(id) ? prev : [...prev, id];
                  });
                }
              }}
              onBackToChat={() => {
                openChatView();
              }}
            />
          </div>
        ) : null}
        {isStreaming && <div className="chat-loading-bar" aria-hidden="true" />}
        {socraticMode && (
          <div className="socratic-indicator">
            ðŸŽ¯ Socratic Mode Active - Stage: {socraticQuestions[socraticStage].stage}
          </div>
        )}

        {/* Prompt Presets */}
        {showPromptPresets && (
          <div className="prompt-presets-container">
            <PromptPresets 
              onSelectPreset={(prompt) => {
                setInput(prompt);
                setShowPromptPresets(false);
              }}
            />
          </div>
        )}

        <div className="chat-box" ref={chatBoxRef}>
          {messages.map((msg) => (
            <div key={msg.id} className={`message-wrapper ${msg.sender}`}>
              <div className={`message ${msg.sender} ${msg.error ? 'error-message' : ''}`}>
                {msg.image && <img src={msg.image} alt="Captured" className="message-image" />}
                {msg.files && msg.files.length > 0 && (
                  <div className="message-files">
                    {msg.files.map((file, idx) => (
                      <div key={idx} className="message-file-item">
                        {file.type.startsWith('image/') ? (
                          <img src={file.url} alt={file.name} className="message-file-image" />
                        ) : file.type.startsWith('video/') ? (
                          <video src={file.url} controls className="message-file-video" />
                        ) : (
                          <div className="message-file-document">
                            <span className="file-icon">ðŸ“„</span>
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">{(file.size / 1024).toFixed(2)} KB</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {msg.sender === "bot" ? (
                  <>
                  {isStreaming && msg.id === streamingMessageId && msg.activity !== 'live' && msg.activity !== 'done' && (
                    <ChatActivityStatus active />
                  )}
                  {msg.showWorked && msg.activity !== 'live' && msg.activity !== 'done' && (
                    <ChatActivityStatus
                      active={false}
                      startedAt={msg.startedAt}
                      workedMs={msg.workedMs}
                    />
                  )}
                  {(msg.activity === 'live' || msg.activity === 'done') ? (
                    <ChatActivityStatus
                      label={msg.text}
                      startedAt={msg.startedAt}
                      workedMs={msg.workedMs}
                      steps={msg.steps}
                      active={msg.activity === 'live'}
                      statusOnComposer={dockedComputer?.id === msg.id}
                    />
                  ) : msg.isStreaming && msg.text && msg.text.length > 0 ? (
                    <StreamingMessage 
                      text={chatVisibleText(msg.text)} 
                      isStreaming={true}
                      onComplete={() => {
                        const id = msg.id;
                        setMessages((prev) => prev.map(m =>
                          m.id === id ? { ...m, isStreaming: false } : m
                        ));
                        if (streamingMessageIdRef.current === id) {
                          setIsStreaming(false);
                          setStreamingMessageId(null);
                          streamingMessageIdRef.current = null;
                        }
                        typewriterDoneRef.current = true;
                        pumpChatQueue();
                      }}
                    />
                  ) : msg.text ? (
                    <>
                      <div className="bot-answer-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ node, ...props }) => <div className="markdown-p" {...props} />,
                          ul: ({ node, ...props }) => <ul className="markdown-ul" {...props} />,
                          ol: ({ node, ...props }) => <ol className="markdown-ol" {...props} />,
                          li: ({ node, ...props }) => <li className="markdown-li" {...props} />,
                          strong: ({ node, ...props }) => <strong className="markdown-strong" {...props} />,
                          em: ({ node, ...props }) => <em className="markdown-em" {...props} />,
                          pre: ({ node, children }) => <div className="markdown-pre-wrapper">{children}</div>,
                          code: CodeBlock,
                          h1: ({ node, ...props }) => <h1 className="markdown-h1" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="markdown-h2" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="markdown-h3" {...props} />,
                          h4: ({ node, ...props }) => <h4 className="markdown-h4" {...props} />,
                          h5: ({ node, ...props }) => <h5 className="markdown-h5" {...props} />,
                          h6: ({ node, ...props }) => <h6 className="markdown-h6" {...props} />,
                          hr: ({ node, ...props }) => <hr className="markdown-hr" {...props} />,
                          blockquote: ({ node, ...props }) => <blockquote className="markdown-blockquote" {...props} />,
                          a: ({ node, href, ...props }) => {
                            const isExternal = href && (href.startsWith('http://') || href.startsWith('https://'));
                            return (
                              <a
                                className="markdown-a"
                                href={href}
                                {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                {...props}
                              />
                            );
                          },
                          br: ({ node, ...props }) => <br {...props} />,
                        }}
                      >
              {chatVisibleText(msg.text)}
                      </ReactMarkdown>
                      </div>
                      {msg.pendingActions && Array.isArray(msg.pendingActions) && msg.pendingActions.length > 0 && (
                        <div className="error-actions">
                          <button
                            className="retry-btn"
                            onClick={() => {
                              setZolaidActions(msg.pendingActions);
                              setShowZolaidComputer(true);
                              setCurrentTask(msg.contract?.intent || 'execute');
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, pendingActions: null } : m));
                            }}
                          >
                            âœ… Approve
                          </button>
                          <button
                            className="regenerate-btn"
                            onClick={() => {
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, pendingActions: null } : m));
                            }}
                          >
                            âŒ Reject
                          </button>
                          <button
                            className="regenerate-btn"
                            onClick={() => {
                              sendMessage("Before executing, propose a plan and ask for approvals.", null);
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, pendingActions: null } : m));
                            }}
                          >
                            ðŸ§­ Ask for plan
                          </button>
                        </div>
                      )}
                      {msg.pendingPlan && (
                        <div className="error-actions">
                          <button
                            className="retry-btn"
                            onClick={() => {
                              // Symbiosis: user approves plan -> ask backend to execute it
                              sendMessage("Proceed. Execute the plan now. Return mode=execute with actions.", null);
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, pendingPlan: null } : m));
                            }}
                          >
                            â–¶ï¸ Execute plan
                          </button>
                          <button
                            className="regenerate-btn"
                            onClick={() => setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, pendingPlan: null } : m))}
                          >
                            âŒ Cancel
                          </button>
                        </div>
                      )}
                      {msg.error && (
                        <div className="error-actions">
                          <button 
                            className="retry-btn"
                            onClick={() => retryMessage(msg.id)}
                          >
                            ðŸ”„ Retry
                          </button>
                          <button 
                            className="regenerate-btn"
                            onClick={() => retryMessage(msg.id)}
                          >
                            âœ¨ Regenerate
                          </button>
                          <a
                            href={`${typeof window !== 'undefined' ? window.location.origin : ''}/chat/diag`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="retry-btn"
                            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                          >
                            ðŸ”— Test connection
                          </a>
                        </div>
                      )}
                    </>
                  ) : null}
                  </>
                ) : editingMessageId === msg.id ? (
                  <div className="edit-message-container">
                    <textarea
                      className="edit-message-input"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          saveEdit(msg.id);
                        } else if (e.key === 'Escape') {
                          cancelEdit();
                        }
                      }}
                      autoFocus
                    />
                    <div className="edit-message-actions">
                      <button className="edit-save-btn" onClick={() => saveEdit(msg.id)}>Save</button>
                      <button className="edit-cancel-btn" onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="user-message-content">
                    <div className="user-message-text">{msg.text}</div>
                  </div>
                )}
              </div>
              {msg.sender === "user" && editingMessageId !== msg.id && (
                <div className="user-message-actions">
                  {messageSentTime(msg)}
                  <button
                    type="button"
                    className="message-copy-btn"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(msg.text || '');
                        const btn = document.activeElement;
                        if (btn && btn.classList) {
                          btn.classList.add('copied');
                          setTimeout(() => btn.classList.remove('copied'), 1500);
                        }
                      } catch (_) {}
                    }}
                    title="Copy"
                    aria-label="Copy message"
                  >
                    <svg
                      className="message-copy-icon"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </button>
                </div>
              )}
              {msg.sender === "bot" && !msg.isStreaming && !STOCK_GREETINGS.has(msg.text) ? (
                <div className="user-message-actions answer-message-actions">
                  {messageSentTime(msg)}
                  {answerCopyText(msg) ? (
                    <button
                      type="button"
                      className="message-copy-btn"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(answerCopyText(msg) || '');
                          const btn = document.activeElement;
                          if (btn && btn.classList) {
                            btn.classList.add('copied');
                            setTimeout(() => btn.classList.remove('copied'), 1500);
                          }
                        } catch (_) {}
                      }}
                      title="Copy"
                      aria-label="Copy message"
                    >
                      <svg
                        className="message-copy-icon"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  ) : null}
                  <button
                    className={`feedback-btn thumbs-up ${messageFeedback[msg.id] === 'thumbsUp' ? 'active' : ''}`}
                    onClick={() => {
                      const newFeedback = messageFeedback[msg.id] === 'thumbsUp' ? null : 'thumbsUp';
                      setMessageFeedback({ ...messageFeedback, [msg.id]: newFeedback });
                      // Send feedback to backend (optional)
                      if (newFeedback) {
                        console.log('Feedback sent:', { messageId: msg.id, feedback: 'thumbsUp' });
                      }
                    }}
                    title="Helpful"
                    aria-label="Helpful"
                  >
                    <svg
                      className="feedback-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M7 10v12" />
                      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
                    </svg>
                  </button>
                  <button
                    className={`feedback-btn thumbs-down ${messageFeedback[msg.id] === 'thumbsDown' ? 'active' : ''}`}
                    onClick={() => {
                      const newFeedback = messageFeedback[msg.id] === 'thumbsDown' ? null : 'thumbsDown';
                      setMessageFeedback({ ...messageFeedback, [msg.id]: newFeedback });
                      // Send feedback to backend (optional)
                      if (newFeedback) {
                        console.log('Feedback sent:', { messageId: msg.id, feedback: 'thumbsDown' });
                      }
                    }}
                    title="Not helpful"
                    aria-label="Not helpful"
                  >
                    <svg
                      className="feedback-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M17 14V2" />
                      <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
                    </svg>
                  </button>
                </div>
              ) : null}
            </div>
          ))}
          {messages.length === 1 && STOCK_GREETINGS.has(messages[0]?.text) && !isStreaming && (
            <div className="chat-starters" role="group" aria-label="Example requests">
              {CHAT_STARTERS.map((task) => (
                <button
                  key={task.label}
                  type="button"
                  className="chat-starter"
                  onClick={() => sendMessage(task.text)}
                >
                  <span className="chat-starter__label">{task.label}</span>
                  <span className="chat-starter__hint">{task.hint}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="thread-bottom-container">
          {showSearchDropdown && (
            <>
              <div className="settings-menu-overlay" onClick={() => setShowSearchDropdown(false)}></div>
              <div className="search-dropdown">
                <div className="search-dropdown-top">
                  <div className="search-dropdown-input-wrapper">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      fill="currentColor"
                      className="search-dropdown-icon"
                      viewBox="0 0 16 16"
                    >
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                    </svg>
          <input
                      ref={searchDropdownInputRef}
                      type="text"
                      placeholder="Search chats..."
                      className="search-dropdown-input"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowSearchDropdown(true);
                      }}
                      onFocus={() => setShowSearchDropdown(true)}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="search-dropdown-content">
                  {filteredChatHistory.length > 0 ? (
                    <>
                      <div className="search-dropdown-header">Recent Chats</div>
                      {filteredChatHistory.map((chat) => (
                        <div
                          key={chat.id}
                          className="search-dropdown-item"
                          onClick={() => loadChat(chat)}
                        >
                          <div className="search-dropdown-item-title">{chat.title}</div>
                          <div className="search-dropdown-item-time">
                            {new Date(chat.timestamp).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : searchQuery ? (
                    <div className="search-dropdown-empty">No chats found</div>
                  ) : (
                    <div className="search-dropdown-empty">No recent chats</div>
                  )}
                </div>
              </div>
            </>
          )}
          {showSettingsMenu && createPortal(
            <>
              <div
                className="settings-menu-overlay settings-menu-overlay-live"
                onClick={() => setShowSettingsMenu(false)}
              ></div>
              <div
                className="settings-menu-wrapper settings-menu-over-live"
                ref={settingsMenuRef}
              >
                <button
                  type="button"
                  className="settings-menu-close"
                  aria-label="Close settings"
                  onClick={() => setShowSettingsMenu(false)}
                >
                  Ã—
                </button>
                {settingsToast ? <div className="settings-toast">{settingsToast}</div> : null}
                <div className="settings-menu-content">
                  <div className="settings-menu-section">
                    <label className="settings-nav-search">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                        <circle cx="11" cy="11" r="6.5" />
                        <path d="m16 16 4 4" />
                      </svg>
                      <input
                        type="search"
                        placeholder="Search"
                        value={settingsNavQuery}
                        onChange={(e) => setSettingsNavQuery(e.target.value)}
                        aria-label="Search settings"
                      />
                    </label>
                    {visibleSettingsGroups.length === 0 ? (
                      <div className="settings-nav-empty">No matches</div>
                    ) : visibleSettingsGroups.map((group) => (
                      <div className="settings-nav-group" key={group.label}>
                        <div className="settings-nav-label">{group.label}</div>
                        {group.items.map((item) => (
                          <div
                            key={item.id}
                            className={`settings-menu-item ${((selectedSettingsItem === item.id) || (!selectedSettingsItem && item.id === 'overview')) ? 'active' : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedSettingsItem(item.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedSettingsItem(item.id); } }}
                          >
                            <SettingsNavIcon id={item.id} />
                            <span>{item.label}</span>
                    </div>
                        ))}
                    </div>
                    ))}
                  </div>
                  <div className="settings-detail-panel">
                    {(selectedSettingsItem === 'overview' || !selectedSettingsItem) && (
                      <>
                        <div className="settings-detail-header">Account</div>
                        <div className="settings-detail-content settings-account-panel">
                          <div className="settings-account-identity">
                            <span className="settings-account-avatar" aria-hidden="true">{accountInitial}</span>
                            <div className="settings-account-identity-copy">
                              <span className="settings-account-field-label">Full name</span>
                              <div className="settings-account-name-field">{accountDisplayName}</div>
                          </div>
                          </div>
                          <div className="settings-account-plan">
                            <span className="settings-account-plan-name">{accountPlanLabel}</span>
                            <button className="settings-account-upgrade" type="button" onClick={() => goToFromSettings("/pricing")}>Upgrade</button>
                            </div>
                          <div className="settings-account-row">
                            <div className="settings-account-row-copy">
                              <div className="settings-account-row-label">Email</div>
                              <div className="settings-account-row-value">{signedInEmail || 'Not signed in'}</div>
                            </div>
                            {(user || token) ? (
                              <button className="settings-account-action" type="button" onClick={() => { try { logout(); } catch (_) {} goToFromSettings("/"); }}>Sign out</button>
                            ) : (
                              <button className="settings-account-action" type="button" onClick={() => goToFromSettings("/login")}>Sign in</button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'usage' && (
                      <>
                        <div className="settings-detail-header">Usage & Credits</div>
                        <div className="settings-detail-content">
                          <UsageMeter />
                          <button className="top-up-btn" onClick={() => setShowCreditTopUp(true)}>ðŸ’° Top Up Credits</button>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'settings' && (
                      <>
                        <div className="settings-detail-header">Settings</div>
                        <div className="settings-detail-content">
                          <div className="settings-options">
                            <div className="setting-option" role="button" tabIndex={0} onClick={() => setSelectedSettingsItem("preferences")}>Theme</div>
                            <div className="setting-option" role="button" tabIndex={0} onClick={() => setSelectedSettingsItem("preferences")}>Language</div>
                            <div className="setting-option" role="button" tabIndex={0}>Notifications</div>
                            <div className="setting-option" role="button" tabIndex={0}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input type="checkbox" checked={privacyEnabled} onChange={e => { setPrivacyEnabled(e.target.checked); try { localStorage.setItem('zolaid-privacy-enabled', e.target.checked ? 'true' : 'false'); } catch (_) {} }} style={{ marginRight: 8 }} />
                                Privacy Enabled
                              </label>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'preferences' && (
                      <>
                        <div className="settings-detail-header">{t("preferences")}</div>
                        <div className="settings-detail-content">
                          <div className="preference-section">
                            <div className="preference-item">
                              <label>{t("theme")}</label>
                              <select className="preference-select" value={theme} onChange={(e) => { const newTheme = e.target.value; setTheme(newTheme); localStorage.setItem('zolaid-theme', newTheme); }}>
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                                <option value="system">System</option>
                              </select>
                            </div>
                            <div className="preference-item">
                              <label>{t("language")}</label>
                              <select className="preference-select" value={language} onChange={(e) => { const next = e.target.value; setLanguage(next); try { localStorage.setItem("zolaid-language", next); } catch (_) {} }}>
                                <option value="en">English</option>
                                <option value="es">EspaÃ±ol (Spanish)</option>
                                <option value="fr">FranÃ§ais (French)</option>
                                <option value="de">Deutsch (German)</option>
                                <option value="it">Italiano (Italian)</option>
                                <option value="pt">PortuguÃªs (Portuguese)</option>
                                <option value="nl">Nederlands (Dutch)</option>
                                <option value="ru">Ð ÑƒÑÑÐºÐ¸Ð¹ (Russian)</option>
                                <option value="zh-CN">ç®€ä½“ä¸­æ–‡ (Chinese Simplified)</option>
                                <option value="zh-TW">ç¹é«”ä¸­æ–‡ (Chinese Traditional)</option>
                                <option value="ja">æ—¥æœ¬èªž (Japanese)</option>
                                <option value="ko">í•œêµ­ì–´ (Korean)</option>
                                <option value="ar">Ø§Ù„Ø¹Ø±Ø¨ÙŠØ© (Arabic)</option>
                                <option value="hi">à¤¹à¤¿à¤¨à¥à¤¦à¥€ (Hindi)</option>
                                <option value="tr">TÃ¼rkÃ§e (Turkish)</option>
                                <option value="pl">Polski (Polish)</option>
                                <option value="sv">Svenska (Swedish)</option>
                                <option value="da">Dansk (Danish)</option>
                                <option value="no">Norsk (Norwegian)</option>
                                <option value="fi">Suomi (Finnish)</option>
                                <option value="el">Î•Î»Î»Î·Î½Î¹ÎºÎ¬ (Greek)</option>
                                <option value="cs">ÄŒeÅ¡tina (Czech)</option>
                                <option value="ro">RomÃ¢nÄƒ (Romanian)</option>
                                <option value="hu">Magyar (Hungarian)</option>
                                <option value="th">à¹„à¸—à¸¢ (Thai)</option>
                                <option value="vi">Tiáº¿ng Viá»‡t (Vietnamese)</option>
                                <option value="id">Bahasa Indonesia (Indonesian)</option>
                                <option value="he">×¢×‘×¨×™×ª (Hebrew)</option>
                                <option value="uk">Ð£ÐºÑ€Ð°Ñ—Ð½ÑÑŒÐºÐ° (Ukrainian)</option>
                                <option value="bn">à¦¬à¦¾à¦‚à¦²à¦¾ (Bengali)</option>
                              </select>
                            </div>
                            <div className="preference-item">
                              <label>{t("timeZone") || "Time Zone"}</label>
                              <select className="preference-select" value={timezone} onChange={(e) => { const next = e.target.value; setTimezone(next); try { localStorage.setItem("zolaid-timezone", next); } catch (_) {} }}>
                                <option value="UTC">UTC</option>
                                <option value="America/New_York">Eastern Time (US & Canada)</option>
                                <option value="America/Chicago">Central Time (US & Canada)</option>
                                <option value="America/Denver">Mountain Time (US & Canada)</option>
                                <option value="America/Los_Angeles">Pacific Time (US & Canada)</option>
                                <option value="Europe/London">London</option>
                                <option value="Europe/Paris">Paris</option>
                                <option value="Europe/Berlin">Berlin</option>
                                <option value="Europe/Moscow">Moscow</option>
                                <option value="Asia/Dubai">Dubai</option>
                                <option value="Asia/Kolkata">India</option>
                                <option value="Asia/Shanghai">China</option>
                                <option value="Asia/Tokyo">Tokyo</option>
                                <option value="Asia/Seoul">Seoul</option>
                                <option value="Australia/Sydney">Sydney</option>
                                <option value="Pacific/Auckland">Auckland</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'docs' && (
                      <>
                        <div className="settings-detail-header">Docs</div>
                        <div className="settings-detail-content">
                          <p style={{ marginBottom: 12 }}>Zolaid documentation, guides, and API reference.</p>
                          <button type="button" className="upgrade-btn" onClick={() => goToFromSettings('/docs')}>
                            View Docs
                          </button>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'cloud-agents' && (
                      <>
                        <div className="settings-detail-header">Cloud Agents</div>
                        <div className="settings-detail-content">
                          <p style={{ marginBottom: 12 }}>Run Zolaid agents in the cloud for long tasks, scheduled jobs, and background automation.</p>
                          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                            <li>Background task execution</li>
                            <li>Scheduled runs (e.g. daily summaries)</li>
                            <li>Multi-step workflows</li>
                          </ul>
                          <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--z-text-secondary, #666)' }}>Cloud agents use your existing credits. Manage them from the Zolaid dashboard or API.</p>
                          <button type="button" className="upgrade-btn" onClick={() => goToFromSettings('/')}>Open Dashboard</button>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'bugbot' && (
                      <>
                        <div className="settings-detail-header">Bugbot</div>
                        <div className="settings-detail-content">
                          <p style={{ marginBottom: 12 }}>Bugbot helps find and fix issues in your code and projects.</p>
                          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                            <li>Describe a bug or paste an error â€” get suggested fixes</li>
                            <li>Run in Zolaid Computer for automated patches</li>
                            <li>Use in chat: e.g. &quot;Debug this error&quot; or &quot;Why does this code fail?&quot;</li>
                          </ul>
                          <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--z-text-secondary, #666)' }}>No separate setup. Just ask in the chat or open Zolaid Computer and request a fix.</p>
                          <button type="button" className="upgrade-btn" onClick={() => { setShowSettingsMenu(false); setSelectedSettingsItem(null); }}>Back to Chat</button>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'spending' && (
                      <>
                        <div className="settings-detail-header">Spending</div>
                        <div className="settings-detail-content">
                          <p style={{ marginBottom: 12 }}>View and control how your credits are used across chat, image edit, video, and tools.</p>
                          <p style={{ marginBottom: 12, fontSize: 13, color: 'var(--z-text-secondary, #666)' }}>Current usage is shown in the <strong>Usage</strong> section. Top up credits when you need more.</p>
                          <button type="button" className="upgrade-btn" onClick={openLiveScreenUsage}>View Usage</button>
                          <button type="button" className="top-up-btn" style={{ marginLeft: 8 }} onClick={() => { setShowCreditTopUp(true); }}>Top Up Credits</button>
                        </div>
                      </>
                    )}
                    {selectedSettingsItem === 'billing' && (
                      <>
                        <div className="settings-detail-header">Billing & Invoices</div>
                        <div className="settings-detail-content">
                          <p style={{ marginBottom: 12 }}>Manage payment methods, view invoices, and update your billing address.</p>
                          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
                            <li>Add or update payment method</li>
                            <li>Download past invoices</li>
                            <li>Change billing cycle (monthly/yearly)</li>
                          </ul>
                          <button type="button" className="upgrade-btn" onClick={() => setShowCreditTopUp(true)}>Open Billing</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>,
            document.body
          )}
          <div className="thread-bottom">
            <div className="thread-content-wrapper">
              <div className="thread-content">
                {showBrowserPanel && taskPanelView !== 'full' && (
                  <div className={`zolaid-workspace browser-task-panel ${browserPanelMinimized ? 'minimized' : ''} ${taskPanelView === 'large' ? 'browser-task-panel-size-large' : ''}`} id="zolaid-agent-workspace" style={{ minWidth: '320px', maxWidth: '100%' }}>
                    <div className="zolaid-workspace-inner">
                      <div className="zolaid-workspace-header">
                        <span className="zolaid-workspace-title">Browser Operator</span>
                        <div className="zolaid-workspace-header-actions">
                          <button type="button" className="zolaid-workspace-header-btn" title="Options" aria-label="Options" aria-expanded="false" aria-haspopup="dialog" onClick={cycleTaskPanelView}>
                            <svg width="20" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" d="M3.38477 3.33341C2.92453 3.33341 2.55143 3.70651 2.55143 4.16675V12.5001C2.55143 12.9603 2.92453 13.3334 3.38477 13.3334H16.7181C17.1783 13.3334 17.5514 12.9603 17.5514 12.5001V4.16675C17.5514 3.70651 17.1783 3.33341 16.7181 3.33341H3.38477ZM0.884766 4.16675C0.884766 2.78604 2.00405 1.66675 3.38477 1.66675H16.7181C18.0988 1.66675 19.2181 2.78604 19.2181 4.16675V12.5001C19.2181 13.8808 18.0988 15.0001 16.7181 15.0001H3.38477C2.00405 15.0001 0.884766 13.8808 0.884766 12.5001V4.16675Z" fill="currentColor"/><path fillRule="evenodd" clipRule="evenodd" d="M5.88477 17.5001C5.88477 17.0398 6.25786 16.6667 6.7181 16.6667H13.3848C13.845 16.6667 14.2181 17.0398 14.2181 17.5001C14.2181 17.9603 13.845 18.3334 13.3848 18.3334H6.7181C6.25786 18.3334 5.88477 17.9603 5.88477 17.5001Z" fill="currentColor"/><path fillRule="evenodd" clipRule="evenodd" d="M10.0501 13.3333C10.5104 13.3333 10.8835 13.7063 10.8835 14.1666V17.4999C10.8835 17.9602 10.5104 18.3333 10.0501 18.3333C9.58989 18.3333 9.2168 17.9602 9.2168 17.4999V14.1666C9.2168 13.7063 9.58989 13.3333 10.0501 13.3333Z" fill="currentColor"/></svg>
                          </button>
                          <button
                            type="button"
                            className="zolaid-workspace-header-btn"
                            title="Full screen"
                            aria-label="Full screen"
                            onClick={() => {
                              setTaskPanelView('full');
                              setBrowserPanelFullscreen(true);
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 14" fill="none" width="16" height="16" aria-hidden="true"><path d="M3.75 3.85C3.75 3.51863 4.01863 3.25 4.35 3.25L13.65 3.25C13.9814 3.25 14.25 3.51863 14.25 3.85L14.25 10.15C14.25 10.4814 13.9814 10.75 13.65 10.75L4.35 10.75C4.01863 10.75 3.75 10.4814 3.75 10.15L3.75 3.85Z" fill="currentColor"/><path d="M15.75 2.5C15.75 2.08579 15.4142 1.75 15 1.75L3 1.75C2.58579 1.75 2.25 2.08579 2.25 2.5L2.25 11.5C2.25 11.9142 2.58579 12.25 3 12.25L15 12.25C15.4142 12.25 15.75 11.9142 15.75 11.5L15.75 2.5ZM17.25 11.5C17.25 12.7426 16.2426 13.75 15 13.75L3 13.75C1.75736 13.75 0.75 12.7426 0.75 11.5L0.75 2.5C0.75 1.25736 1.75736 0.25 3 0.25L15 0.25C16.2426 0.25 17.25 1.25736 17.25 2.5L17.25 11.5Z" fill="currentColor"/></svg>
                          </button>
                          <button type="button" className="zolaid-workspace-header-btn" onClick={() => setBrowserPanelMinimized((v) => !v)} title={browserPanelMinimized ? 'Expand' : 'Minimize'} aria-label={browserPanelMinimized ? 'Expand' : 'Minimize'}>
                            {browserPanelMinimized ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>}
                          </button>
                          <span className="zolaid-workspace-header-divider" aria-hidden="true" />
                          <button type="button" className="zolaid-workspace-header-btn" title="Close" aria-label="Close" onClick={() => { setShowBrowserPanel(false); setBrowserPanelMinimized(false); setBrowserPanelFullscreen(false); setTaskPanelView('default'); }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                          </button>
                        </div>
                      </div>
                      <div className="zolaid-workspace-status-row">
                        <div className="zolaid-workspace-status-icon" aria-hidden="true">ðŸŒ</div>
                        <span className="zolaid-workspace-status-text" title="Browser Operator status">
                          {browserOperatorLiveState?.running ? 'Runningâ€¦' : (browserOperatorLastRun?.finalUrl || browserPreviewUrl) ? 'Ready' : 'Idle'}
                        </span>
                        <span className="zolaid-workspace-status-divider" />
                        <span className="zolaid-workspace-status-task" title={browserOperatorLiveState?.lastAction || browserPreviewUrl || browserOperatorLastRun?.summary || ''}>
                          {browserOperatorLiveState?.running
                            ? (browserOperatorLiveState.lastAction || 'â€¦')
                            : browserPreviewUrl
                              ? (browserPreviewUrl.length > 40 ? browserPreviewUrl.slice(0, 37) + 'â€¦' : browserPreviewUrl)
                              : (browserOperatorLastRun?.summary || 'Ask: "open google.com"')}
                        </span>
                      </div>
                      {!browserPanelMinimized && (
                        <>
                          <div className={`zolaid-workspace-main ${(browserPreviewUrl || browserOperatorLiveState?.screenshotBase64) ? 'has-content' : ''}`}>
                            {(browserPreviewUrl || browserOperatorLiveState?.screenshotBase64) ? (
                              <div className="browser-task-panel-body" style={{ width: '100%', height: '100%', minHeight: '400px', position: 'relative', background: '#1a1a1a' }}>
                                {browserOperatorLiveState?.screenshotBase64 ? (
                                  <>
                                    <img src={`data:image/png;base64,${browserOperatorLiveState.screenshotBase64}`} alt="Live sandbox browser" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                    {(browserOperatorLiveState.running || browserOperatorLiveState.lastAction) && (
                                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '6px 10px', background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 11 }}>
                                        {browserOperatorLiveState.running ? `Step ${browserOperatorLiveState.stepIndex || 0} â€” ${browserOperatorLiveState.lastAction || 'â€¦'}` : (browserOperatorLiveState.lastAction || browserOperatorLiveState.url || '')}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <SandboxBrowser url={browserPreviewUrl} onClose={() => { setShowBrowserPanel(false); setBrowserPanelMinimized(false); }} />
                                )}
                              </div>
                            ) : (
                              <div className="zolaid-workspace-idle">
                                <svg className="zolaid-workspace-idle-icon" width="160" height="160" viewBox="0 0 160 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                  <rect x="20" y="40" width="120" height="80" rx="4" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3"/>
                                  <rect x="28" y="48" width="104" height="64" rx="2" fill="currentColor" opacity="0.08"/>
                                  <path d="M60 100 L80 120 L100 100" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.4"/>
                                  <rect x="68" y="128" width="24" height="4" rx="1" fill="currentColor" opacity="0.2"/>
                                </svg>
                                <span className="zolaid-workspace-idle-text">Browser sandbox is idle</span>
                                <span className="zolaid-workspace-idle-hint">Ask in chat: &quot;open google.com&quot; or &quot;go to example.com&quot;</span>
                              </div>
                            )}
                          </div>
                          <div className="zolaid-workspace-taskbar">
                            <div className="zolaid-workspace-taskbar-content">
                              <div className="zolaid-workspace-taskbar-inner">
                                <svg className={`zolaid-workspace-taskbar-spinner ${browserOperatorLiveState?.running ? 'spinning' : ''}`} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                  <circle cx="6.99658" cy="6.99902" r="6.34375" stroke="currentColor" strokeWidth="1.3125" strokeDasharray="2.13 1.42"/>
                                </svg>
                                <div className="zolaid-workspace-taskbar-info">
                                  <div className={`zolaid-workspace-taskbar-title ${!browserPreviewUrl && !browserOperatorLiveState?.url ? 'waiting' : ''}`} title={browserOperatorLiveState?.url || browserPreviewUrl || 'Waiting for instructions'}>
                                    {(browserOperatorLiveState?.url || browserPreviewUrl) ? ((browserOperatorLiveState?.url || browserPreviewUrl).length > 45 ? (browserOperatorLiveState?.url || browserPreviewUrl).slice(0, 42) + '...' : (browserOperatorLiveState?.url || browserPreviewUrl)) : 'Waiting for instructions'}
                                  </div>
                                  <div className="zolaid-workspace-taskbar-subtitle">
                                    {browserOperatorLiveState?.running ? `Step ${browserOperatorLiveState.stepIndex ?? 0}` : browserPreviewUrl || browserOperatorLiveState?.url ? 'Viewing page' : 'Ask the AI to open a website'}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="zolaid-workspace-taskbar-steps">
                              {browserOperatorLiveState?.running && typeof browserOperatorLiveState.stepIndex === 'number'
                                ? `${browserOperatorLiveState.stepIndex + 1} / â€¦`
                                : browserOperatorLastRun?.steps?.length
                                  ? `${browserOperatorLastRun.steps.length} / ${browserOperatorLastRun.steps.length}`
                                  : 'â€”'}
                            </div>
                            <button type="button" className="zolaid-workspace-taskbar-chevron" aria-label="Expand task" title="Expand" onClick={cycleTaskPanelView}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
                {showImageEditorPanel && taskPanelView !== 'full' && (
                  <div
                    ref={imageEditorPanelRef}
                    className={`browser-task-panel ${imageEditorPanelMinimized ? 'minimized' : ''} ${taskPanelView === 'large' ? 'browser-task-panel-size-large' : ''}`}
                  >
                    <div className="browser-task-panel-header">
                      <span className="browser-task-panel-title">Image Editor</span>
                      <div className="browser-task-panel-actions">
                        <button
                          type="button"
                          className="browser-task-panel-btn"
                          onClick={cycleTaskPanelView}
                          title={taskPanelView === 'default' ? 'Larger' : taskPanelView === 'large' ? 'Full screen (right side)' : 'Default size'}
                          aria-label="Cycle size"
                        >
                          â›¶
                        </button>
                        <button
                          type="button"
                          className="browser-task-panel-btn"
                          onClick={() => setImageEditorPanelMinimized((v) => !v)}
                          title={imageEditorPanelMinimized ? 'Expand' : 'Minimize'}
                          aria-label={imageEditorPanelMinimized ? 'Expand' : 'Minimize'}
                        >
                          {imageEditorPanelMinimized ? 'â–¾' : 'â–´'}
                        </button>
                        <button
                          type="button"
                          className="browser-task-panel-btn close"
                          onClick={() => {
                            setShowImageEditorPanel(false);
                            setImageEditorPanelMinimized(false);
                            setTaskPanelView('default');
                          }}
                          title="Close"
                          aria-label="Close image editor panel"
                        >
                          âœ•
                        </button>
                      </div>
                    </div>
                    {!imageEditorPanelMinimized && (
                      <div className="browser-task-panel-body browser-task-panel-body-editor">
                        <ProImageEditorV2
                          isOpen={true}
                          embedded
                          onClose={() => {
                            setShowImageEditorPanel(false);
                            setImageEditorPanelMinimized(false);
                          }}
                          onSwitchToVideoEditor={() => {
                            setShowImageEditorPanel(false);
                            setShowVideoEditorPanel(true);
                            setVideoEditorPanelMinimized(false);
                          }}
                          onSwitchToChat={() => setShowImageEditorPanel(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
                {showPhotoshopEditorPanel && taskPanelView !== 'full' && (
                  <div
                    ref={photoshopPanelRef}
                    className={`browser-task-panel browser-task-panel-photoshop ${photoshopEditorPanelMinimized ? 'minimized' : ''} ${taskPanelView === 'large' ? 'browser-task-panel-size-large' : ''}`}
                  >
                    <div className="browser-task-panel-header">
                      <span className="browser-task-panel-title">Photoshop</span>
                      <div className="browser-task-panel-actions">
                        <button
                          type="button"
                          className="browser-task-panel-btn"
                          onClick={cycleTaskPanelView}
                          title={taskPanelView === 'default' ? 'Larger' : taskPanelView === 'large' ? 'Full screen (right side)' : 'Default size'}
                          aria-label="Cycle size"
                        >
                          â›¶
                        </button>
                        <button
                          type="button"
                          className="browser-task-panel-btn"
                          onClick={() => setPhotoshopEditorPanelMinimized((v) => !v)}
                          title={photoshopEditorPanelMinimized ? 'Expand' : 'Minimize'}
                          aria-label={photoshopEditorPanelMinimized ? 'Expand' : 'Minimize'}
                        >
                          {photoshopEditorPanelMinimized ? 'â–¾' : 'â–´'}
                        </button>
                        <button
                          type="button"
                          className="browser-task-panel-btn close"
                          onClick={() => {
                            setShowPhotoshopEditorPanel(false);
                            setPhotoshopEditorPanelMinimized(false);
                            setTaskPanelView('default');
                          }}
                          title="Close"
                          aria-label="Close Photoshop panel"
                        >
                          âœ•
                        </button>
                      </div>
                    </div>
                    {!photoshopEditorPanelMinimized && (
                      <div className="browser-task-panel-body browser-task-panel-body-editor">
                        <ProImageEditorV2
                          isOpen={true}
                          embedded
                          embeddedWorkspaceTitle="Adobe Photoshop"
                          defaultImageEditModel="photoshop"
                          onClose={() => {
                            setShowPhotoshopEditorPanel(false);
                            setPhotoshopEditorPanelMinimized(false);
                          }}
                          onSwitchToVideoEditor={() => {
                            setShowPhotoshopEditorPanel(false);
                            setShowVideoEditorPanel(true);
                            setVideoEditorPanelMinimized(false);
                            setTaskPanelView('default');
                          }}
                          onSwitchToChat={() => setShowPhotoshopEditorPanel(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
                {showVideoEditorPanel && taskPanelView !== 'full' && (
                  <div className={`browser-task-panel ${videoEditorPanelMinimized ? 'minimized' : ''} ${taskPanelView === 'large' ? 'browser-task-panel-size-large' : ''}`}>
                    <div className="browser-task-panel-header">
                      <span className="browser-task-panel-title">Video Editor</span>
                      <div className="browser-task-panel-actions">
                        <button
                          type="button"
                          className="browser-task-panel-btn"
                          onClick={cycleTaskPanelView}
                          title={taskPanelView === 'default' ? 'Larger' : taskPanelView === 'large' ? 'Full screen (right side)' : 'Default size'}
                          aria-label="Cycle size"
                        >
                          â›¶
                        </button>
                        <button
                          type="button"
                          className="browser-task-panel-btn"
                          onClick={() => setVideoEditorPanelMinimized((v) => !v)}
                          title={videoEditorPanelMinimized ? 'Expand' : 'Minimize'}
                          aria-label={videoEditorPanelMinimized ? 'Expand' : 'Minimize'}
                        >
                          {videoEditorPanelMinimized ? 'â–¾' : 'â–´'}
                        </button>
                        <button
                          type="button"
                          className="browser-task-panel-btn close"
                          onClick={() => {
                            setShowVideoEditorPanel(false);
                            setVideoEditorPanelMinimized(false);
                            setTaskPanelView('default');
                          }}
                          title="Close"
                          aria-label="Close video editor panel"
                        >
                          âœ•
                        </button>
                      </div>
                    </div>
                    {!videoEditorPanelMinimized && (
                      <div className="browser-task-panel-body browser-task-panel-body-editor">
                        <ProVideoEditorV2
                          isOpen={true}
                          embedded
                          openScriptToVideo={openVideoEditorForScriptToVideo}
                          onScriptToVideoOpened={() => setOpenVideoEditorForScriptToVideo(false)}
                          onClose={() => {
                            setShowVideoEditorPanel(false);
                            setVideoEditorPanelMinimized(false);
                          }}
                          onSwitchToImageEditor={() => {
                            setShowVideoEditorPanel(false);
                            setTaskPanelView('default');
                            setShowPhotoshopEditorPanel(false);
                            setShowImageEditorPanel(true);
                            setImageEditorPanelMinimized(false);
                          }}
                          onSwitchToChat={() => setShowVideoEditorPanel(false)}
                        />
                      </div>
                    )}
                  </div>
                )}
                <div className="composer-container">
                  {showGmailPanel && (
                    <div className="gmail-panel" role="region" aria-label="Gmail organizer">
                      <div className="gmail-panel-head">
                        <div className="gmail-panel-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <GmailLogo /> Gmail
                        </div>
                        <button type="button" className="gmail-panel-close" onClick={() => setShowGmailPanel(false)} aria-label="Close Gmail panel">
                          <FaTimes />
                        </button>
                      </div>
                      <div className="gmail-panel-actions">
                        <button type="button" className="gmail-panel-btn" onClick={connectGmail} disabled={gmailConnected}>
                          {gmailConnected ? 'Connected' : 'Connect Gmail'}
                        </button>
                        <button
                          type="button"
                          className="gmail-panel-btn"
                          onClick={() => window.open('https://mail.google.com/', '_blank', 'noopener,noreferrer')}
                        >
                          Open Gmail
                        </button>
                        <button
                          type="button"
                          className="gmail-panel-btn"
                          onClick={async () => {
                            await fetchGmailStatus().catch(() => false);
                            await fetchGmailMessages();
                          }}
                          disabled={gmailLoading}
                        >
                          {gmailLoading ? 'Refreshingâ€¦' : 'Refresh'}
                        </button>
                        <button
                          type="button"
                          className="gmail-panel-btn primary"
                          onClick={organizeGmailMessages}
                          disabled={gmailLoading || gmailOrganizing}
                        >
                          Organize (up to 7 labels)
                        </button>
                        <div className={`gmail-panel-status ${gmailConnected ? 'ok' : 'warn'}`}>
                          {gmailConnected ? 'Connected' : 'Not connected'}
                        </div>
                      </div>

                      <div className="gmail-panel-meta">
                        <div className="gmail-panel-hint">
                          {gmailConnected
                            ? (gmailLastRefresh ? `Last refreshed: ${new Date(gmailLastRefresh).toLocaleString()}` : 'Not refreshed yet')
                            : 'Connect Gmail to load your recent emails.'}
                        </div>
                      </div>

                      {gmailMessages?.length > 0 && (
                        <div className="gmail-panel-summary">
                          {Object.entries(getGmailLabelSummary(gmailMessages)).map(([label, count]) => (
                            <div key={label} className="gmail-panel-chip">
                              <span className="gmail-panel-chip-name">{label}</span>
                              <span className="gmail-panel-chip-count">{count}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {!!gmailError && <div className="gmail-panel-error">{gmailError}</div>}
                      {gmailOrganizing && (
                        <div className="gmail-panel-progress">
                          Labelingâ€¦ {gmailProgress.done}/{gmailProgress.total}
                          <div className="gmail-panel-progressbar" aria-hidden="true">
                            <div
                              className="gmail-panel-progressbar-fill"
                              style={{
                                width: gmailProgress.total ? `${Math.min(100, Math.round((gmailProgress.done / gmailProgress.total) * 100))}%` : '0%',
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="gmail-panel-list">
                        {gmailLoading && <div className="gmail-panel-loading">Loading emailsâ€¦</div>}
                        {(gmailMessages || []).slice(0, 15).map((m) => (
                          <div key={m.id} className="gmail-panel-item">
                            <div className="gmail-panel-item-top">
                              <div className="gmail-panel-subject">{m.subject || '(No subject)'}</div>
                              <div className="gmail-panel-label">{classifyGmailMessage(m)}</div>
                            </div>
                            <div className="gmail-panel-from">{m.from}</div>
                            {!!m.snippet && <div className="gmail-panel-snippet">{m.snippet}</div>}
                          </div>
                        ))}
                        {!gmailLoading && (!gmailMessages || gmailMessages.length === 0) && (
                          <div className="gmail-panel-empty">
                            {gmailConnected ? 'No messages loaded yet. Click â€œRefreshâ€.' : 'Not connected yet. Click â€œConnect Gmailâ€.'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <form className="composer-form" onSubmit={(e) => { e.preventDefault(); if (!replyInProgress && input.trim()) sendMessage(); }}>
                    {dockedComputer ? (
                      <ComposerActivityDock
                        active={dockedComputer.activity === 'live'}
                        startedAt={dockedComputer.startedAt}
                        workedMs={dockedComputer.workedMs}
                        steps={dockedComputer.steps}
                        liveWriting={liveWritingFromBuild(siteBuild, dockedComputer.steps)}
                        onOpenComputer={() => revealComputer(dockedComputer)}
                      />
                    ) : null}
                    <div 
                      className={`composer-box${dockedComputer ? ' composer-box--attached' : ''}${listening ? ' composer-box--dictation' : ''} ${isDragging ? 'dragging' : ''}`}
                      ref={composerBoxRef}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <>
                      {/* Leading area - Plus button */}
                      <div className="composer-leading" ref={searchDropdownRef} style={listening ? { display: 'none' } : undefined}>
                        <div className="plus-button-wrapper">
                          <button
                            ref={plusButtonRef}
                            type="button"
                            className="composer-btn plus-btn"
                            onClick={() => {
                              setShowSearchGlass(prev => !prev);
                            }}
                            title="Add files and more"
                            aria-label="Add files and more"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                          </button>
                        </div>
                        {showSearchGlass && (
                          <>
                            <div className="settings-menu-overlay" onClick={() => setShowSearchGlass(false)}></div>
                            <div className="search-dropdown search-glass plus-composer-menu">
                              <div className="search-dropdown-content">
                                {/* Plus Menu Options */}
                                <div className="plus-menu-options">
                                  <div className="plus-menu-item" onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    try {
                                      if (fileInputRef.current) {
                                        console.log('Opening file dialog...');
                                        fileInputRef.current.click();
                                      } else {
                                        console.error('File input ref is not available');
                                        alert('File upload is not available. Please refresh the page.');
                                      }
                                    } catch (err) {
                                      console.error('Error opening file dialog:', err);
                                      alert('Error opening file dialog. Please try again.');
                                    }
                                    setShowSearchGlass(false);
                                  }}>
                                    <svg className="plus-menu-glyph" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                      <path d="M3.5 7.5V6.2A1.7 1.7 0 0 1 5.2 4.5h4.1l1.6 1.8h7.9a1.7 1.7 0 0 1 1.7 1.7v9.3a1.7 1.7 0 0 1-1.7 1.7H5.2a1.7 1.7 0 0 1-1.7-1.7V7.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                                      <path d="M12 16.2V10.6M9.6 12.7 12 10.4l2.4 2.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span>Upload Files</span>
                                  </div>
                                  {unifiedChatbotPlusTools.map((tool) => {
                                    const Icon = tool.icon && UnifiedIcons[tool.icon] ? UnifiedIcons[tool.icon] : null;
                                    const emoji = getUnifiedEmoji(tool.icon);
                                    return (
                                      <div
                                        key={tool.key}
                                        className="plus-menu-item"
                                        onClick={async () => {
                                          setShowSearchGlass(false);
                                          await handleUnifiedChatbotToolClick(tool);
                                        }}
                                      >
                                        {Icon ? <Icon /> : null}
                                        {!Icon && emoji ? `${emoji} ` : null}
                                        <span>{tool.label}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Selected Files Preview */}
                      {selectedFiles.length > 0 && (
                        <div className="selected-files-preview">
                          {selectedFiles.map((file, idx) => (
                            <div key={idx} className="selected-file-item">
                              <span className="file-name">{file.name}</span>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => {
                                  setSelectedFiles(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                Ã—
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Hidden file input MUST stay mounted (otherwise onChange can be lost when the menu closes) */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={(e) => {
                          try {
                            const files = Array.from(e.target.files || []);
                            console.log("onChange triggered, files:", files.length);
                            if (files.length > 0) {
                              console.log("Files selected:", files.length, "files:", files.map((f) => f.name));
                              setSelectedFiles((prev) => {
                                const newFiles = [...prev, ...files];
                                console.log("Total files now:", newFiles.length);
                                return newFiles;
                              });
                              if (e.target) e.target.value = "";
                            } else {
                              console.log("No files selected");
                            }
                          } catch (err) {
                            console.error("Error handling file selection:", err);
                            alert("Error selecting files: " + err.message);
                          }
                        }}
                        onClick={(e) => {
                          // Reset value to allow selecting the same file again
                          e.target.value = "";
                        }}
                        accept="image/*,video/*,.pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
                      />

                      {/* Primary area - Input */}
                      <div className="composer-primary">
                        <div className="composer-textarea-wrapper">
                          <textarea
                            className="composer-textarea"
            value={input}
            onChange={(e) => {
              if (listening) return; // voice owns the typing box while dictating
              setInput(e.target.value);
            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (listening) {
                                  if (input.trim()) sendDictation();
                                  return;
                                }
                                if (input.trim()) sendMessage();
                              }
                            }}
                            placeholder={listening ? 'Listening… speak now' : (socraticMode ? t("shareThoughts", { defaultValue: "Share your thoughts..." }) : t("askPlaceholder"))}
                            readOnly={listening || dictationBusy}
                            rows="1"
                            ref={(textarea) => {
                              if (textarea) {
                                textarea.style.height = "auto";
                                textarea.style.height = Math.min(textarea.scrollHeight, 180) + "px";
                              }
                            }}
                            onInput={(e) => {
                              const textarea = e.target;
                              textarea.style.height = "auto";
                              const newHeight = Math.min(textarea.scrollHeight, 180);
                              textarea.style.height = newHeight + "px";
                              
                              // Update parent container height
                              const composerBox = textarea.closest('.composer-box');
                              if (composerBox) {
                                const chips = composerBox.querySelector(".selected-files-preview");
                                const chipsH = chips ? chips.offsetHeight : 0;
                                const padding = 96; // open field + bottom control row
                                const desired = textarea.scrollHeight + chipsH + padding;
                                composerBox.style.maxHeight = Math.min(desired, 300) + "px";
                              }
                            }}
                          />
        </div>
      </div>

                      {/* Trailing: while dictating show wave bar; otherwise mic / send / call */}
                      {listening ? (
                        <div className="composer-dictation-bar" role="group" aria-label="Voice dictation">
                          <button
                            type="button"
                            className="composer-dictation-cancel"
                            aria-label="Cancel dictation"
                            title="Cancel"
                            onClick={(event) => {
                              event.preventDefault();
                              cancelDictation();
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                          <div className="composer-dictation-wave" aria-hidden="true">
                            {dictationLevels.map((level, index) => (
                              <span
                                key={`dictation-bar-${index}`}
                                className="composer-dictation-wave-bar"
                                style={{ transform: `scaleY(${Math.max(0.08, level)})` }}
                              />
                            ))}
                          </div>
                          <div className="composer-dictation-actions">
                            <button
                              type="button"
                              className="composer-dictation-stop"
                              aria-label="Stop listening"
                              title="Stop"
                              onClick={(event) => {
                                event.preventDefault();
                                finishDictationKeepText();
                              }}
                            >
                              <span className="composer-dictation-stop-square" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="composer-dictation-send"
                              aria-label="Send voice message"
                              title="Send"
                              disabled={dictationBusy || replyInProgress}
                              onClick={(event) => {
                                event.preventDefault();
                                sendDictation();
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="12" y1="19" x2="12" y2="5" />
                                <polyline points="5 12 12 5 19 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                      <div className="composer-trailing">
                        <div className="composer-actions">
                          <button
                            type="button"
                            className={`composer-btn voice-btn${listening ? ' voice-btn--listening' : ''}`}
                            title={voiceHint || 'Voice assistance'}
                            onClick={startVoiceInput}
                            aria-label="Voice assistance"
                            aria-pressed={listening}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                              <line x1="12" y1="19" x2="12" y2="23" />
                              <line x1="8" y1="23" x2="16" y2="23" />
                            </svg>
                          </button>
                          {replyInProgress ? (
                            <button
                              type="button"
                              className="composer-submit-btn"
                              aria-label="Stop"
                              title="Stop"
                              onClick={(event) => {
                                event.preventDefault();
                                stopActiveReply();
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                              </svg>
                            </button>
                          ) : input.trim() ? (
                            <button
                              type="button"
                              className="composer-submit-btn"
                              aria-label="Send message"
                              title="Send message"
                              onClick={(event) => {
                                event.preventDefault();
                                if (input.trim()) sendMessage();
                              }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="12" y1="19" x2="12" y2="5"></line>
                                <polyline points="5 12 12 5 19 12"></polyline>
                              </svg>
                            </button>
                          ) : (
                            <div className="voice-call-wrap" ref={voiceCallWrapRef}>
                              <button
                                type="button"
                                className={`composer-btn voice-call-btn${voiceCallActive ? ' voice-call-btn--active' : ''}`}
                                title={voiceCallActive ? 'End voice call' : 'Voice call'}
                                onClick={toggleVoiceCall}
                                aria-label="Voice call"
                                aria-pressed={voiceCallActive}
                              >
                                <span className="voice-wave" aria-hidden="true">
                                  <span className="voice-wave-bar" />
                                  <span className="voice-wave-bar" />
                                  <span className="voice-wave-bar" />
                                  <span className="voice-wave-bar" />
                                </span>
                              </button>
                              <button
                                type="button"
                                className="voice-call-chevron"
                                title="Choose voice"
                                aria-label="Choose voice"
                                aria-expanded={voicePresetMenuOpen}
                                aria-haspopup="menu"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setVoicePresetMenuOpen((open) => !open);
                                }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                  <polyline points="6 9 12 15 18 9" />
                                </svg>
                              </button>
                              {voicePresetMenuOpen ? (
                                <div className="voice-call-menu" role="menu" aria-label="Voice">
                                  {[
                                    { id: 'woman', label: 'Woman' },
                                    { id: 'man', label: 'Man' },
                                  ].map((preset) => (
                                    <button
                                      key={preset.id}
                                      type="button"
                                      role="menuitemradio"
                                      aria-checked={voicePreset === preset.id}
                                      className={`voice-call-menu__item${voicePreset === preset.id ? ' is-selected' : ''}`}
                                      onClick={() => selectVoicePreset(preset.id)}
                                    >
                                      {preset.label}
                                    </button>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      </div>
                      )}
                      </>
                    </div>
                    {voiceHint && !listening && !dictationBusy ? (
                      <p className="composer-voice-hint" role="status" aria-live="polite">{voiceHint}</p>
                    ) : null}
                    <p className="composer-disclaimer">Zolaid is an AI agent and can make mistakes. Please double-check before use.</p>
                  </form>
                </div>
            </div>
          </div>
        </div>
        </div>
        </div>
        {showSandbox && (
          <div className="chat-panel-preview">
            <AgentSandbox
              minimized={sandboxMinimized}
              size="full"
              fullscreen={sandboxFullscreen}
              onMinimize={() => setSandboxMinimized((v) => !v)}
              onClose={closeSandbox}
              onCycleSize={cycleTaskPanelView}
              onToggleFullscreen={() => setSandboxFullscreen((v) => !v)}
              previewKind={sandboxPreviewKind}
              videoEdit={videoEdit}
              processLog={sandboxProcessLog}
              resultImageUrl={null}
              sitePreview={
                zolaidArtifacts
                  ? {
                      html: zolaidArtifacts.html || '',
                      css: zolaidArtifacts.css || '',
                      js: zolaidArtifacts.js || '',
                    }
                  : null
              }
              browserPreviewUrl={browserPreviewUrl}
              browserOperatorLiveState={browserOperatorLiveState}
              slides={computerSlides}
              siteBuild={siteBuild}
              settingsRequest={sandboxSettingsRequest}
              onOpenChatUsage={openChatSettingsUsage}
              onComputerRequestFix={({ lastError, errorCount, preview, recentEvents }) => {
                const payload = { errorCount, lastError, preview, recentEvents };
                const prompt = [
                  "The preview runtime shows errors. Fix the code.",
                  "Return ONE JSON object only.",
                  "Return mode=render with corrected artifacts {html, css, js}.",
                  "Keep the UI the same unless needed to fix errors.",
                  "",
                  "Context (JSON):",
                  JSON.stringify(payload),
                ].join("\n");
                sendMessage(prompt, null);
                openSandbox({ kind: 'site', log: 'Fixing preview errors' });
                setShowZolaidComputer(true);
              }}
              gmail={{
                GmailLogo,
                gmailConnected,
                gmailLoading,
                gmailOrganizing,
                gmailProgress,
                gmailError,
                gmailMessages,
                gmailLastRefresh,
                onConnect: connectGmail,
                onRefresh: fetchGmailMessages,
                onOrganize: organizeGmailMessages,
              }}
              ideaGeneration={{
                active: socraticMode,
                stageIndex: socraticStage,
                stages: socraticQuestions,
                businessIdea,
                onRestart: startSocraticMethod,
              }}
              promptPresets={{
                onSelect: (prompt) => {
                  setInput(prompt);
                  setShowPromptPresets(false);
                },
              }}
              camera={
                showCamera
                  ? {
                      videoRef,
                      canvasRef,
                      onCapture: captureImage,
                      onClose: closeCamera,
                    }
                  : null
              }
            />
          </div>
        )}
        {(showBrowserPanel || showImageEditorPanel || showPhotoshopEditorPanel || showVideoEditorPanel) &&
          taskPanelView === 'full' && (
          <div className="chat-panel-right">
            {showBrowserPanel && (
              <div className="zolaid-workspace browser-task-panel browser-task-panel-full">
                <div className="zolaid-workspace-inner" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <div className="zolaid-workspace-header">
                    <span className="zolaid-workspace-title">Browser Operator</span>
                    <div className="zolaid-workspace-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button
                        type="button"
                        className="zolaid-workspace-header-btn"
                        onClick={() => setBrowserPanelFullscreen((v) => !v)}
                        title={browserPanelFullscreen ? 'Exit full screen' : 'Full screen'}
                        aria-label={browserPanelFullscreen ? 'Exit full screen' : 'Full screen'}
                      >
                        â›¶
                      </button>
                      <button type="button" className="zolaid-workspace-header-btn" onClick={cycleTaskPanelView} title="Exit full screen" aria-label="Exit full screen">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 14" fill="none" width="16" height="16"><path d="M3.75 3.85C3.75 3.51863 4.01863 3.25 4.35 3.25L13.65 3.25C13.9814 3.25 14.25 3.51863 14.25 3.85L14.25 10.15C14.25 10.4814 13.9814 10.75 13.65 10.75L4.35 10.75C4.01863 10.75 3.75 10.4814 3.75 10.15L3.75 3.85Z" fill="currentColor"/><path d="M15.75 2.5C15.75 2.08579 15.4142 1.75 15 1.75L3 1.75C2.58579 1.75 2.25 2.08579 2.25 2.5L2.25 11.5C2.25 11.9142 2.58579 12.25 3 12.25L15 12.25C15.4142 12.25 15.75 11.9142 15.75 11.5L15.75 2.5Z" fill="currentColor"/></svg>
                      </button>
                      <span className="zolaid-workspace-header-divider" />
                      <button type="button" className="zolaid-workspace-header-btn close" onClick={() => { setShowBrowserPanel(false); setBrowserPanelMinimized(false); setBrowserPanelFullscreen(false); setTaskPanelView('default'); }} title="Close" aria-label="Close">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                      </button>
                    </div>
                  </div>
                  <div className="zolaid-workspace-status-row">
                    <div className="zolaid-workspace-status-icon">ðŸŒ</div>
                    <span className="zolaid-workspace-status-text">Zolaid is inactive</span>
                    <span className="zolaid-workspace-status-divider" />
                    <span className="zolaid-workspace-status-task">Waiting for instructions</span>
                  </div>
                  <div className="zolaid-workspace-main has-content" style={{ flex: 1, minHeight: 0 }}>
                    <div className="browser-task-panel-body" style={{ width: '100%', height: '100%', minHeight: 0, position: 'relative', background: '#1a1a1a', display: 'flex', flexDirection: 'column' }}>
                      {browserOperatorLiveState?.screenshotBase64 ? (
                        <>
                          <img
                            src={`data:image/png;base64,${browserOperatorLiveState.screenshotBase64}`}
                            alt="Live sandbox browser"
                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                          />
                          {(browserOperatorLiveState.running || browserOperatorLiveState.lastAction) && (
                            <div className="browser-operator-live-overlay" style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '8px 12px', background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 12 }}>
                              {browserOperatorLiveState.running ? (
                                <span>ðŸ”„ Step {browserOperatorLiveState.stepIndex || 0} â€” {browserOperatorLiveState.lastAction || 'â€¦'}</span>
                              ) : (
                                <span>{browserOperatorLiveState.lastAction || browserOperatorLiveState.url || 'Sandbox view'}</span>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <SandboxBrowser url={browserPreviewUrl} onClose={() => { setShowBrowserPanel(false); setBrowserPanelMinimized(false); setTaskPanelView('default'); }} />
                      )}
                    </div>
                  </div>
                  <div className="zolaid-workspace-taskbar">
                    <div className="zolaid-workspace-taskbar-content">
                      <div className="zolaid-workspace-taskbar-inner">
                        <svg className="zolaid-workspace-taskbar-spinner" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none"><circle cx="6.99658" cy="6.99902" r="6.34375" stroke="currentColor" strokeWidth="1.3125" strokeDasharray="2.13 1.42"/></svg>
                        <div className="zolaid-workspace-taskbar-info">
                          <div className={`zolaid-workspace-taskbar-title ${!browserPreviewUrl ? 'waiting' : ''}`}>{browserPreviewUrl ? (browserPreviewUrl.length > 50 ? browserPreviewUrl.slice(0, 47) + '...' : browserPreviewUrl) : 'Waiting for instructions'}</div>
                          <div className="zolaid-workspace-taskbar-subtitle">{browserPreviewUrl ? 'Viewing page' : 'Ask the AI to open a website'}</div>
                        </div>
                      </div>
                    </div>
                            <div className="zolaid-workspace-taskbar-steps">
                              {browserOperatorLiveState?.running && typeof browserOperatorLiveState.stepIndex === 'number'
                                ? `${browserOperatorLiveState.stepIndex + 1} / â€¦`
                                : browserOperatorLastRun?.steps?.length
                                  ? `${browserOperatorLastRun.steps.length} / ${browserOperatorLastRun.steps.length}`
                                  : 'â€”'}
                            </div>
                    <button type="button" className="zolaid-workspace-taskbar-chevron" aria-label="Expand task" title="Cycle panel size" onClick={cycleTaskPanelView}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg></button>
                  </div>
                </div>
              </div>
            )}
            {showImageEditorPanel && (
              <div ref={imageEditorPanelRef} className="browser-task-panel browser-task-panel-full">
                <div className="browser-task-panel-header">
                  <span className="browser-task-panel-title">Image Editor</span>
                  <div className="browser-task-panel-actions">
                    <button type="button" className="browser-task-panel-btn" onClick={cycleTaskPanelView} title="Exit full screen" aria-label="Cycle size">â›¶</button>
                    <button type="button" className="browser-task-panel-btn close" onClick={() => { setShowImageEditorPanel(false); setImageEditorPanelMinimized(false); setTaskPanelView('default'); }} title="Close" aria-label="Close">âœ•</button>
                  </div>
                </div>
                <div className="browser-task-panel-body browser-task-panel-body-editor">
                  <ProImageEditorV2 isOpen={true} embedded onClose={() => { setShowImageEditorPanel(false); setImageEditorPanelMinimized(false); setTaskPanelView('default'); }} onSwitchToVideoEditor={() => { setShowImageEditorPanel(false); setShowVideoEditorPanel(true); setVideoEditorPanelMinimized(false); setTaskPanelView('default'); }} onSwitchToChat={() => setShowImageEditorPanel(false)} />
                </div>
              </div>
            )}
            {showPhotoshopEditorPanel && (
              <div ref={photoshopPanelRef} className="browser-task-panel browser-task-panel-full browser-task-panel-photoshop">
                <div className="browser-task-panel-header">
                  <span className="browser-task-panel-title">Photoshop</span>
                  <div className="browser-task-panel-actions">
                    <button type="button" className="browser-task-panel-btn" onClick={cycleTaskPanelView} title="Exit full screen" aria-label="Cycle size">â›¶</button>
                    <button type="button" className="browser-task-panel-btn close" onClick={() => { setShowPhotoshopEditorPanel(false); setPhotoshopEditorPanelMinimized(false); setTaskPanelView('default'); }} title="Close" aria-label="Close">âœ•</button>
                  </div>
                </div>
                <div className="browser-task-panel-body browser-task-panel-body-editor">
                  <ProImageEditorV2
                    isOpen={true}
                    embedded
                    embeddedWorkspaceTitle="Adobe Photoshop"
                    defaultImageEditModel="photoshop"
                    onClose={() => { setShowPhotoshopEditorPanel(false); setPhotoshopEditorPanelMinimized(false); setTaskPanelView('default'); }}
                    onSwitchToVideoEditor={() => { setShowPhotoshopEditorPanel(false); setShowVideoEditorPanel(true); setVideoEditorPanelMinimized(false); setTaskPanelView('default'); }}
                    onSwitchToChat={() => setShowPhotoshopEditorPanel(false)}
                  />
                </div>
              </div>
            )}
            {showVideoEditorPanel && (
              <div className="browser-task-panel browser-task-panel-full">
                <div className="browser-task-panel-header">
                  <span className="browser-task-panel-title">Video Editor</span>
                  <div className="browser-task-panel-actions">
                    <button type="button" className="browser-task-panel-btn" onClick={cycleTaskPanelView} title="Exit full screen" aria-label="Cycle size">â›¶</button>
                    <button type="button" className="browser-task-panel-btn close" onClick={() => { setShowVideoEditorPanel(false); setVideoEditorPanelMinimized(false); setTaskPanelView('default'); }} title="Close" aria-label="Close">âœ•</button>
                  </div>
                </div>
                <div className="browser-task-panel-body browser-task-panel-body-editor">
                  <ProVideoEditorV2 isOpen={true} embedded openScriptToVideo={openVideoEditorForScriptToVideo} onScriptToVideoOpened={() => setOpenVideoEditorForScriptToVideo(false)} onClose={() => { setShowVideoEditorPanel(false); setVideoEditorPanelMinimized(false); setTaskPanelView('default'); }} onSwitchToImageEditor={() => { setShowVideoEditorPanel(false); setShowPhotoshopEditorPanel(false); setShowImageEditorPanel(true); setImageEditorPanelMinimized(false); setTaskPanelView('default'); }} onSwitchToChat={() => setShowVideoEditorPanel(false)} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CAMERA MODAL */}
      {showCamera && (
        <div className="camera-modal">
          <div className="camera-container">
            <div className="camera-header">
              <h3>ðŸ“· Take a Photo</h3>
              <button className="close-camera" onClick={closeCamera}>
                âœ•
              </button>
            </div>
            <video ref={videoRef} autoPlay playsInline className="camera-video"></video>
            <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
            <div className="camera-controls">
              <button className="capture-btn" onClick={captureImage}>
                ðŸ“¸ Capture
              </button>
              <button className="cancel-btn" onClick={closeCamera}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IMAGE EDITOR MODAL */}
      {showImageEditor && (
        <div className="camera-modal">
          <ImageComputer isOpen={true} onClose={() => setShowImageEditor(false)} />
        </div>
      )}

      {/* VIDEO EDITOR MODAL */}
      {showVideoEditor && (
        <div className="camera-modal">
          <VideoComputer isOpen={true} onClose={() => setShowVideoEditor(false)} />
        </div>
      )}

      {/* PRO VIDEO EDITOR MODAL */}
      {showProVideoEditor && (
        <ProVideoEditorV2
          isOpen={true}
          onClose={() => setShowProVideoEditor(false)}
          onSwitchToImageEditor={() => {
            setShowProVideoEditor(false);
            setShowProImageEditor(true);
          }}
          onSwitchToChat={() => {
            setShowProVideoEditor(false);
          }}
        />
      )}

      {/* PRO IMAGE EDITOR MODAL */}
      {showProImageEditor && (
        <ProImageEditorV2
          isOpen={true}
          onClose={() => setShowProImageEditor(false)}
          onSwitchToVideoEditor={() => {
            setShowProImageEditor(false);
            setShowProVideoEditor(true);
          }}
          onSwitchToChat={() => {
            setShowProImageEditor(false);
          }}
        />
      )}

      {/* WEBSITE BUILDER MODAL */}
      {showWebsiteBuilder && (
        <div className="camera-modal" style={{ zIndex: 10000 }}>
          <div className="camera-container" style={{ maxWidth: "95%", width: "95%", maxHeight: "95vh", overflowY: "auto" }}>
            <div className="camera-header">
              <h3>ðŸŒ Website Builder</h3>
              <button className="close-camera" onClick={() => setShowWebsiteBuilder(false)}>
                âœ•
              </button>
            </div>
            <WebsiteBuilder initialPrompt={websiteBuilderSeed} />
          </div>
        </div>
      )}

      {/* Credit Top-Up Modal */}
      {showCreditTopUp && (
        <CreditTopUp
          onClose={() => setShowCreditTopUp(false)}
          onSuccess={() => {
            setShowCreditTopUp(false);
            // Refresh usage meter if needed
          }}
        />
      )}

      {/* Browser Operator (Website) */}
      {showBrowserOperator && (
        <div className="camera-modal" style={{ zIndex: 10000 }}>
          <div className="camera-container" style={{ maxWidth: "95%", width: "95%", maxHeight: "95vh", overflowY: "auto" }}>
            <div className="camera-header">
              <h3>ðŸŒ Website</h3>
              <button className="close-camera" onClick={() => setShowBrowserOperator(false)}>
                âœ•
              </button>
            </div>
            <SandboxBrowser url={browserPreviewUrl} onClose={() => setShowBrowserOperator(false)} />
          </div>
        </div>
      )}

      {/* AI Browser Agent - Human-like browser automation */}
      {showAIBrowserAgent && (
        <AIBrowserAgent
          isOpen={showAIBrowserAgent}
          onClose={() => {
            setShowAIBrowserAgent(false);
            setBrowserAgentTask('');
          }}
          initialUrl={browserPreviewUrl}
          initialTask={browserAgentTask}
          onTaskResult={({ message }) => {
            const id = pendingBrowserResultMessageIdRef.current;
            if (id != null) {
              setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, text: message } : m)));
              pendingBrowserResultMessageIdRef.current = null;
            }
          }}
        />
      )}

      {/* Autonomous Agent (runs behind the scenes â€“ hidden from view) */}
      {showAutonomousAgent && (
        <div className="autonomous-agent-behind-scenes" aria-hidden="true">
          <div className="camera-modal" style={{ zIndex: 10000 }}>
            <div className="camera-container" style={{ maxWidth: "800px", width: "90%", maxHeight: "90vh", overflowY: "auto" }}>
              <div className="camera-header">
                <h3>ðŸ¤– Autonomous Agent</h3>
                <button className="close-camera" onClick={() => {
                  setShowAutonomousAgent(false);
                  setAutonomousAgentActive(false);
                  setAutonomousAgentTask(null);
                }}>
                  âœ•
                </button>
              </div>
              <AutonomousAgent
                task={autonomousAgentTask}
              isActive={autonomousAgentActive}
              browserEnabled={browserOperatorEnabled}
              onBrowserAction={(action) => {
                if (action?.type === 'navigate' && action?.url) {
                  setBrowserPreviewUrl(action.url);
                  setShowBrowserPanel(true);
                  try {
                    if (localStorage.getItem('zolaid-operator-stealth') === 'true') {
                      setBrowserPanelMinimized(true);
                    } else {
                      setBrowserPanelMinimized(false);
                    }
                  } catch (_) {
                    setBrowserPanelMinimized(false);
                  }
                }
              }}
              onConfirmRequired={async (action, label) => {
                return new Promise((resolve) => {
                  setOperatorConfirmPending({ label, resolve });
                });
              }}
              onTakeoverRequired={async (fieldLabel) => {
                setOperatorTakeoverInputValue('');
                return new Promise((resolve) => {
                  setOperatorTakeoverPending({
                    fieldLabel,
                    resolve: (value) => {
                      setOperatorTakeoverPending(null);
                      resolve(value || '');
                    },
                  });
                });
              }}
              onComplete={(result) => {
                if (result.success) {
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: Date.now(),
                      sender: 'bot',
                      text: 'âœ… Autonomous Agent Task Completed!\n\n' +
                            'Recent Actions:\n' +
                            result.logs.slice(-3).map(log => `â€¢ ${log.message}`).join('\n')
                    }
                  ]);
                } else {
                  setMessages((prev) => [
                    ...prev,
                    {
                      id: Date.now(),
                      sender: 'bot',
                      text: 'âŒ Autonomous Agent Task Failed\n\n' +
                            'Error: ' + result.error +
                            '\n\nThe agent will learn from this experience and adjust future planning.'
                    }
                  ]);
                }
                setAutonomousAgentActive(false);
              }}
              onProgress={(log) => {
                // Log progress in console or UI if needed
                console.log('[Autonomous Agent]', log.message);
              }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Operator: high-consequence action confirmation */}
      {operatorConfirmPending && (
        <div className="operator-modal-overlay" role="dialog" aria-labelledby="operator-confirm-title">
          <div className="operator-modal operator-confirm-modal">
            <h3 id="operator-confirm-title">Confirm action</h3>
            <p>{operatorConfirmPending.label}</p>
            <p className="operator-modal-hint">The agent is waiting. Allow this action?</p>
            <div className="operator-modal-actions">
              <button
                type="button"
                className="operator-modal-btn primary"
                onClick={() => {
                  operatorConfirmPending.resolve(true);
                  setOperatorConfirmPending(null);
                }}
              >
                Yes, continue
              </button>
              <button
                type="button"
                className="operator-modal-btn"
                onClick={() => {
                  operatorConfirmPending.resolve(false);
                  setOperatorConfirmPending(null);
                }}
              >
                No, skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Operator: takeover â€“ user enters sensitive value */}
      {operatorTakeoverPending && (
        <div className="operator-modal-overlay" role="dialog" aria-labelledby="operator-takeover-title">
          <div className="operator-modal operator-takeover-modal">
            <h3 id="operator-takeover-title">Enter value (takeover mode)</h3>
            <p>{operatorTakeoverPending.fieldLabel}</p>
            <p className="operator-modal-hint">The agent will not see this value. Enter it and click Submit to continue.</p>
            <input
              type="password"
              autoComplete="off"
              className="operator-takeover-input"
              placeholder="Enter valueâ€¦"
              value={operatorTakeoverInputValue}
              onChange={(e) => setOperatorTakeoverInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  operatorTakeoverPending.resolve(operatorTakeoverInputValue);
                  setOperatorTakeoverPending(null);
                }
                if (e.key === 'Escape') {
                  operatorTakeoverPending.resolve('');
                  setOperatorTakeoverPending(null);
                }
              }}
            />
            <div className="operator-modal-actions">
              <button
                type="button"
                className="operator-modal-btn primary"
                onClick={() => {
                  operatorTakeoverPending.resolve(operatorTakeoverInputValue);
                  setOperatorTakeoverPending(null);
                }}
              >
                Submit
              </button>
              <button
                type="button"
                className="operator-modal-btn"
                onClick={() => {
                  operatorTakeoverPending.resolve('');
                  setOperatorTakeoverPending(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zolaid Computer */}
      {showZolaidComputer && (
        <ZolaidComputer
          actions={zolaidActions}
          isActive={isStreaming}
          currentTask={currentTask}
          artifacts={zolaidArtifacts}
          initialViewMode={zolaidComputerInitialViewMode}
          privacyEnabled={privacyEnabled}
          embedVSCodeUrl={ZOLAID_VSCODE_EMBED_URL}
          onRequestFix={({ lastError, errorCount, preview, recentEvents }) => {
            const payload = {
              errorCount,
              lastError,
              preview,
              recentEvents,
            };
            const prompt = [
              "The preview runtime shows errors. Fix the code.",
              "Return ONE JSON object only.",
              "Return mode=render with corrected artifacts {html, css, js}.",
              "Keep the UI the same unless needed to fix errors.",
              "",
              "Context (JSON):",
              JSON.stringify(payload),
            ].join("\n");
            sendMessage(prompt, null);
            setShowZolaidComputer(true);
          }}
          onClose={() => {
            setShowZolaidComputer(false);
            setCurrentTask(null);
            setZolaidComputerInitialViewMode(defaultZolaidComputerViewMode());
          }}
        />
      )}
      </div>
    </ChatbotErrorBoundary>
  );
}
